import PaginatedQuestionPreview from "../components/document-processing/PaginatedQuestionPreview";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { createGlobalStyle, css, keyframes } from "styled-components";
import { handoutKaiFontFamily, handoutKaiFontUrl, handoutKaiLocalSources } from "../features/handoutPreviewFonts";
import { dragScrollOffset, usePointerDragAutoScroll } from "../shared/dragAutoScroll";
import { getQuestionPaginationPayload, getQuestionPaginationStyle, needsQuestionPageBreak, needsStandalonePageBreak, needsChapterPageBreak, getForcedHandoutPageCount, isExplicitHandoutPageBreak } from "../features/handoutPagination";
import { getHandoutPreviewInsertAfter, isHandoutPreviewControl } from "../features/handoutPreviewInteractions";
import { confirmLoadRecord, confirmSaveBeforeExport } from "../shared/recordLoadDialog";
import { recordEditorSignature } from "../shared/recordEditorSignature";
import useEditorHistory from "../shared/useEditorHistory";
import QuestionUuidCopyButton from "../components/math/QuestionUuidCopyButton";
import { getHandoutQuestionPreviewPage, getHandoutSectionPreviewPage } from "../features/handoutPreviewSelection";
import { showEditorSaveToast } from "../shared/editorSaveToast";
import { isBilingualExamTemplate, restoreRecordBilingualCache, selectBilingualTranslations, withBilingualTemplate } from "../shared/bilingualExamCache";
import {
  FaBookOpen,
  FaCheck,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardList,
  FaDownload,
  FaFileWord,
  FaEye,
  FaEyeSlash,
  FaGlobe,
  FaGripVertical,
  FaImage,
  FaListOl,
  FaLock,
  FaArchive,
  FaCopy,
  FaPlus,
  FaRegEdit,
  FaSave,
  FaSearch,
  FaSlidersH,
  FaStar,
  FaSortAmountDownAlt,
  FaSyncAlt,
  FaTimes,
  FaUndo,
  FaRedo,
} from "react-icons/fa";
import { getAssetPublicUrl, listAssets } from "../api/assets";
import * as questionBankApi from "../api/mathBank";
import QuestionCategorySelect from "../components/math/QuestionCategorySelect";
import SubjectWorkspace from "../components/math/SubjectWorkspace";
import SearchableSelect from "../components/math/SearchableSelect";
import QuestionFilterSelect from "../components/math/QuestionFilterSelect";
import { filterMatches, filterParam, reconcileUnitFilter } from "../shared/questionFilters";
import { questionDifficulties as difficultyOptions } from "../components/document-processing/mathBankQuestionOptions";
import BulkQuestionInsert from "../components/math/BulkQuestionInsert";
import HandoutRecords from "../components/math/HandoutRecords";
import QuestionAssetPreviewStrip, { getPreviewableQuestionAssets } from "../components/document-processing/QuestionAssetPreviewStrip";
import ShortAnswerWorkspace from "../components/document-processing/ShortAnswerWorkspace";
import BilingualDocumentPreview from "../components/document-processing/BilingualDocumentPreview";
import ExpandedEditorModal from "../components/document-processing/ExpandedEditorModal";
import ImageBackgroundRemovalDialog from "../components/document-processing/ImageBackgroundRemovalDialog";
import { withoutHandoutScores } from "../features/handoutContent";
import { prepareHandoutBackgroundSource } from "../features/handoutBackgroundSource";
import HandoutImagePythonDialog from "../components/document-processing/HandoutImagePythonDialog";
import { getHandoutImagePythonCode } from "../features/handoutImagePython";
import { restoreImageBackground } from "../features/imageBackgroundRemoval";
import MathText from "../components/math/MathText";
import MarkdownMathText from "../components/math/MarkdownMathText";
import HandoutFocusContent from "../components/math/HandoutFocusContent";
import HandoutFocusFrame, { getHandoutFocusFrameUrl } from "../components/math/HandoutFocusFrame";
import HandoutWordHeading from "../components/math/HandoutWordHeading";
import HandoutLayoutControls from "../components/math/HandoutLayoutControls";
import HandoutFocusImagePicker from "../components/math/HandoutFocusImagePicker";
import DeferredSearchInput from "../components/math/DeferredSearchInput";
import withPendingFeedback from "../components/math/withPendingFeedback";
import { BILINGUAL_MAX_QUESTIONS, HANDOUT_MAX_QUESTIONS } from "../shared/apiErrorMessage";
import { HandoutWordQuestionText, HandoutWordAnswer } from "../components/math/HandoutQuestionTypography";
import { getHandoutBesideFigureWidth } from "../features/handoutQuestionTypography";
import { filterHandoutAssets, getHandoutAssetFolders, getHandoutRoleFolder, handoutIconSize, serializeHandoutIcon } from "../features/handoutAssets";
import { HANDOUT_WORD_PRESET, HANDOUT_WORD_NARROW_PRESET, getHandoutLayoutPresetLabel, handoutWordWideMargins, applyHandoutWordLayout, applyHandoutSimpleLayout, getHandoutFocusCaption, getHandoutFocusBackgroundStyle, handoutIconPreviewStyle, handoutWordDefaultIcons, hasHandoutSectionHeading, isHandoutWordLayout, normalizeHandoutHeaderFooter } from "../features/handoutWordLayout";
import HandoutFocusBackgroundPicker from "../components/math/HandoutFocusBackgroundPicker";
import useHandoutChapterHeaders, { handoutHeaderForPage } from "../features/handoutChapterHeaders";
import { groupHandoutConceptSections } from "../features/handoutConceptGroups";
import { getHandoutFocusImages, serializeHandoutFocusImages, moveImageSectionIntoFocus, getHandoutFocusSubsections, handoutImageLoadProps } from "../features/handoutFocusImages";
import { prepareHandoutRecordPayload } from "../features/handoutRecordPayload";
import { handoutSourceLabel, handoutResponseMinHeight } from "../features/handoutResponsePreview";
import { chooseMixedWordExport } from "../shared/chooseMixedWordExport";
import observeHandoutFlow from "../features/observeHandoutFlow";
import { measureHandoutBreakPages, handoutBreakPageLabel } from "../features/handoutBreakPages";
import QuestionFamilyDraw from "../components/math/QuestionFamilyDraw";
import QuestionFamilyReplacement from "../components/math/QuestionFamilyReplacement";
import QuestionFamilyAnalysis from "../components/math/QuestionFamilyAnalysis";
import QuestionFamilyDialog from "../components/math/QuestionFamilyDialog";
import QuestionPreviewModal from "../components/math/QuestionPreviewModal";
import PreviewUuidTools from "../components/math/PreviewUuidTools";
import QuestionDifficultyBadge, {
  getQuestionDifficultyLabel as getDifficultyLabel,
} from "../components/math/QuestionDifficultyBadge";
import {
  getCachedMatplotlibPreview,
  getOrRenderMatplotlibPreview,
} from "../shared/matplotlibPreviewCache";
import { useAuth } from "../utils";
import LoadingText from "../components/LoadingText";
import WordExportProgressModal from "../components/WordExportProgressModal";
import useReaderPaging from "../components/math/useReaderPaging";
import useContinuousReaderPaging from "../components/math/useContinuousReaderPaging";
import { getQuestionImageAlignment } from "../features/questionImageAlignment";

const questionStatuses = [
  { value: "", label: "全部狀態" },
  { value: "draft", label: "草稿" },
  { value: "published", label: "已發布" },
  { value: "archived", label: "封存" },
];

const questionTypes = [
  { value: "", label: "全部題型" },
  { value: "choice", label: "選擇題" },
  { value: "fill", label: "填充題" },
  { value: "calculation", label: "計算題" },
  { value: "proof", label: "證明題" },
  { value: "application", label: "應用題" },
  { value: "short_answer", label: "簡答題" },
  { value: "essay", label: "申論題" },
];

const questionDifficulties = [
  { value: "", label: "全部難度" },
  ...difficultyOptions,
];

const questionVisibilities = [
  { value: "", label: "全部可見性" },
  { value: "public", label: "公開題目" },
  { value: "private", label: "私有題目" },
];

const questionGoodStarFilters = [
  { value: "", label: "無篩選" },
  { value: "true", label: "好題星星" },
];

const fallbackTemplates = [
  { id: "exam_paper", name: "檢測卷" },
  { id: "elementary_exam_paper", name: "國小段考卷" },
  { id: "junior_exam_paper", name: "國中段考卷" },
  { id: "high_school_exam_paper", name: "高中段考卷" },
  { id: "bilingual_exam_paper", name: "雙語考卷（英文在上）" },
  { id: "bilingual_chinese_first_exam_paper", name: "雙語考卷（中文在上）" },
  { id: "junior_math_handout", name: "國中講義" },
  { id: "high_school_math_handout", name: "高中講義" },
  { id: "private_school_mock", name: "私中模擬題本" },
];
const pickerTemplateIds = new Set([
  "elementary_exam_paper",
  "junior_exam_paper",
  "high_school_exam_paper",
  "bilingual_exam_paper",
  "bilingual_chinese_first_exam_paper",
  "private_school_mock",
]);
const pickerExamTemplateIds = new Set(["elementary_exam_paper", "junior_exam_paper", "high_school_exam_paper", "bilingual_exam_paper", "bilingual_chinese_first_exam_paper"]);
const pickerTemplateTitles = {
  elementary_exam_paper: "國小段考卷",
  junior_exam_paper: "國中段考卷",
  high_school_exam_paper: "高中段考卷",
  bilingual_exam_paper: "雙語考卷",
  bilingual_chinese_first_exam_paper: "雙語考卷（中文在上）",
  private_school_mock: "私中模擬題本",
};
const supportedTemplateIds = new Set(
  fallbackTemplates.map((template) => template.id),
);

const handoutTemplateChoices = [
  {
    id: "junior_math_handout",
    label: "國中模板",
    title: "國中講義",
    description: "重點整理、題型區塊、講義挑題",
    icon: "book",
  },
  {
    id: "high_school_math_handout",
    label: "高中模板",
    title: "高中講義",
    description: "高中數學講義挑題",
    icon: "book",
  },
  {
    id: "exam_paper",
    label: "檢測卷",
    title: "GM/MAC 檢測卷",
    description: "首頁說明、考試時間、題數",
    icon: "exam",
  },
];

const outputModes = [
  { value: "mixed", label: "教用學用混合" },
  { value: "teaching_brief", label: "教用簡答" },
  { value: "teaching_detailed", label: "教用詳解" },
  { value: "student", label: "學用（僅題目）" },
];
const examOutputModes = [
  { value: "mixed", label: "教用學用混合" },
  { value: "teaching", label: "教用簡答" },
  { value: "teaching_detailed", label: "教用詳解" },
  { value: "student", label: "學用（僅題目）" },
];

const sectionModes = [
  { value: "unit", label: "依單元分組" },
  { value: "type", label: "依題型分組" },
  { value: "difficulty", label: "依難度分組" },
  { value: "selected", label: "照選取順序" },
];

const previewFonts = [
  { value: "biauKai", label: "標楷體" },
  { value: "ming", label: "新細明體" },
  { value: "system", label: "系統字體" },
];

const previewFontFamilies = {
  biauKai: '"BiauKai", "DFKai-SB", "KaiTi", serif',
  ming: '"PMingLiU", "MingLiU", "Songti TC", serif',
  system: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const previewFontSizes = [
  { value: "12", label: "12", bodySize: 12, titleSize: 17 },
  { value: "14", label: "14", bodySize: 14, titleSize: 19 },
  { value: "16", label: "16", bodySize: 16, titleSize: 22 },
];

const workspaceTabs = [
  { value: "picker", label: "題庫挑題" },
  { value: "handout", label: "講義製作" },
  { value: "records", label: "講義紀錄" },
  { value: "exam-records", label: "考卷紀錄" },
  { value: "short-answer", label: "簡答製作" },
];

const defaultHandoutTitle = "題庫選題講義";
const defaultHandoutFilename = "題庫選題講義.docx";
const defaultExamHandoutTitle = "段考卷";
const defaultExamHandoutFilename = "段考卷.docx";
const legacyExamHandoutTitles = ["國X數學檢定", "國二上數學檢定"];
const legacyExamHandoutFilenames = ["國X數學檢定.docx", "國二上數學檢定.docx"];

const fallbackExamTemplateFiles = [
  {
    id: "instructions",
    label: "檢測說明模板",
    filename: "檢測說明模板.docx",
    url: "https://assets.sunnytseng.com/%E7%B4%A0%E6%9D%90/%E5%9C%8B%E4%B8%AD%E6%AA%A2%E6%B8%AC%E5%8D%B7%E7%B4%A0%E6%9D%90/%E6%AA%A2%E6%B8%AC%E8%AA%AA%E6%98%8E%E6%A8%A1%E6%9D%BF.docx",
  },
  {
    id: "questions",
    label: "檢測題目模板",
    filename: "檢測題目模板.docx",
    url: "https://assets.sunnytseng.com/%E7%B4%A0%E6%9D%90/%E5%9C%8B%E4%B8%AD%E6%AA%A2%E6%B8%AC%E5%8D%B7%E7%B4%A0%E6%9D%90/%E6%AA%A2%E6%B8%AC%E9%A1%8C%E7%9B%AE%E6%A8%A1%E6%9D%BF.docx",
  },
];

const defaultExamParts = [
  {
    id: "exam-part-1",
    title: "第一部分",
    scoreText: "每題5分，共50分",
  },
  {
    id: "exam-part-2",
    title: "第二部分",
    scoreText: "每題5分，共50分",
  },
  {
    id: "exam-part-3",
    title: "第三部分",
    scoreText: "21-25題，每題8分，第26題10分，共50分",
  },
];

const getDocxPreviewUrl = (url) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

const getTemplatePreviewSourceUrl = (file) =>
  file?.preview_url || file?.url || "";

const getTemplateDownloadUrl = (file) =>
  file?.download_url || file?.url || "";

const handoutIconPrefix = "素材/國中講義素材/";
const legacyHandoutIconPrefix = "國中講義內文圖示素材/";
const wordImageExtensions = new Set(["png", "jpg", "jpeg", "gif", "bmp", "tif", "tiff"]);
const handoutPageWidth = 794;
const handoutPageHeight = 1123;
const handoutPageGap = 24;
const handoutPageContentWidth = 686;
const handoutColumnGap = handoutPageWidth + handoutPageGap - handoutPageContentWidth;

const handoutFonts = [
  { value: "標楷體", label: "標楷體" },
  { value: "微軟正黑體", label: "微軟正黑體" },
  { value: "新細明體", label: "新細明體" },
  { value: "Times New Roman", label: "Times New Roman" },
];

const handoutPreviewFontFamilies = {
  "標楷體": handoutKaiFontFamily,
  "微軟正黑體": '"微軟正黑體", "Microsoft JhengHei", "PingFang TC", "Heiti TC", sans-serif',
  "新細明體": '"新細明體", "PMingLiU", "MingLiU", "Songti TC", serif',
  "Times New Roman": '"Times New Roman", "Songti TC", serif',
};

const HandoutPreviewFonts = createGlobalStyle`
  @font-face {
    font-family: "HandoutMathKai";
    src: ${handoutKaiLocalSources}, url("${handoutKaiFontUrl}") format("truetype");
    unicode-range: U+2E80-2FFF, U+3000-303F, U+3100-312F, U+31A0-31BF, U+3400-4DBF, U+4E00-9FFF, U+F900-FAFF, U+FF00-FFEF, U+20000-323AF;
    font-style: normal;
    font-weight: 400;
    font-display: swap;
  }
  @font-face {
    font-family: "HandoutKai";
    src: ${handoutKaiLocalSources}, url("${handoutKaiFontUrl}") format("truetype");
    font-style: normal;
    font-weight: 400;
    font-display: swap;
  }
`;

const handoutIconRoles = [
  { value: "chapter", label: "單元標" },
  { value: "focus", label: "重點整理" },
  { value: "type", label: "題型標" },
  { value: "practice", label: "類題" },
  { value: "wave", label: "題型分隔線" },
  { value: "example", label: "範例" },
  { value: "exercise", label: "練習" },
  { value: "challenge", label: "挑戰" },
];

const handoutPageNumberPositions = [
  { value: "outer", label: "外側" },
  { value: "inner", label: "內側" },
  { value: "center", label: "置中" },
  { value: "none", label: "不放頁碼" },
];

const defaultHandoutOptions = {
  iconPrefix: handoutIconPrefix,
  activeIconRole: "focus",
  bodyFontName: "標楷體",
  headingFontName: "微軟正黑體",
  bodyFontSize: 12,
  headingFontSize: 14,
  titleFontSize: 20,
  lineSpacing: 1.25,
  margins: {
    top: 4,
    bottom: 2.75,
    left: 2.25,
    right: 2.25,
    gutter: 1,
  },
  headerFooter: {
    differentOddEvenPages: false,
    oddHeaderLeft: "",
    oddHeaderRight: "",
    evenHeaderLeft: "",
    evenHeaderRight: "",
    oddFooterLeft: "",
    oddFooterRight: "",
    evenFooterLeft: "",
    evenFooterRight: "",
    pageNumberPosition: "outer",
  },
  focus: {
    backgroundStyle: "gray",
    unitTitle: "",
    title: "重點整理",
    typeTitle: "",
    lines: "",
  },
  exam: {
    coverTitle: "GM/MAC試題本",
    instructionTitle: "檢測說明",
    examTimeMinutes: 60,
    questionCount: "",
    pageCount: 6,
    totalScore: 150,
    noticeText: "請不要翻到次頁！\n讀完本頁的說明，聽從監考老師的指示才開始作答！",
    description: "本測驗題本採雙面印刷。",
    cautions: "題本分三個部分，第一部分10題，第二部分10題，第三部分6題，皆為應用題。\n試題中參考的附圖，不一定代表實際大小。\n請勿故意汙損答案卷、損壞試題本，考試不予計分。",
    answerRules: "作答時，可利用題本中空白部分計算，過程不扣分，以最終答案作依據給分。\n請依照題意將解答過程及最後結果，清楚完整地呈現出來。\n若解答過程使用了題目敘述中沒有出現的符號，則必須說明。\n更正時請使用橡皮擦或修正帶(液)修正後，重新書寫解答過程。",
    parts: defaultExamParts.map((part) => ({ ...part })),
  },
  icons: {},
  questionImageWidths: {},
  questionImageAlignments: {},
};

const getDefaultHandoutMargins = (templateId) => templateId === "junior_math_handout"
  ? { ...handoutWordWideMargins }
  : { ...defaultHandoutOptions.margins };

const defaultHandoutSections = [
  { id: "toc", type: "toc", title: "目錄", questions: [] },
  { id: "focus", type: "focus", title: "重點整理", lines: defaultHandoutOptions.focus.lines, questions: [] },
  { id: "type-1", type: "questions", title: "", questions: [] },
];

const defaultExamHandoutSections = [
  { id: "exam-info", type: "exam_info", title: "檢測說明", questions: [] },
  ...defaultExamParts.map((part) => ({
    id: part.id,
    type: "questions",
    title: part.title,
    questions: [],
  })),
];

const examPreviewQuestionsPerPage = 5;
const examPreviewPartTitles = defaultExamParts.map(
  (part) => `【${part.title}：${part.scoreText}】`,
);

const chunkItems = (items = [], size = 1) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const getExamPreviewPartTitle = (pageIndex) =>
  examPreviewPartTitles[pageIndex] || `【第 ${pageIndex + 1} 頁】`;

const getExamPreviewPartHeading = (part, fallbackIndex = 0) => {
  const fallbackPart = defaultExamParts[fallbackIndex] || {};
  const title = String(part?.title || fallbackPart.title || `第 ${fallbackIndex + 1} 部分`).trim();
  const scoreText = String(
    part?.scoreText || part?.score_text || fallbackPart.scoreText || "",
  ).trim();
  return scoreText ? `【${title}：${scoreText}】` : `【${title}】`;
};

const cloneHandoutSections = (sections) =>
  sections.map((section) => ({
    ...section,
    questions: [...(section.questions || [])],
  }));

const isLegacyExamQuestionSection = (section) =>
  section?.id === "exam-questions" || section?.title === "檢測題目";

const normalizeExamSectionsForTemplate = (sections = []) => {
  const sourceSections = Array.isArray(sections) ? cloneHandoutSections(sections) : [];
  const sourceInfo = sourceSections.find((section) => section.type === "exam_info");
  const defaultInfo = defaultExamHandoutSections[0];
  const questionSources = sourceSections.filter((section) => section.type === "questions");
  const consumedSourceIndexes = new Set();
  const nextQuestionSections = defaultExamParts.map((part, partIndex) => {
    let matchedByIdentity = false;
    let sourceIndex = questionSources.findIndex(
      (section, index) =>
        !consumedSourceIndexes.has(index) &&
        (section.id === part.id || section.title === part.title),
    );

    if (sourceIndex >= 0) {
      matchedByIdentity = true;
    } else if (partIndex === 0) {
      sourceIndex = questionSources.findIndex(
        (section, index) =>
          !consumedSourceIndexes.has(index) && isLegacyExamQuestionSection(section),
      );
    }

    if (sourceIndex < 0 && questionSources.length <= defaultExamParts.length) {
      sourceIndex = questionSources.findIndex(
        (_section, index) => !consumedSourceIndexes.has(index),
      );
    }

    const sourceSection = sourceIndex >= 0 ? questionSources[sourceIndex] : null;
    if (sourceIndex >= 0) consumedSourceIndexes.add(sourceIndex);

    return {
      id: part.id,
      type: "questions",
      title: matchedByIdentity && sourceSection?.title ? sourceSection.title : part.title,
      questions: [...(sourceSection?.questions || [])],
    };
  });

  questionSources.forEach((section, index) => {
    if (!consumedSourceIndexes.has(index)) {
      nextQuestionSections[nextQuestionSections.length - 1].questions.push(
        ...(section.questions || []),
      );
    }
  });

  return [
    {
      ...defaultInfo,
      ...(sourceInfo || {}),
      id: defaultInfo.id,
      type: defaultInfo.type,
      title: sourceInfo?.title || defaultInfo.title,
      questions: [],
    },
    ...nextQuestionSections,
  ];
};

const getQuestionsFromSections = (sections) =>
  (sections || []).flatMap((section) =>
    section.type === "questions" ? section.questions || [] : [],
  );

const getDefaultSectionsForTemplate = (templateId) =>
  cloneHandoutSections(
    templateId === "exam_paper" ? defaultExamHandoutSections : defaultHandoutSections,
  );

const buildSectionsForTemplate = (templateId, sections = []) => {
  if (templateId === "exam_paper") {
    return normalizeExamSectionsForTemplate(sections);
  }

  if (sections.some((section) => section.type === "focus")) {
    return cloneHandoutSections(sections);
  }

  const nextSections = getDefaultSectionsForTemplate(templateId);
  const questions = getQuestionsFromSections(sections);
  if (questions.length) {
    const questionSection = nextSections.find((section) => section.type === "questions");
    if (questionSection) questionSection.questions = questions;
  }
  return nextSections;
};

const normalizeSectionsForTemplate = (templateId, sections = []) => {
  const sourceSections = Array.isArray(sections) && sections.length
    ? cloneHandoutSections(sections)
    : [];
  if (templateId !== "exam_paper") {
    return sourceSections.length ? groupHandoutConceptSections(sourceSections) : getDefaultSectionsForTemplate(templateId);
  }
  return normalizeExamSectionsForTemplate(sourceSections);
};

const getDefaultActiveSectionId = (templateId, sections = []) => {
  const availableSections = sections.length ? sections : getDefaultSectionsForTemplate(templateId);
  return (
    availableSections.find((section) => section.type === "questions")?.id ||
    availableSections[0]?.id ||
    ""
  );
};

const cloneHandoutPayload = (payload) => JSON.parse(JSON.stringify(payload));


const filterNoneValue = "__none__";
const filterAllValue = "__all__";

const emptyFilters = {
  family_id: "",
  search: "",
  grade_id: filterNoneValue,
  unit_id: filterNoneValue,
  type: "",
  difficulty: "",
  category: "",
  status: "",
  visibility: "",
  is_good_question: "",
  question_source: "",
};

const questionPageSize = 30;

const formatThinking = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join("、");
  return String(value || "");
};

const getVisibilityLabel = (visibility) =>
  visibility === "private" ? "私有" : "公開";

const getStatusLabel = (status) =>
  questionStatuses.find((item) => item.value === status)?.label || status || "";

const getQuestionTypeLabel = (type) =>
  questionTypes.find((item) => item.value === type)?.label || type || "未標示題型";


const getStatusIcon = (status) => {
  if (status === "published") return <FaCheck />;
  if (status === "archived") return <FaArchive />;
  return <FaRegEdit />;
};

const getQuestionPrompt = (question) =>
  question.prompt_md?.trim() || question.prompt_preview?.trim() || "";

const splitPromptAnswerTail = (value) => {
  const text = String(value || "").trim();
  const labeledLineMatch = text.match(
    /(?:^|\n)[ \t\u3000]*(?:A|Ans(?:wer)?|答案|答)[ \t\u3000]*[:：.]?[ \t\u3000]*([\s\S]*?)$/i,
  );
  const bracketMatch = text.match(
    /[ \t\u3000]*(?:【答案】|\[答案\])[ \t\u3000]*([\s\S]*?)$/i,
  );
  const match = labeledLineMatch || bracketMatch;
  if (!match) return { prompt: text, answer: "" };

  return {
    prompt: text.slice(0, match.index).trim(),
    answer: String(match[1] || "").trim(),
  };
};

const getHandoutPreviewPrompt = (question) =>
  splitPromptAnswerTail(getQuestionPrompt(question)).prompt;

const getHandoutPreviewAnswer = (question) => {
  const answer = String(question.answer_md || "").trim();
  return answer || splitPromptAnswerTail(getQuestionPrompt(question)).answer;
};

const getHandoutPreviewSource = (question) => handoutSourceLabel(question);

const answerSourceBoxHint = "簡答預設可見；來源與 UUID 預設字型隱藏，保留左右半欄與原排版空間，可在 Word 樣式中取消隱藏。";
const outputModeHint = (mode) => mode === "mixed" ? "下載時選擇教用、學用或兩份；都不含詳解，學用的簡答設為隱藏樣式。"
  : mode === "student" ? "只輸出題目，不包含簡答、詳解與來源。" : answerSourceBoxHint;

const getEditorOutputMode = (mode, templateId) => mode === "teaching_brief" &&
  (pickerExamTemplateIds.has(templateId) || templateId === "exam_paper") ? "teaching" : mode;

const AnswerSourcePreview = ({ question, children, widthPx, mode, compact = false }) => (
  <AnswerSourceRow aria-label="簡答與來源" style={{
    minHeight: `${handoutResponseMinHeight(getHandoutPreviewSource(question), widthPx, mode !== "student", !compact) || 0}pt`,
    ...(compact && mode === "student" ? { margin: 0 } : {}),
  }}>
    <div>{children}</div>
    <AnswerSourceText $compact={compact} style={{ visibility: "hidden" }} aria-hidden="true">{getHandoutPreviewSource(question)}</AnswerSourceText>
  </AnswerSourceRow>
);

const normalizeQuestionSourceValue = (value) => String(value || "").trim();

const normalizeQuestionSourceOptions = (data) => {
  const values = Array.isArray(data) ? data : data?.results || [];
  return Array.from(new Set(values.map(normalizeQuestionSourceValue).filter(Boolean)));
};

const getQuestionUnitId = (question) =>
  String(question?.unit?.id || question?.unit_id || question?.unit || "");

const getQuestionUuid = (question) =>
  String(question?.uuid || question?.id || "").trim();

const shortId = (id) => {
  const value = String(id || "").trim();
  return value ? `${value.slice(0, 8)}...${value.slice(-6)}` : "";
};

const getPromptQuestionAssets = (assets = []) => assets.filter((asset) => {
  const role = String(asset?.role || "").trim().toLowerCase();
  return !role || role === "prompt";
});

function AssetPreviewStrip({ assets, compact = false, limit = Infinity, imageAlignments, lazyRender = compact, widthCm }) {
  return <QuestionAssetPreviewStrip assets={getPromptQuestionAssets(assets)} compact={compact} limit={limit} defaultAlignment="right" imageAlignments={imageAlignments} lazyRender={lazyRender} widthCm={widthCm} />;
}

const isPythonSourceAsset = (asset) =>
  String(asset?.source_language || "").trim().toLowerCase() === "python" &&
  String(asset?.source_code || "").trim();

const assetHasImageUrl = (asset) => String(asset?.url || "").trim();

const ensureDocxFilename = (value, fallback = "題庫選題.docx") => {
  const filename = (value || fallback).trim().replace(/[\\/]/g, "-").split(String.fromCharCode(0)).join("") || fallback;
  return filename.toLowerCase().endsWith(".docx")
    ? filename
    : `${filename}.docx`;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const getBilingualPreviewSignature = (questions, sectionMode) => JSON.stringify({
  sectionMode,
  questions: questions.map((question) => ({
    id: question.id, prompt: question.prompt_md || question.stem,
    options: question.options, shared_context: question.shared_context, content_blocks: question.content_blocks,
    assets: (question.assets || []).map((asset) => asset.id || asset.key || asset.url),
  })),
});

const getAssetName = (key) => String(key || "").split("/").pop() || key;

const getAssetExtension = (key) => {
  const filename = getAssetName(key).toLowerCase();
  const dotIndex = filename.lastIndexOf(".");
  return dotIndex >= 0 ? filename.slice(dotIndex + 1) : "";
};

const isHandoutIconFile = (file) =>
  ["avif", "gif", "jpg", "jpeg", "png", "svg", "webp"].includes(
    getAssetExtension(file.key),
  ) && !String(file.key || "").endsWith("/.keep");

const withAssetUrl = (file) => ({
  ...file,
  name: file.name || getAssetName(file.key),
  url: file.url || getAssetPublicUrl(file.key),
});

// A question may appear more than once in a handout. Number and key each
// occurrence by its slot, so editing one copy never shadows or orphans another.
const handoutQuestionSlotKey = (sectionId, index) => `${sectionId}\u0000${index}`;
const getHandoutQuestionNumber = (numbers, sectionId, index, questionId) =>
  numbers.get(handoutQuestionSlotKey(sectionId, index)) ?? numbers.get(questionId);
const getHandoutQuestionOccurrenceKey = (questions, index) => {
  const id = questions[index]?.id;
  let occurrence = 0;
  for (let position = 0; position < index; position += 1) if (questions[position]?.id === id) occurrence += 1;
  return `${id}#${occurrence}`;
};

const createHandoutFocusImage = (asset) => ({
  id: `focus-image-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  asset: { ...asset }, widthCm: 4, alignment: "right", layout: "float",
});

const getHandoutQuestionImageUrl = (asset) =>
  String(asset?._preview_url || asset?._previewUrl || asset?.url || "").trim();

const splitHandoutLines = (value) =>
  String(value || "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const getHandoutStartNumber = (value) => {
  const number = Number(value);
  return Number.isInteger(number) && number >= 1 && number <= 9999 ? number : null;
};

const buildHandoutSectionsPayload = (sections) =>
  sections
    .filter((section) => section.type === "questions" && section.questions.length)
    .map((section) => ({
      id: section.id,
      title: section.title,
      ...(section.heading ? { heading: { ...section.heading } } : {}),
      start_number: getHandoutStartNumber(section.startNumber),
      question_ids: section.questions.map((question) => question.id),
    }));

const getHandoutFocusLines = (section, settings) => {
  const source = section.lines ?? section.markdown ?? section.focusLines ??
    (section.id === "focus" ? settings.focus.lines : "");
  return Array.isArray(source) ? source.join("\n") : typeof source === "string" ? source : "";
};

const mathHandoutTemplateIds = new Set(["junior_math_handout", "high_school_math_handout"]);
const hasHandoutSectionContent = (sections, settings) => sections.some((section) =>
  (section.type === "focus" && (getHandoutFocusLines(section, settings).trim() || getHandoutFocusImages(section).length)) ||
  (section.type === "image" && String(section.asset?.url || "").trim()));

const getHandoutImageTextParts = (section) => {
  const text = String(section.text || "").replace(/\r\n?/g, "\n");
  if (section.textLayout !== "inline") return { inline: "", below: text };
  const [firstLine, ...remainingLines] = text.split("\n");
  return { inline: firstLine, below: remainingLines.join("\n") };
};

const toOptionalPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : undefined;
};

const normalizeExamPartOptions = (parts = []) =>
  defaultExamParts.map((defaultPart, index) => {
    const savedPart = Array.isArray(parts)
      ? parts.find((part) => part?.id === defaultPart.id) || parts[index] || {}
      : {};
    return {
      ...defaultPart,
      ...savedPart,
      id: defaultPart.id,
      title: savedPart.title || defaultPart.title,
      scoreText: savedPart.scoreText || savedPart.score_text || defaultPart.scoreText,
    };
  });

const getExamPartConfigs = (exam = {}, sections = []) => {
  const savedParts = normalizeExamPartOptions(exam.parts);
  const questionSections = (sections || []).filter((section) => section.type === "questions");
  return defaultExamParts.map((defaultPart, index) => {
    const section =
      questionSections.find((item) => item.id === defaultPart.id) ||
      questionSections[index] ||
      {};
    const savedPart = savedParts[index] || defaultPart;
    const title = String(section.title || savedPart.title || defaultPart.title).trim();
    const questions = section.questions || [];
    return {
      id: defaultPart.id,
      title,
      scoreText: String(savedPart.scoreText || savedPart.score_text || "").trim(),
      scorePerQuestion: toOptionalPositiveNumber(
        savedPart.scorePerQuestion || savedPart.score_per_question,
      ),
      totalScore: toOptionalPositiveNumber(savedPart.totalScore || savedPart.total_score),
      questionCount: questions.length,
      questionIds: questions.map((question) => question.id),
      questions,
    };
  });
};

const buildExamPartCautionLine = (parts = []) => {
  const listedParts = parts.filter((part) => part.title);
  if (!listedParts.length) return "";
  const partCountLabel = listedParts.length === 3 ? "三" : String(listedParts.length);
  const countText = listedParts
    .map((part) => `${part.title}${part.questionCount || 0}題`)
    .join("，");
  return `題本分${partCountLabel}個部分，${countText}，皆為應用題。`;
};

const isExamPartCautionLine = (line) =>
  line.startsWith("題本分") && line.includes("部分") && line.includes("題");

const buildExamCautionLines = (exam = {}, parts = []) => {
  const dynamicLine = buildExamPartCautionLine(parts);
  const cautionLines = splitHandoutLines(exam.cautions || exam.caution_lines);
  if (!dynamicLine) return cautionLines;
  if (!cautionLines.length) {
    return [
      dynamicLine,
      ...splitHandoutLines(defaultHandoutOptions.exam.cautions).slice(1),
    ];
  }
  if (isExamPartCautionLine(cautionLines[0])) {
    return [dynamicLine, ...cautionLines.slice(1)];
  }
  return [dynamicLine, ...cautionLines];
};

const isPointerInsideElement = (event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  return (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  );
};

const buildHandoutExamPayload = (settings, totalQuestionCount = 0, sections = []) => {
  const exam = settings.exam || defaultHandoutOptions.exam;
  const parts = getExamPartConfigs(exam, sections);
  return {
    cover_enabled: true,
    cover_title: exam.coverTitle,
    instruction_title: exam.instructionTitle,
    exam_time_minutes: toOptionalPositiveNumber(exam.examTimeMinutes),
    question_count:
      toOptionalPositiveNumber(exam.questionCount) ||
      toOptionalPositiveNumber(totalQuestionCount),
    page_count: toOptionalPositiveNumber(exam.pageCount),
    total_score: toOptionalPositiveNumber(exam.totalScore),
    notice_lines: splitHandoutLines(exam.noticeText),
    description: exam.description,
    caution_lines: buildExamCautionLines(exam, parts),
    answer_rule_lines: splitHandoutLines(exam.answerRules),
    parts: parts.map((part) => ({
      id: part.id,
      title: part.title,
      score_text: part.scoreText,
      score_per_question: part.scorePerQuestion,
      total_score: part.totalScore,
      question_count: part.questionCount,
      question_ids: part.questionIds,
    })),
  };
};

const buildHandoutOptionsPayload = (settings, sections = [], totalQuestionCount = 0) => ({
  style: {
    ...(isHandoutWordLayout(settings) ? { layout_preset: settings.layoutPreset } : {}),
    body_font_name: settings.bodyFontName,
    heading_font_name: settings.headingFontName,
    body_font_size: Number(settings.bodyFontSize) || 12,
    heading_font_size: Number(settings.headingFontSize) || 14,
    title_font_size: Number(settings.titleFontSize) || 20,
    line_spacing: Number(settings.lineSpacing) || 1.25,
    ...(settings.optionsLayout ? { options_layout: settings.optionsLayout } : {}),
    ...(settings.questionSpacingPt !== undefined ? { question_spacing_pt: Number(settings.questionSpacingPt) } : {}),
    ...(settings.questionFigureLayout ? { question_figure_layout: settings.questionFigureLayout } : {}),
    ...(settings.answerFontSize !== undefined ? { answer_font_size: Number(settings.answerFontSize) } : {}),
    ...(typeof settings.answerBold === "boolean" ? { answer_bold: settings.answerBold } : {}),
    ...(typeof settings.preserveManualPageBreaks === "boolean" ? { preserve_manual_page_breaks: settings.preserveManualPageBreaks } : {}),
  },
  page: {
    top_cm: Number(settings.margins.top) || 4,
    bottom_cm: Number(settings.margins.bottom) || 2.75,
    left_cm: Number(settings.margins.left) || 2.25,
    right_cm: Number(settings.margins.right) || 2.25,
    gutter_cm: Number(settings.margins.gutter) || 0,
  },
  header_footer: {
    different_odd_even: isHandoutWordLayout(settings) || settings.headerFooter.differentOddEvenPages,
    odd_header_left: settings.headerFooter.oddHeaderLeft,
    odd_header_right: settings.headerFooter.oddHeaderRight,
    even_header_left: settings.headerFooter.evenHeaderLeft,
    even_header_right: settings.headerFooter.evenHeaderRight,
    odd_footer_left: settings.headerFooter.oddFooterLeft,
    odd_footer_right: settings.headerFooter.oddFooterRight,
    even_footer_left: settings.headerFooter.evenFooterLeft,
    even_footer_right: settings.headerFooter.evenFooterRight,
    page_number_position: settings.headerFooter.pageNumberPosition,
  },
  focus: {
    unit_title: settings.focus.unitTitle,
    ...(settings.focus.backgroundStyle || isHandoutWordLayout(settings)
      ? { background_style: settings.focus.backgroundStyle || "gray" } : {}),
    title: settings.focus.title,
    type_title: settings.focus.typeTitle,
    ...(getHandoutFocusFrameUrl(settings.focus.frame) ? { frame: { url: getHandoutFocusFrameUrl(settings.focus.frame) } } : {}),
    lines: splitHandoutLines(settings.focus.lines),
    markdown: settings.focus.lines || "",
    blocks: sections
      .flatMap((section, index) => section.type === "focus" ? [{
        title: section.title,
        continue_previous_focus: section.continuePreviousFocus === true,
        ...(isHandoutWordLayout(settings) ? { background_style: getHandoutFocusBackgroundStyle(section, settings) } : {}),
        ...(section.chapter ? { chapter: { ...section.chapter } } : {}),
        lines: splitHandoutLines(getHandoutFocusLines(section, settings)),
        markdown: getHandoutFocusLines(section, settings),
      images: serializeHandoutFocusImages(section),
        before_section_id: sections.slice(index + 1).find(
          (nextSection) => nextSection.type === "questions" && nextSection.questions.length,
        )?.id || "",
      }] : []),
  },
  exam: buildHandoutExamPayload(settings, totalQuestionCount, sections),
  icons: Object.fromEntries(
    Object.entries(settings.icons || {})
      .filter(([, icon]) => icon?.url)
      .map(([role, icon]) => [
        role,
        serializeHandoutIcon(icon),
      ]),
  ),
  sections: buildHandoutSectionsPayload(sections),
  content_blocks: sections.flatMap((section) => {
    if (section.type === "focus") return [{
      type: "focus",
      title: section.title,
      continue_previous_focus: section.continuePreviousFocus === true,
      ...(isHandoutWordLayout(settings) ? { background_style: getHandoutFocusBackgroundStyle(section, settings) } : {}),
      ...(section.chapter ? { chapter: { ...section.chapter } } : {}),
      lines: splitHandoutLines(getHandoutFocusLines(section, settings)),
      markdown: getHandoutFocusLines(section, settings),
      images: serializeHandoutFocusImages(section),
    }];
    if (section.type === "questions" && section.questions.length) {
      return [{
        type: "questions",
        section_id: section.id,
        ...(section.heading ? { heading: { ...section.heading } } : {}),
        question_pagination: Object.fromEntries(section.questions.map((question) => [question.id, getQuestionPaginationPayload(question)])),
        page_break_before_question_ids: section.questions
          .filter((question) => question.pageBreakBefore)
          .map((question) => question.id),
      }];
    }
    if (section.type === "page_break" || section.type === "section_break") {
      return [{ type: section.type, ...(isExplicitHandoutPageBreak(section) ? { force_page_break: true } : {}) }];
    }
    if (section.type === "image") return [{
      type: "image",
      url: section.asset?.url || "",
      ...(section.asset?.source_language === "python" ? { asset: section.asset } : {}),
      width_cm: Number(section.widthCm) || 8,
      alignment: section.alignment || "center",
      text: section.text || "",
      text_layout: section.textLayout || "below",
      text_font_size: section.textFontSize ?? null,
      text_offset_pt: section.textOffsetPt || 0,
    }];
    return [];
  }),
  question_image_widths: settings.questionImageWidths || {},
  question_image_alignments: settings.questionImageAlignments || {},
});

const getPreviewSectionIdentity = (question, sectionMode) => {
  if (sectionMode === "selected") return ["selected", "題庫選題"];
  if (sectionMode === "type") {
    const title = getQuestionTypeLabel(question.type);
    return [`type:${question.type || title}`, title];
  }
  if (sectionMode === "difficulty") {
    const title = getDifficultyLabel(question.difficulty);
    return [`difficulty:${question.difficulty || title}`, title];
  }

  const gradeName = question.grade?.name || "未分類年級";
  const unitName = question.unit?.name || "未分類單元";
  return [
    `unit:${question.grade?.id || question.grade_id || gradeName}:${getQuestionUnitId(question) || unitName}`,
    `${gradeName}｜${unitName}`,
  ];
};

const buildHandoutPreviewSections = (questions, sectionMode) => {
  const sections = [];
  const sectionsByKey = new Map();

  questions.forEach((question) => {
    const [key, title] = getPreviewSectionIdentity(question, sectionMode);
    if (!sectionsByKey.has(key)) {
      const section = { key, title, questions: [] };
      sectionsByKey.set(key, section);
      sections.push(section);
    }
    sectionsByKey.get(key).questions.push(question);
  });

  return sections;
};

const markdownTableSeparatorPattern = /^:?-{3,}:?$/;

const isMarkdownTableLine = (line) => {
  const text = String(line || "").trim();
  return text.startsWith("|") && text.endsWith("|") && text.split("|").length >= 3;
};

const splitMarkdownTableRow = (line) => {
  let text = String(line || "").trim();
  if (text.startsWith("|")) text = text.slice(1);
  if (text.endsWith("|")) text = text.slice(0, -1);

  const cells = [];
  let current = "";
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === "\\" && text[index + 1] === "|") {
      current += "|";
      index += 1;
      continue;
    }
    if (char === "|") {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  cells.push(current.trim());
  return cells;
};

const isMarkdownTableSeparatorRow = (row) =>
  row.length > 0 &&
  row.every((cell) => markdownTableSeparatorPattern.test(cell.trim()));

const parseMarkdownTableRows = (lines) => {
  const rawRows = lines.map(splitMarkdownTableRow);
  const rows = rawRows.filter((row) => !isMarkdownTableSeparatorRow(row));
  if (rows.length < 2 || rows.length === rawRows.length) return null;

  const columnCount = Math.max(...rows.map((row) => row.length));
  return rows.map((row) => [
    ...row.map((cell) => cell.replaceAll("\\\\", "\\").replace(/^\$|\$$/g, "").trim()),
    ...Array(Math.max(0, columnCount - row.length)).fill(""),
  ]);
};

const splitMarkdownTableBlocks = (value) => {
  const lines = String(value || "").replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let textLines = [];
  let tableLines = [];

  const flushText = () => {
    const text = textLines.join("\n").trim();
    if (text) blocks.push({ type: "text", text });
    textLines = [];
  };

  const flushTable = () => {
    if (!tableLines.length) return;
    const rows = parseMarkdownTableRows(tableLines);
    if (rows) {
      flushText();
      blocks.push({ type: "table", rows });
    } else {
      textLines = [...textLines, ...tableLines];
    }
    tableLines = [];
  };

  lines.forEach((line) => {
    if (isMarkdownTableLine(line)) {
      tableLines.push(line);
      return;
    }
    flushTable();
    textLines.push(line);
  });

  flushTable();
  flushText();
  return blocks.length ? blocks : [{ type: "text", text: String(value || "") }];
};

const MarkdownAwareMathText = React.memo(function MarkdownAwareMathText({ source, fallback = "" }) {
  const blocks = splitMarkdownTableBlocks(source || fallback);

  return (
    <>
      {blocks.map((block, blockIndex) =>
        block.type === "table" ? (
          <MarkdownPreviewTable key={`table-${blockIndex}`}>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <td key={`cell-${rowIndex}-${cellIndex}`}>
                      <MathText source={cell} fallback="" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </MarkdownPreviewTable>
        ) : (
          <MathText key={`text-${blockIndex}`} source={block.text} fallback="" />
        ),
      )}
    </>
  );
});

function HandoutNumberInput({ value, onChange, min, max, emptyValue, ...props }) {
  const [draft, setDraft] = useState(String(value ?? ""));
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(String(value ?? ""));
  }, [value, editing]);
  return (
    <input {...props} type="number" min={min} max={max} value={draft}
      onFocus={() => setEditing(true)}
      onChange={(event) => {
        const raw = event.target.value;
        setDraft(raw);
        const number = Number(raw);
        if (raw !== "" && Number.isFinite(number) && number >= min && number <= max) {
          onChange(number);
        }
      }}
      onBlur={() => {
        const number = Number(draft);
        const normalized = draft === "" || !Number.isFinite(number)
          ? emptyValue : Math.min(max, Math.max(min, number));
        onChange(normalized);
        setEditing(false);
      }}
    />
  );
}

const cancelledWordExport = () => Object.assign(new Error("已取消 Word 匯出。"), { cancelled: true, code: "cancelled" });
const checkWordExportSignal = (signal) => { if (signal?.aborted) throw cancelledWordExport(); };

const awaitWordPreview = async (render, signal) => {
  checkWordExportSignal(signal);
  let timer;
  let abort;
  const interruption = new Promise((_, reject) => {
    abort = () => reject(cancelledWordExport());
    signal?.addEventListener("abort", abort, { once: true });
    timer = setTimeout(() => reject(Object.assign(new Error("題目圖片準備逾時，請稍後重試匯出。"), { code: "preview_timeout" })), 20000);
  });
  try {
    const result = await Promise.race([interruption, render()]);
    checkWordExportSignal(signal);
    return result;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
};

export default function QuestionBankPickerPage({ onEditQuestion, routeActive = true } = {}) {
  return <SubjectWorkspace
    component={QuestionBankPickerWorkspace}
    workspaceProps={{ onEditQuestion, routeActive }}
  />;
}

function QuestionBankPickerWorkspace({ subject, subjectLabel, isActive, routeActive, onEditQuestion }) {
  const imageResizeAutoScroll = usePointerDragAutoScroll();
  const { createHandoutRecord, updateHandoutRecord, getHandoutRecord, copyHandoutRecord, exportEditedHandoutRecord, createExamRecord, listGrades, listQuestionFamilies, listQuestionSources, listUnits, searchQuestions } = useMemo(() => Object.fromEntries(
    Object.entries(questionBankApi).map(([name, fn]) => [name, (options = {}) => fn({ ...options, subject })]),
  ), [subject]);
  const {
    authToken,
    fetchWordTemplates,
    generateWordFromBank,
    previewBilingualExam,
    previewMatplotlibCode,
  } = useAuth();
  const [grades, setGrades] = useState([]);
  const [questionFamilies, setQuestionFamilies] = useState([]);
  const [familyDraws, setFamilyDraws] = useState([]);
  const [handoutFamilyDraws, setHandoutFamilyDraws] = useState([]);
  const [units, setUnits] = useState([]);
  const [questionIds, setQuestionIds] = useState([]);
  const [questionsById, setQuestionsById] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedQuestionsById, setSelectedQuestionsById] = useState({});
  const [familyReplacementTarget, setFamilyReplacementTarget] = useState(null);
  const [familyAnalysisIds, setFamilyAnalysisIds] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [questionSources, setQuestionSources] = useState([]);
  const [templates, setTemplates] = useState(fallbackTemplates);
  const [templateId, setTemplateId] = useState("junior_exam_paper");
  const [outputMode, setOutputMode] = useState("teaching");
  const [sectionMode, setSectionMode] = useState("type");
  const [documentTitle, setDocumentTitle] = useState(defaultHandoutTitle);
  const [outputFilename, setOutputFilename] = useState(defaultHandoutFilename);
  const [examRange, setExamRange] = useState("");
  const [loading, setLoading] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [wordExportFilename, setWordExportFilename] = useState("");
  const [wordExportProgress, setWordExportProgress] = useState(null);
  const [wordExportCancelling, setWordExportCancelling] = useState(false);
  const wordExportTaskRef = useRef(null);
  const wordExportScope = useMemo(() => ({ authToken, subject, isActive, routeActive }), [authToken, subject, isActive, routeActive]);
  const liveWordExportScope = useRef(wordExportScope);
  liveWordExportScope.current = wordExportScope;
  const [bilingualPreview, setBilingualPreview] = useState(null);
  const [bilingualTranslations, setBilingualTranslations] = useState({});
  const [bilingualPendingQuestions, setBilingualPendingQuestions] = useState({});
  const [bilingualError, setBilingualError] = useState("");
  const [bilingualLoading, setBilingualLoading] = useState(false);
  const bilingualRequestRef = useRef(0);
  const bilingualActiveSignatureRef = useRef("");
  const bilingualAbortRef = useRef(null);
  useEffect(() => () => {
    bilingualRequestRef.current += 1;
    bilingualAbortRef.current?.abort();
    bilingualAbortRef.current = null;
  }, []);
  const [recordSaving, setRecordSaving] = useState(false);
  const recordSaveInFlightRef = useRef(false);
  const [previewPreparing, setPreviewPreparing] = useState(false);
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => {
      setNotice((current) => (current === notice ? null : current));
    }, notice.tone === "error" ? 8000 : 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);
  const [draggedSelectedId, setDraggedSelectedId] = useState("");
  const [dragOverSelectedId, setDragOverSelectedId] = useState("");
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const questionPreviewTimerRef = useRef(null);
  useEffect(() => () => window.clearTimeout(questionPreviewTimerRef.current), []);

  const queueQuestionPreview = (event, question) => {
    window.clearTimeout(questionPreviewTimerRef.current);
    if (event.detail > 1) return;
    questionPreviewTimerRef.current = window.setTimeout(() => setPreviewQuestion(question), 350);
  };

  const openQuestionEditorFromCard = (event, question) => {
    const interactiveTarget = event.target.closest("button, input, a, [role='button']");
    if (interactiveTarget && interactiveTarget !== event.currentTarget) return;
    window.clearTimeout(questionPreviewTimerRef.current);
    setPreviewQuestion(null);
    onEditQuestion?.(question, subject);
  };
  const [templatePreviewFile, setTemplatePreviewFile] = useState(null);
  const [previewFont, setPreviewFont] = useState("biauKai");
  const [previewFontSize, setPreviewFontSize] = useState("12");
  const [activeWorkspace, setActiveWorkspace] = useState("picker");
  const handoutWorkspaceRef = useRef(null);
  useLayoutEffect(() => {
    const workspace = handoutWorkspaceRef.current;
    if (!workspace) return undefined;
    const resize = () => {
      const width = workspace.clientWidth || window.innerWidth;
      workspace.dataset.handoutLayout = width <= 760 ? "narrow" : width <= 1100 ? "medium" : "wide";
    };
    resize();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    observer?.observe(workspace);
    window.addEventListener("resize", resize);
    return () => { observer?.disconnect(); window.removeEventListener("resize", resize); };
  }, [activeWorkspace]);
  const [pickerStep, setPickerStep] = useState("select");
  const [handoutTemplateId, setHandoutTemplateId] = useState("junior_math_handout");
  const [handoutTitle, setHandoutTitle] = useState(defaultHandoutTitle);
  const [handoutFilename, setHandoutFilename] = useState(defaultHandoutFilename);
  const [handoutOutputMode, setHandoutOutputMode] = useState("teaching");
  const [handoutOptions, setHandoutOptions] = useState(() => applyHandoutWordLayout({ ...defaultHandoutOptions, margins: getDefaultHandoutMargins("junior_math_handout") }));
  const handoutPixelsPerCm = 96 / 2.54;
  const handoutContentLeft = (Number(handoutOptions.margins.left) + Number(handoutOptions.margins.gutter || 0)) * handoutPixelsPerCm;
  const handoutContentRight = Number(handoutOptions.margins.right) * handoutPixelsPerCm;
  const handoutContentTop = Number(handoutOptions.margins.top) * handoutPixelsPerCm;
  const handoutContentBottom = Number(handoutOptions.margins.bottom) * handoutPixelsPerCm;
  const handoutPageContentWidth = handoutPageWidth - handoutContentLeft - handoutContentRight;
  const handoutColumnGap = handoutPageWidth + handoutPageGap - handoutPageContentWidth;
  const [handoutIconFiles, setHandoutIconFiles] = useState([]);
  const [handoutIconLoading, setHandoutIconLoading] = useState(false);
  const [handoutIconSearch, setHandoutIconSearch] = useState("");
  const [handoutIconFolder, setHandoutIconFolder] = useState("");
  const [handoutFilters, setHandoutFilters] = useState(emptyFilters);
  const [handoutQuestionIds, setHandoutQuestionIds] = useState([]);
  const [handoutQuestionsById, setHandoutQuestionsById] = useState({});
  const [handoutQuestionLoading, setHandoutQuestionLoading] = useState(false);
  const [handoutLoadingMore, setHandoutLoadingMore] = useState(false);
  const [handoutHasMore, setHandoutHasMore] = useState(false);
  const [handoutSections, setHandoutSections] = useState(() =>
    getDefaultSectionsForTemplate("junior_math_handout").map((section) => section.type === "focus"
      ? { ...section, chapter: { number: "1", title: defaultHandoutTitle } } : section),
  );
  const [activeHandoutSectionId, setActiveHandoutSectionId] = useState(() =>
    getDefaultActiveSectionId("junior_math_handout"),
  );
  const [draggedHandoutQuestion, setDraggedHandoutQuestion] = useState(null);
  const [dragOverHandoutSectionId, setDragOverHandoutSectionId] = useState("");
  const [draggedHandoutOutlineSectionId, setDraggedHandoutOutlineSectionId] = useState("");
  const [handoutOutlineInsertTarget, setHandoutOutlineInsertTarget] = useState(null);
  const [showHandoutOutlineImages, setShowHandoutOutlineImages] = useState(true);
  const [handoutInsertTarget, setHandoutInsertTarget] = useState(null);
  const [showHandoutSettingsDialog, setShowHandoutSettingsDialog] = useState(false);
  const [handoutVimTarget, setHandoutVimTarget] = useState(null);
  const [backgroundRemovalTarget, setBackgroundRemovalTarget] = useState(null);
  const [focusPythonTarget, setFocusPythonTarget] = useState(null);
  const [backgroundRemovalPreparing, setBackgroundRemovalPreparing] = useState("");
  const backgroundRemovalRequest = useRef(0);
  useEffect(() => {
    setBackgroundRemovalTarget(null);
    setFocusPythonTarget(null);
    setBackgroundRemovalPreparing("");
    return () => { backgroundRemovalRequest.current += 1; };
  }, [subject, isActive, routeActive, activeWorkspace]);
  const [showHandoutTemplateMenu, setShowHandoutTemplateMenu] = useState(false);
  const handoutTemplatePickerRef = useRef(null);
  // Handouts no longer keep drafts; restore points and scopes use an empty draft id.
  const selectedHandoutDraftId = "";
  const [savedHandoutRestorePoint, setSavedHandoutRestorePoint] = useState(null);
  const [handoutRecordSource, setHandoutRecordSource] = useState(null);
  // Question UUIDs of the opened record that were deleted from the bank; they keep
  // their saved snapshot and no longer sync.
  const [handoutDeletedQuestionIds, setHandoutDeletedQuestionIds] = useState(() => new Set());
  const handoutRestoreScope = useMemo(() => ({ authToken }), [authToken]);
  const liveHandoutRestoreScope = useRef(handoutRestoreScope);
  liveHandoutRestoreScope.current = handoutRestoreScope;
  const handoutDocumentVersion = useRef(0);
  const handoutHistoryResetRef = useRef(null);
  const recordEditorBaselines = useRef({ scope: null });
  const pickerRecordLoadVersion = useRef(0);
  const pendingRecordEditorBaselines = useRef({ handout: true, picker: true });
  const handoutRestorePoint = savedHandoutRestorePoint?.scope === handoutRestoreScope ? savedHandoutRestorePoint : null;
  const activeHandoutRecordSource = handoutRecordSource?.scope === handoutRestoreScope ? handoutRecordSource : null;
  const handoutSaveInFlightRef = useRef(false);
  const handoutSaveShortcutRef = useRef(null);
  useEffect(() => {
    setGenerating(false);
    setWordExportProgress(null);
    setWordExportCancelling(false);
    setNotice((current) => current?.retry ? null : current);
    return () => {
      const task = wordExportTaskRef.current;
      wordExportTaskRef.current = null;
      task?.controller.abort();
      if (task?.includeHandoutOptions) handoutSaveInFlightRef.current = false;
    };
  }, [wordExportScope]);

  const [handoutPreviewMode, setHandoutPreviewMode] = useState("two");
  const [handoutShowFormattingMarks, setHandoutShowFormattingMarks] = useState(true);
  // Briefly frame questions that were just inserted so the user can see where they landed.
  const [flashQuestionIds, setFlashQuestionIds] = useState([]);
  const flashTimerRef = useRef(null);
  const flashInsertedQuestions = useCallback((ids) => {
    clearTimeout(flashTimerRef.current);
    setFlashQuestionIds(ids);
    flashTimerRef.current = setTimeout(() => setFlashQuestionIds([]), INSERT_FLASH_MS);
  }, []);
  useEffect(() => () => clearTimeout(flashTimerRef.current), []);
  // Jump the A4 preview to a question by text / UUID, or to a typed page number.
  const [handoutJumpQuery, setHandoutJumpQuery] = useState("");
  const [handoutJumpPageInput, setHandoutJumpPageInput] = useState(null);
  const [handoutJumpMessage, setHandoutJumpMessage] = useState("");
  const handoutJumpCursorRef = useRef({ query: "", index: -1 });
  // First question of the latest handout insert; the preview turns to its page once laid out.
  const handoutRevealQuestionIdRef = useRef(null);
  const [handoutPreviewPage, setHandoutPreviewPage] = useState(0);
  const [handoutPreviewPageCount, setHandoutPreviewPageCount] = useState(1);
  const [handoutBreakPages, setHandoutBreakPages] = useState({});
  const [handoutPreviewMeasureTick, setHandoutPreviewMeasureTick] = useState(0);
  const [handoutPreviewPendingImages, setHandoutPreviewPendingImages] = useState(0);
  const [handoutPreviewScale, setHandoutPreviewScale] = useState(0.9);
  const [draggedHandoutAsset, setDraggedHandoutAsset] = useState(null);
  const handoutPreviewPanelRef = useRef(null);
  const handoutFlowRef = useRef(null);
  const [handoutPreviewSelection, setHandoutPreviewSelection] = useState(null);
  const selectHandoutOutlineSection = (sectionId) => {
    setActiveHandoutSectionId(sectionId);
    setHandoutPreviewSelection({ sectionId });
  };
  const handoutContentPanelRef = useRef(null);
  // Switching blocks re-renders the whole editor. Mark the content panel busy
  // straight on the DOM (no extra React render), paint it, then switch, and
  // clear the mark once the switched content has been painted.
  const handoutContentSwitchToken = useRef(0);
  const switchHandoutOutlineSectionWithFeedback = (sectionId) => {
    const panel = handoutContentPanelRef.current;
    if (!panel || sectionId === activeHandoutSectionId || process.env.NODE_ENV === "test" ||
      typeof window.requestAnimationFrame !== "function") {
      selectHandoutOutlineSection(sectionId);
      return;
    }
    const token = ++handoutContentSwitchToken.current;
    panel.scrollTop = 0;
    panel.dataset.switching = "true";
    const afterPaint = (callback) => window.requestAnimationFrame(() => window.setTimeout(callback, 0));
    afterPaint(() => {
      if (token !== handoutContentSwitchToken.current) return;
      selectHandoutOutlineSection(sectionId);
      afterPaint(() => {
        if (token === handoutContentSwitchToken.current) delete panel.dataset.switching;
      });
    });
  };
  const handoutContentListRef = useRef(null);
  const [handoutContentListHeight, setHandoutContentListHeight] = useState(620);
  useLayoutEffect(() => {
    const list = handoutContentListRef.current;
    if (!list) return undefined;
    const cards = Array.from(list.querySelectorAll("[data-handout-question-card]")).slice(0, 2);
    const measure = () => {
      const last = cards[cards.length - 1];
      const height = last ? last.offsetTop + last.offsetHeight + 10 : 0;
      if (height > 10) setHandoutContentListHeight(Math.ceil(height));
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    cards.forEach((card) => observer?.observe(card));
    list.addEventListener("load", measure, true);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); list.removeEventListener("load", measure, true); window.removeEventListener("resize", measure); };
  }, [activeHandoutSectionId, handoutSections]);
  const [handoutPreviewEditTarget, setHandoutPreviewEditTarget] = useState(null);
  useLayoutEffect(() => {
    // The content panel scrolls on its own; start each newly selected block at its top.
    if (handoutContentPanelRef.current) handoutContentPanelRef.current.scrollTop = 0;
  }, [activeHandoutSectionId]);
  useLayoutEffect(() => {
    if (!handoutPreviewEditTarget || activeHandoutSectionId !== handoutPreviewEditTarget.sectionId) return;
    const panel = handoutContentPanelRef.current;
    const card = handoutPreviewEditTarget.questionId && Array.from(
      panel?.querySelectorAll("[data-handout-editor-question]") || [],
    ).find((node) => node.dataset.handoutEditorQuestion === handoutPreviewEditTarget.questionId);
    const target = card || panel;
    target?.scrollIntoView?.({ behavior: "smooth", block: "center", inline: "nearest" });
    target?.focus?.({ preventScroll: true });
  }, [handoutPreviewEditTarget, activeHandoutSectionId]);
  const handoutImageResizeCleanupRef = useRef(null);
  useEffect(() => () => handoutImageResizeCleanupRef.current?.(), []);

  useEffect(() => {
    if (!showHandoutTemplateMenu) return undefined;
    const closeOnOutsidePointer = (event) => {
      if (!handoutTemplatePickerRef.current?.contains(event.target)) {
        setShowHandoutTemplateMenu(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setShowHandoutTemplateMenu(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showHandoutTemplateMenu]);
  const questionStateRef = useRef({
    signature: "",
    ids: [],
    byId: {},
    nextCursor: null,
    hasMore: false,
  });
  const handoutQuestionStateRef = useRef({
    signature: "",
    ids: [],
    byId: {},
    nextCursor: null,
    hasMore: false,
  });
  const questionCacheRef = useRef(new Map());
  const handoutQuestionCacheRef = useRef(new Map());
  const activeFilterSignatureRef = useRef("");
  const activeHandoutFilterSignatureRef = useRef("");
  const sentinelRef = useRef(null);
  const handoutSentinelRef = useRef(null);

  const questions = useMemo(
    () => questionIds.map((id) => questionsById[id]).filter(Boolean),
    [questionIds, questionsById],
  );

  const filteredQuestions = questions;

  const selectedQuestions = useMemo(() => {
    return selectedIds
      .map((id) => selectedQuestionsById[id] || questionsById[id])
      .filter(Boolean);
  }, [questionsById, selectedIds, selectedQuestionsById]);

  const bilingualSignature = useMemo(() => getBilingualPreviewSignature(selectedQuestions, sectionMode), [selectedQuestions, sectionMode]);
  const bilingualSignatureRef = useRef(bilingualSignature);
  bilingualSignatureRef.current = bilingualSignature;
  const bilingualEnabledRef = useRef(false);
  bilingualEnabledRef.current = isBilingualExamTemplate(templateId) && isActive && routeActive;
  const bilingualPreviewDocument = useMemo(() => bilingualPreview?.signature === bilingualSignature
    ? withBilingualTemplate(bilingualPreview.document, templateId) : null, [bilingualPreview, bilingualSignature, templateId]);
  const hasBilingualPreview = Boolean(bilingualPreviewDocument);
  useEffect(() => {
    if (!isBilingualExamTemplate(templateId) || bilingualActiveSignatureRef.current !== bilingualSignature || !isActive || !routeActive) {
      bilingualRequestRef.current += 1;
      bilingualAbortRef.current?.abort();
      bilingualAbortRef.current = null;
      setBilingualLoading(false);
    }
    setBilingualPreview((current) => current && current.signature === bilingualSignature && isBilingualExamTemplate(templateId) ? current : null);
  }, [bilingualSignature, templateId, isActive, routeActive]);

  const handoutQuestionPool = useMemo(
    () => handoutQuestionIds.map((id) => handoutQuestionsById[id]).filter(Boolean),
    [handoutQuestionIds, handoutQuestionsById],
  );

  const filteredHandoutQuestionPool = handoutQuestionPool;

  const activeHandoutSection = useMemo(
    () =>
      handoutSections.find((section) => section.id === activeHandoutSectionId) ||
      handoutSections.find((section) => section.type === "questions") ||
      handoutSections[0],
    [activeHandoutSectionId, handoutSections],
  );

  const handoutQuestionsForOutput = useMemo(
    () =>
      handoutSections.flatMap((section) =>
        section.type === "questions" ? section.questions : [],
      ),
    [handoutSections],
  );

  const handoutQuestionIdsForOutput = useMemo(
    () => handoutQuestionsForOutput.map((question) => question.id),
    [handoutQuestionsForOutput],
  );

  const handoutTotalQuestionCount = handoutQuestionIdsForOutput.length;
  const hasHandoutContent = mathHandoutTemplateIds.has(handoutTemplateId) &&
    hasHandoutSectionContent(handoutSections, handoutOptions);
  const canExportHandout = Boolean(handoutTotalQuestionCount || hasHandoutContent);
  const familySelectionRef = useRef(null);
  familySelectionRef.current = { subject, selectedIds, handoutSections, handoutTemplateId, activeWorkspace, familyReplacementTarget };
  useEffect(() => {
    setFamilyReplacementTarget(null);
    setFamilyAnalysisIds(null);
  }, [subject, activeWorkspace, handoutTemplateId]);

  const rememberFamilyQuestion = (question) => {
    if (!question?.id) return;
    setSelectedQuestionsById((current) => ({ ...current, [question.id]: question }));
    setQuestionsById((current) => ({ ...current, [question.id]: question }));
    setHandoutQuestionsById((current) => ({ ...current, [question.id]: question }));
    setHandoutSections((sections) => sections.map((section) => ({ ...section, questions: section.questions?.map((item) => item.id === question.id ? { ...item, ...question } : item) })));
    for (const cache of [questionCacheRef.current, handoutQuestionCacheRef.current]) {
      for (const value of cache.values()) if (value.byId[question.id]) value.byId = { ...value.byId, [question.id]: question };
    }
    for (const state of [questionStateRef.current, handoutQuestionStateRef.current]) if (state.byId[question.id]) state.byId = { ...state.byId, [question.id]: question };
  };
  const openFamilyAction = (event, question, sectionId = null) => {
    event.stopPropagation();
    window.clearTimeout(questionPreviewTimerRef.current);
    setPreviewQuestion(null);
    setFamilyReplacementTarget({ question, sectionId, subject, templateId: handoutTemplateId, workspace: activeWorkspace });
  };
  const openFamilyAnalysis = (event, question) => {
    event.stopPropagation();
    window.clearTimeout(questionPreviewTimerRef.current);
    setPreviewQuestion(null);
    setFamilyAnalysisIds([question.id]);
  };
  const replaceFamilyQuestion = (replacement) => {
    const live = familySelectionRef.current;
    const target = live.familyReplacementTarget;
    if (!target || target.subject !== live.subject || target.workspace !== live.activeWorkspace) return false;
    if (target.sectionId) {
      if (target.templateId !== live.handoutTemplateId || !live.handoutSections.some((section) => section.id === target.sectionId && section.questions?.some((item) => item.id === target.question.id))) return false;
      if (live.handoutSections.some((section) => section.questions?.some((item) => item.id === replacement.id && item.id !== target.question.id))) return false;
      setHandoutSections((sections) => sections.map((section) => section.id === target.sectionId ? { ...section, questions: section.questions.map((item) => item.id === target.question.id ? { ...item, ...replacement } : item) } : section));
    } else {
      if (!live.selectedIds.includes(target.question.id) || (replacement.id !== target.question.id && live.selectedIds.includes(replacement.id))) return false;
      setSelectedIds((ids) => ids.map((identity) => identity === target.question.id ? replacement.id : identity));
    }
    rememberFamilyQuestion(replacement);
    setNotice({ tone: "success", text: "已替換題目，保留原題位置與題數。" });
    return true;
  };
  const refreshFamilyAnalysis = async () => {
    const scopedSubject = subject;
    const [families, ...updated] = await Promise.all([
      questionBankApi.listQuestionFamilies({ subject, authToken }),
      ...(familyAnalysisIds || []).map((id) => questionBankApi.getQuestion({ subject, authToken, id })),
    ]);
    if (familySelectionRef.current.subject !== scopedSubject) return;
    setQuestionFamilies(families || []);
    updated.forEach(rememberFamilyQuestion);
  };
  const familyActionButton = (question, sectionId = null) => <>
    <FamilyAction type="button" aria-label={`${question.family?.id ? "同族換題" : "換題"} ${shortId(question.id)}`} onClick={(event) => openFamilyAction(event, question, sectionId)}>
      <span title={question.family?.name || "未歸題族"}>{question.family?.name || "未歸題族"}</span><strong>換題</strong>
    </FamilyAction>
    {!question.family?.id && <FamilySetupAction type="button" aria-label={`AI 建議題族 ${shortId(question.id)}`} onClick={(event) => openFamilyAnalysis(event, question)}>AI 題族</FamilySetupAction>}
  </>;

  const handoutQuestionIdSet = useMemo(
    () => new Set(handoutQuestionIdsForOutput),
    [handoutQuestionIdsForOutput],
  );

  const handoutQuestionIndexById = useMemo(() => {
    const next = new Map();
    let number = 1;
    handoutSections.forEach((section) => {
      if (section.type !== "questions" || !section.questions?.length) return;
      number = getHandoutStartNumber(section.startNumber) ?? number;
      section.questions.forEach((question, index) => {
        next.set(question.id, number);
        next.set(handoutQuestionSlotKey(section.id, index), number);
        number += 1;
      });
    });
    return next;
  }, [handoutSections]);

  const activeHandoutSectionQuestionIds = useMemo(
    () => new Set((activeHandoutSection?.questions || []).map((question) => question.id)),
    [activeHandoutSection],
  );

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const visibleSelectedCount = useMemo(
    () => filteredQuestions.filter((question) => selectedIdSet.has(question.id)).length,
    [filteredQuestions, selectedIdSet],
  );

  const allVisibleQuestionsSelected =
    filteredQuestions.length > 0 && visibleSelectedCount === filteredQuestions.length;

  const previewSections = useMemo(
    () => buildHandoutPreviewSections(selectedQuestions, sectionMode),
    [sectionMode, selectedQuestions],
  );

  const previewFontConfig = useMemo(
    () =>
      previewFontSizes.find((item) => item.value === previewFontSize) ||
      previewFontSizes[1],
    [previewFontSize],
  );

  const handoutQuestionSections = useMemo(
    () => handoutSections.filter((section) => section.type === "questions"),
    [handoutSections],
  );

  const availableHandoutTemplates = useMemo(() => {
    const availableIds = new Set(templates.map((template) => template.id));
    return handoutTemplateChoices.filter(
      (choice) =>
        availableIds.has(choice.id) ||
        fallbackTemplates.some((template) => template.id === choice.id),
    );
  }, [templates]);

  const templateMetadataById = useMemo(
    () => new Map(templates.map((template) => [template.id, template])),
    [templates],
  );

  const selectedHandoutTemplate = useMemo(
    () =>
      availableHandoutTemplates.find((template) => template.id === handoutTemplateId) ||
      availableHandoutTemplates[0] ||
      handoutTemplateChoices[0],
    [availableHandoutTemplates, handoutTemplateId],
  );

  const isPickerExamTemplate = pickerExamTemplateIds.has(templateId);
  const isExamHandoutTemplate = selectedHandoutTemplate.id === "exam_paper";
  const examTemplateFilesForUi =
    templateMetadataById.get("exam_paper")?.files?.length
      ? templateMetadataById.get("exam_paper").files
      : fallbackExamTemplateFiles;
  const handoutDisplayTitle = isExamHandoutTemplate
    ? handoutTitle || defaultExamHandoutTitle
    : handoutOptions.focus.unitTitle || handoutTitle || defaultHandoutTitle;
  const handoutExamSettings = handoutOptions.exam || defaultHandoutOptions.exam;
  const handoutExamParts = useMemo(
    () => getExamPartConfigs(handoutExamSettings, handoutSections),
    [handoutExamSettings, handoutSections],
  );
  const handoutExamCautionLines = useMemo(
    () => buildExamCautionLines(handoutExamSettings, handoutExamParts),
    [handoutExamParts, handoutExamSettings],
  );
  const handoutExamQuestionCount =
    toOptionalPositiveNumber(handoutExamSettings.questionCount) || handoutTotalQuestionCount;
  const handoutWorkspaceLabel = isExamHandoutTemplate ? "檢測卷" : "講義";
  const handoutWorkspaceTitle = `${handoutWorkspaceLabel}製作`;
  const handoutOutlineTitle = `${handoutWorkspaceLabel}列表`;
  const visibleHandoutSections = handoutSections.filter(
    (section) => showHandoutOutlineImages || section.type !== "image",
  );
  const handoutPickerTitle = `${handoutWorkspaceLabel}挑題`;
  const handoutQuestionTargetLabel = isExamHandoutTemplate ? "檢測題目部分" : "題型區塊";

  const selectedHandoutRole =
    handoutIconRoles.find((role) => role.value === handoutOptions.activeIconRole) ||
    handoutIconRoles[0];

  const handoutIconFolders = useMemo(() => getHandoutAssetFolders(handoutIconFiles, handoutOptions.iconPrefix),
    [handoutIconFiles, handoutOptions.iconPrefix]);
  const filteredHandoutIconFiles = useMemo(() => filterHandoutAssets(handoutIconFiles, {
    search: handoutIconSearch, folder: handoutIconFolder, prefix: handoutOptions.iconPrefix,
  }), [handoutIconFiles, handoutIconSearch, handoutIconFolder, handoutOptions.iconPrefix]);

  const draggableHandoutImages = useMemo(() => filteredHandoutIconFiles.filter(
    (file) => wordImageExtensions.has(getAssetExtension(file.key)),
  ), [filteredHandoutIconFiles]);

  useLayoutEffect(() => {
    if (activeWorkspace !== "handout" || isExamHandoutTemplate) return undefined;
    const panel = handoutPreviewPanelRef.current;
    if (!panel) return undefined;
    const updateScale = () => {
      const preferred = 0.9;
      const pagesShown = handoutPreviewMode === "two" ? 2 : 1;
      const requiredWidth = pagesShown * handoutPageWidth + (pagesShown - 1) * handoutPageGap;
      const availableWidth = panel.clientWidth ? panel.clientWidth - 140 : requiredWidth * preferred;
      setHandoutPreviewScale(Math.max(0.1, Math.min(preferred, availableWidth / requiredWidth)));
    };
    updateScale();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateScale) : null;
    observer?.observe(panel);
    window.addEventListener("resize", updateScale);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [activeWorkspace, handoutPreviewMode, isExamHandoutTemplate]);

  useLayoutEffect(() => {
    if (activeWorkspace !== "handout" || isExamHandoutTemplate) return undefined;
    const flow = handoutFlowRef.current;
    if (!flow) return undefined;
    const forcedPages = getForcedHandoutPageCount(handoutSections, handoutOptions);
    const updatePageCount = () => {
      const measuredPages = Math.ceil(
        // scrollWidth rounds to whole pixels; do not count that rounding as a blank page.
        (flow.scrollWidth + handoutColumnGap - 1) / (handoutPageWidth + handoutPageGap),
      );
      setHandoutPreviewPageCount(Math.max(1, forcedPages, measuredPages || 1));
      setHandoutPreviewPendingImages(flow.querySelectorAll('[data-handout-image-size="pending"]').length);
      const breaks = measureHandoutBreakPages(flow, handoutSections, handoutOptions, handoutPageWidth + handoutPageGap);
      setHandoutBreakPages((current) => JSON.stringify(current) === JSON.stringify(breaks) ? current : breaks);
    };
    updatePageCount();
    return observeHandoutFlow(flow, updatePageCount);
  }, [activeWorkspace, handoutOptions, handoutSections, handoutQuestionsForOutput,
    handoutPreviewMeasureTick, handoutOutputMode, handoutColumnGap,
    isExamHandoutTemplate, handoutPreviewScale]);

  const showHandoutPreviewPage = (page) => {
    const shown = handoutPreviewMode === "two" ? 2 : 1;
    const last = Math.max(0, handoutPreviewPageCount - 1);
    setHandoutPreviewPage(Math.floor(Math.max(0, Math.min(last, page)) / shown) * shown);
  };

  const jumpToHandoutQuestion = (step = 1) => {
    const query = handoutJumpQuery.trim().toLowerCase();
    if (!query) { setHandoutJumpMessage(""); return; }
    const matches = [];
    const seen = {};
    handoutSections.forEach((section) => {
      if (section.type !== "questions") return;
      (section.questions || []).forEach((question) => {
        const occurrence = seen[question.id] || 0;
        seen[question.id] = occurrence + 1;
        const haystack = [question.id, question.math_bank?.id, question.question_uuid, question.prompt_md,
          question.prompt, question.stem, question.answer_md, question.solution_md]
          .filter(Boolean).join("\n").toLowerCase();
        if (haystack.includes(query)) matches.push({ id: question.id, occurrence, sectionId: section.id });
      });
    });
    if (!matches.length) { setHandoutJumpMessage("無結果"); return; }
    const cursor = handoutJumpCursorRef.current;
    // Enter again with the same text moves to the next match; Shift+Enter goes back.
    const index = cursor.query === query
      ? (cursor.index + step + matches.length) % matches.length
      : step < 0 ? matches.length - 1 : 0;
    handoutJumpCursorRef.current = { query, index };
    const match = matches[index];
    const page = getHandoutQuestionPreviewPage(handoutFlowRef.current, match.id,
      handoutPageWidth + handoutPageGap, match.occurrence);
    setActiveHandoutSectionId(match.sectionId);
    if (page === null) { setHandoutJumpMessage(`${index + 1}/${matches.length} · 排版中`); return; }
    showHandoutPreviewPage(page);
    flashInsertedQuestions([match.id]);
    setHandoutJumpMessage(`${index + 1}/${matches.length} · 第 ${page + 1} 頁`);
  };

  const jumpToHandoutPage = () => {
    const number = Number.parseInt(handoutJumpPageInput, 10);
    setHandoutJumpPageInput(null);
    if (!Number.isFinite(number)) return;
    showHandoutPreviewPage(Math.max(1, Math.min(handoutPreviewPageCount, number)) - 1);
  };

  useLayoutEffect(() => {
    const questionId = handoutRevealQuestionIdRef.current;
    if (!questionId || activeWorkspace !== "handout" || isExamHandoutTemplate) return;
    const page = getHandoutQuestionPreviewPage(handoutFlowRef.current, questionId, handoutPageWidth + handoutPageGap);
    if (page === null) return;
    handoutRevealQuestionIdRef.current = null;
    const shown = handoutPreviewMode === "two" ? 2 : 1;
    setHandoutPreviewPage(Math.floor(page / shown) * shown);
  }, [handoutSections, handoutPreviewPageCount, handoutPreviewMode, activeWorkspace, isExamHandoutTemplate]);

  useLayoutEffect(() => {
    if (!handoutPreviewSelection || activeWorkspace !== "handout" || isExamHandoutTemplate) return;
    const page = getHandoutSectionPreviewPage(handoutFlowRef.current, handoutSections,
      handoutPreviewSelection.sectionId, handoutPageWidth + handoutPageGap);
    if (page === null) return;
    const shown = handoutPreviewMode === "two" ? 2 : 1;
    setHandoutPreviewPage(Math.floor(page / shown) * shown);
    setHandoutPreviewSelection(null);
  }, [handoutPreviewSelection, activeWorkspace, isExamHandoutTemplate, handoutSections,
    handoutPreviewMode, handoutPreviewScale, handoutPreviewMeasureTick, handoutPreviewPageCount]);

  useEffect(() => {
    if (activeWorkspace !== "handout" || isExamHandoutTemplate || !document.fonts?.load ||
      (handoutOptions.bodyFontName !== "標楷體" && handoutOptions.headingFontName !== "標楷體")) {
      return undefined;
    }
    let cancelled = false;
    document.fonts.load('14px "HandoutKai"', "講義數學").then(() => {
      if (!cancelled) setHandoutPreviewMeasureTick((tick) => tick + 1);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeWorkspace, isExamHandoutTemplate, handoutOptions.bodyFontName, handoutOptions.headingFontName]);

  useEffect(() => {
    setHandoutPreviewPage((page) => Math.min(page, Math.max(0, handoutPreviewPageCount - 1)));
  }, [handoutPreviewPageCount]);

  useEffect(() => {
    if (!routeActive || !isActive || activeWorkspace !== "handout" || isExamHandoutTemplate) {
      return undefined;
    }
    const handlePreviewPageShortcut = (event) => {
      if (event.key !== "Enter" || event.defaultPrevented || event.isComposing ||
        event.keyCode === 229 || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      const interactive = 'input, textarea, select, button, a[href], [role="button"], [role="textbox"], [contenteditable]:not([contenteditable="false"])';
      if (event.target?.closest?.(interactive) || document.activeElement?.closest?.(interactive)) return;
      if (showHandoutTemplateMenu || document.querySelector('[role="dialog"], dialog[open], .swal2-container')) return;
      event.preventDefault();
      const step = handoutPreviewMode === "two" ? 2 : 1;
      // Two-page spreads start on odd pages (1–2, 3–4…), including the last one.
      const lastStart = step === 2 ? Math.floor((handoutPreviewPageCount - 1) / 2) * 2 : handoutPreviewPageCount - 1;
      setHandoutPreviewPage((page) => Math.max(0, Math.min(lastStart, page + (event.shiftKey ? -step : step))));
    };
    window.addEventListener("keydown", handlePreviewPageShortcut);
    return () => window.removeEventListener("keydown", handlePreviewPageShortcut);
  }, [activeWorkspace, handoutPreviewMode, handoutPreviewPageCount, isActive,
    isExamHandoutTemplate, routeActive, showHandoutTemplateMenu]);

  const handoutLineHeight = Number(handoutOptions.lineSpacing) || 1.25;

  const handoutFilteredUnits = useMemo(() => {
    if (handoutFilters.grade_id === filterNoneValue) return [];
    if (handoutFilters.grade_id === filterAllValue) return units;
    return units.filter((unit) => filterMatches(handoutFilters.grade_id, unit.grade?.id));
  }, [handoutFilters.grade_id, units]);

  const shouldLoadHandoutQuestionResults = useMemo(
    () => Boolean(handoutFilters.search.trim()) || Object.entries(handoutFilters).some(([key, value]) =>
      key !== "search" && (Array.isArray(value) ? value.length > 0 : Boolean(value) && value !== filterNoneValue),
    ),
    [handoutFilters],
  );

  const filteredUnits = useMemo(() => {
    if (filters.grade_id === filterNoneValue) return [];
    if (filters.grade_id === filterAllValue) return units;
    return units.filter((unit) => filterMatches(filters.grade_id, unit.grade?.id));
  }, [filters.grade_id, units]);

  const shouldLoadQuestionResults = useMemo(
    () => Boolean(filters.search.trim()) || Object.entries(filters).some(([key, value]) =>
      key !== "search" && (Array.isArray(value) ? value.length > 0 : Boolean(value) && value !== filterNoneValue),
    ),
    [filters],
  );

  const activeFilters = useMemo(
    () => ({
      search: filters.search.trim(),
      family_id: filters.family_id,
      grade_id: filterParam(filters.grade_id),
      unit_id: filterParam(filters.unit_id),
      type: filters.type,
      difficulty: filters.difficulty,
      category: filters.category,
      status: filters.status,
      visibility: filters.visibility,
      is_good_question: filters.is_good_question,
      question_source: filters.question_source,
    }),
    [filters],
  );

  const activeFilterSignature = useMemo(
    () => JSON.stringify({ auth: Boolean(authToken), filters: activeFilters }),
    [activeFilters, authToken],
  );
  activeFilterSignatureRef.current = activeFilterSignature;

  const handoutActiveFilters = useMemo(
    () => ({
      search: handoutFilters.search.trim(),
      family_id: handoutFilters.family_id,
      grade_id: filterParam(handoutFilters.grade_id),
      unit_id: filterParam(handoutFilters.unit_id),
      type: handoutFilters.type,
      difficulty: handoutFilters.difficulty,
      category: handoutFilters.category,
      is_good_question: handoutFilters.is_good_question,
      question_source: handoutFilters.question_source,
    }),
    [handoutFilters],
  );

  const activeHandoutFilterSignature = useMemo(
    () => JSON.stringify({ auth: Boolean(authToken), filters: handoutActiveFilters }),
    [handoutActiveFilters, authToken],
  );
  activeHandoutFilterSignatureRef.current = activeHandoutFilterSignature;

  const applyQuestionState = useCallback((nextState, options = {}) => {
    const { cache = true } = options;
    questionStateRef.current = nextState;
    setQuestionIds(nextState.ids);
    setQuestionsById(nextState.byId);
    setHasMore(nextState.hasMore);

    if (cache && nextState.signature) {
      questionCacheRef.current.set(nextState.signature, nextState);
    }
  }, []);

  const clearQuestionState = useCallback(() => {
    applyQuestionState(
      {
        signature: "",
        ids: [],
        byId: {},
        nextCursor: null,
        hasMore: false,
      },
      { cache: false },
    );
  }, [applyQuestionState]);

  const applyHandoutQuestionState = useCallback((nextState, options = {}) => {
    const { cache = true } = options;
    handoutQuestionStateRef.current = nextState;
    setHandoutQuestionIds(nextState.ids);
    setHandoutQuestionsById(nextState.byId);
    setHandoutHasMore(nextState.hasMore);

    if (cache && nextState.signature) {
      handoutQuestionCacheRef.current.set(nextState.signature, nextState);
    }
  }, []);

  const clearHandoutQuestionState = useCallback(() => {
    applyHandoutQuestionState(
      {
        signature: "",
        ids: [],
        byId: {},
        nextCursor: null,
        hasMore: false,
      },
      { cache: false },
    );
  }, [applyHandoutQuestionState]);

  const loadTaxonomy = useCallback(async () => {
    setLoading(true);
    setNotice(null);
    try {
      const [gradeData, unitData, sourceData, templateData, familyData] = await Promise.all([
        listGrades({ authToken }),
        listUnits({ authToken }),
        listQuestionSources({ authToken }).catch(() => []),
        fetchWordTemplates(),
        authToken ? listQuestionFamilies({ authToken }) : Promise.resolve([]),
      ]);
      setGrades(gradeData || []);
      setQuestionFamilies(familyData || []);
      setUnits(unitData || []);
      setQuestionSources(normalizeQuestionSourceOptions(sourceData));
      if (templateData.success && templateData.templates?.length) {
        const nextTemplates = templateData.templates.filter((template) =>
          supportedTemplateIds.has(template.id),
        );
        setTemplates(nextTemplates.length ? nextTemplates : fallbackTemplates);
        const preferredTemplate = nextTemplates.find(
          (template) => template.id === "junior_exam_paper",
        );
        setTemplateId(preferredTemplate?.id || "junior_exam_paper");
      }
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "資料讀取失敗" });
    } finally {
      setLoading(false);
    }
  }, [authToken, fetchWordTemplates, listGrades, listQuestionFamilies, listQuestionSources, listUnits]);

  const loadHandoutIcons = useCallback(async () => {
    setHandoutIconLoading(true);
    try {
      const result = await listAssets({
        authToken,
        prefix: handoutOptions.iconPrefix,
        all: true,
      });
      if (!result.success) {
        throw new Error(result.error || "素材讀取失敗");
      }
      const files = (result.files || []).filter(isHandoutIconFile).map(withAssetUrl);
      setHandoutIconFiles(files);
      setHandoutIconFolder((current) => getHandoutAssetFolders(files, handoutOptions.iconPrefix).includes(current) ? current : "");
    } catch (error) {
      setHandoutIconFiles([]);
      setNotice({ tone: "error", text: error.message || "素材讀取失敗" });
    } finally {
      setHandoutIconLoading(false);
    }
  }, [authToken, handoutOptions.iconPrefix]);

  useEffect(() => {
    if (activeWorkspace !== "handout" || handoutIconFiles.length) return;
    loadHandoutIcons();
  }, [activeWorkspace, handoutIconFiles.length, loadHandoutIcons]);

  const loadQuestionPage = useCallback(async ({ reset = false, force = false } = {}) => {
    if (!shouldLoadQuestionResults) {
      clearQuestionState();
      return;
    }

    const signature = activeFilterSignature;
    const currentState = questionStateRef.current;

    if (reset && !force) {
      const cachedState = questionCacheRef.current.get(signature);
      if (cachedState) {
        applyQuestionState(cachedState, { cache: false });
        return;
      }
    }

    if (!reset && (!currentState.hasMore || !currentState.nextCursor)) {
      return;
    }

    if (reset) {
      setQuestionLoading(true);
    } else {
      setLoadingMore(true);
    }
    setNotice(null);
    try {
      const questionData = await searchQuestions({
        authToken,
        params: {
          ...activeFilters,
          limit: questionPageSize,
          include_details: 1,
          ...(reset ? {} : { cursor: currentState.nextCursor }),
        },
      });

      if (activeFilterSignatureRef.current !== signature) {
        return;
      }

      const pageQuestions = questionData.results || [];

      const baseState = reset
        ? { ids: [], byId: {} }
        : questionStateRef.current;
      const nextById = { ...baseState.byId };
      const nextIds = reset ? [] : [...baseState.ids];
      const nextIdSet = new Set(nextIds);

      pageQuestions.forEach((question) => {
        nextById[question.id] = {
          ...(nextById[question.id] || {}),
          ...question,
        };
        if (!nextIdSet.has(question.id)) {
          nextIds.push(question.id);
          nextIdSet.add(question.id);
        }
      });

      applyQuestionState({
        signature,
        ids: nextIds,
        byId: nextById,
        nextCursor: questionData.next_cursor || null,
        hasMore: Boolean(questionData.has_more),
      });
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "題目讀取失敗" });
    } finally {
      if (reset) {
        setQuestionLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [
    searchQuestions,
    activeFilterSignature,
    activeFilters,
    applyQuestionState,
    authToken,
    clearQuestionState,
    shouldLoadQuestionResults,
  ]);

  const loadHandoutQuestionPage = useCallback(async ({ reset = false, force = false } = {}) => {
    if (!shouldLoadHandoutQuestionResults) {
      clearHandoutQuestionState();
      return;
    }

    const signature = activeHandoutFilterSignature;
    const currentState = handoutQuestionStateRef.current;

    if (reset && !force) {
      const cachedState = handoutQuestionCacheRef.current.get(signature);
      if (cachedState) {
        applyHandoutQuestionState(cachedState, { cache: false });
        return;
      }
    }

    if (!reset && (!currentState.hasMore || !currentState.nextCursor)) {
      return;
    }

    if (reset) {
      setHandoutQuestionLoading(true);
    } else {
      setHandoutLoadingMore(true);
    }
    setNotice(null);
    try {
      const questionData = await searchQuestions({
        authToken,
        params: {
          ...handoutActiveFilters,
          limit: questionPageSize,
          include_details: 1,
          ...(reset ? {} : { cursor: currentState.nextCursor }),
        },
      });

      if (activeHandoutFilterSignatureRef.current !== signature) {
        return;
      }

      const pageQuestions = questionData.results || [];

      const baseState = reset
        ? { ids: [], byId: {} }
        : handoutQuestionStateRef.current;
      const nextById = { ...baseState.byId };
      const nextIds = reset ? [] : [...baseState.ids];
      const nextIdSet = new Set(nextIds);

      pageQuestions.forEach((question) => {
        nextById[question.id] = {
          ...(nextById[question.id] || {}),
          ...question,
        };
        if (!nextIdSet.has(question.id)) {
          nextIds.push(question.id);
          nextIdSet.add(question.id);
        }
      });

      applyHandoutQuestionState({
        signature,
        ids: nextIds,
        byId: nextById,
        nextCursor: questionData.next_cursor || null,
        hasMore: Boolean(questionData.has_more),
      });
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "講義題目讀取失敗" });
    } finally {
      if (reset) {
        setHandoutQuestionLoading(false);
      } else {
        setHandoutLoadingMore(false);
      }
    }
  }, [
    searchQuestions,
    activeHandoutFilterSignature,
    applyHandoutQuestionState,
    authToken,
    clearHandoutQuestionState,
    handoutActiveFilters,
    shouldLoadHandoutQuestionResults,
  ]);

  useEffect(() => {
    loadTaxonomy();
  }, [loadTaxonomy]);


  useEffect(() => {
    if (!shouldLoadQuestionResults) {
      clearQuestionState();
      return undefined;
    }

    const cachedState = questionCacheRef.current.get(activeFilterSignature);
    if (cachedState) {
      applyQuestionState(cachedState, { cache: false });
      setQuestionLoading(false);
      return undefined;
    }

    clearQuestionState();
    setQuestionLoading(true);
    const timer = window.setTimeout(
      () => loadQuestionPage({ reset: true }),
      260,
    );
    return () => window.clearTimeout(timer);
  }, [
    searchQuestions,
    activeFilterSignature,
    applyQuestionState,
    clearQuestionState,
    loadQuestionPage,
    shouldLoadQuestionResults,
  ]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (
      !sentinel ||
      !shouldLoadQuestionResults ||
      !hasMore ||
      questionLoading ||
      loadingMore
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadQuestionPage({ reset: false });
        }
      },
      { rootMargin: "260px 0px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    hasMore,
    loadQuestionPage,
    loadingMore,
    questionLoading,
    shouldLoadQuestionResults,
  ]);

  useEffect(() => {
    if (activeWorkspace !== "handout") return undefined;

    if (!shouldLoadHandoutQuestionResults) {
      clearHandoutQuestionState();
      return undefined;
    }

    const cachedState = handoutQuestionCacheRef.current.get(activeHandoutFilterSignature);
    if (cachedState) {
      applyHandoutQuestionState(cachedState, { cache: false });
      setHandoutQuestionLoading(false);
      return undefined;
    }

    clearHandoutQuestionState();
    setHandoutQuestionLoading(true);
    const timer = window.setTimeout(
      () => loadHandoutQuestionPage({ reset: true }),
      260,
    );
    return () => window.clearTimeout(timer);
  }, [
    searchQuestions,
    activeHandoutFilterSignature,
    activeWorkspace,
    applyHandoutQuestionState,
    clearHandoutQuestionState,
    loadHandoutQuestionPage,
    shouldLoadHandoutQuestionResults,
  ]);

  useEffect(() => {
    const sentinel = handoutSentinelRef.current;
    if (
      activeWorkspace !== "handout" ||
      !sentinel ||
      !shouldLoadHandoutQuestionResults ||
      !handoutHasMore ||
      handoutQuestionLoading ||
      handoutLoadingMore
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadHandoutQuestionPage({ reset: false });
        }
      },
      { rootMargin: "260px 0px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    activeWorkspace,
    handoutHasMore,
    handoutLoadingMore,
    handoutQuestionLoading,
    loadHandoutQuestionPage,
    shouldLoadHandoutQuestionResults,
  ]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "grade_id"
        ? {
          unit_id: reconcileUnitFilter(prev.unit_id, value, units, (unit) => unit.grade?.id),
        }
        : {}),
    }));
  };

  const updateHandoutFilter = (key, value) => {
    setHandoutFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "grade_id"
        ? {
          unit_id: reconcileUnitFilter(prev.unit_id, value, units, (unit) => unit.grade?.id),
        }
        : {}),
    }));
  };

  const updateHandoutOption = (key, value) => {
    setHandoutOptions((prev) => ({ ...prev, [key]: value }));
  };

  const updateHandoutNestedOption = (group, key, value) => {
    setHandoutOptions((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [key]: value,
      },
    }));
  };

  const updateExamPartOption = (partId, key, value) => {
    setHandoutOptions((prev) => {
      const nextParts = normalizeExamPartOptions(prev.exam?.parts).map((part) =>
        part.id === partId ? { ...part, [key]: value } : part,
      );
      return {
        ...prev,
        exam: {
          ...prev.exam,
          parts: nextParts,
        },
      };
    });
  };

  const selectPickerTemplate = (nextTemplateId) => {
    const nextTitle = pickerTemplateTitles[nextTemplateId] || defaultHandoutTitle;
    const nextFilename = `${nextTitle}.docx`;
    const replaceableTitles = [
      defaultHandoutTitle,
      defaultExamHandoutTitle,
      ...Object.values(pickerTemplateTitles),
      ...legacyExamHandoutTitles,
    ];
    const replaceableFilenames = [
      defaultHandoutFilename,
      defaultExamHandoutFilename,
      ...Object.values(pickerTemplateTitles).map((title) => `${title}.docx`),
      ...legacyExamHandoutFilenames,
    ];

    setTemplateId(nextTemplateId);
    setDocumentTitle((current) =>
      !current.trim() || replaceableTitles.includes(current) ? nextTitle : current,
    );
    setOutputFilename((current) =>
      !current.trim() || replaceableFilenames.includes(current) ? nextFilename : current,
    );
  };

  const selectHandoutTemplate = (nextTemplateId) => {
    const nextSections = buildSectionsForTemplate(nextTemplateId, handoutSections);
    const nextTitle = nextTemplateId === "exam_paper"
      ? defaultExamHandoutTitle
      : defaultHandoutTitle;
    const nextFilename = nextTemplateId === "exam_paper"
      ? defaultExamHandoutFilename
      : defaultHandoutFilename;
    const replaceableTitles = [
      defaultHandoutTitle,
      defaultExamHandoutTitle,
      ...legacyExamHandoutTitles,
    ];
    const replaceableFilenames = [
      defaultHandoutFilename,
      defaultExamHandoutFilename,
      ...legacyExamHandoutFilenames,
    ];

    const previousMargins = getDefaultHandoutMargins(handoutTemplateId);
    setHandoutOptions((current) => {
      const next = Object.keys(previousMargins).every((key) => Number(current.margins[key]) === previousMargins[key])
        ? { ...current, margins: getDefaultHandoutMargins(nextTemplateId) } : current;
      // The exam renderer also reads these options, so do not carry a handout preset into it.
      if (nextTemplateId !== "exam_paper" || !isHandoutWordLayout(next)) return next;
      const defaultFrameUrl = getHandoutFocusFrameUrl(applyHandoutWordLayout({}).focus.frame);
      return { ...next, layoutPreset: "", icons: Object.fromEntries(Object.entries(next.icons || {}).filter(([role, selected]) => {
        const standard = handoutWordDefaultIcons[role];
        return !standard || (selected?.key !== standard.key && selected?.url !== standard.url);
      })), focus: { ...next.focus, frame: getHandoutFocusFrameUrl(next.focus.frame) === defaultFrameUrl ? undefined : next.focus.frame } };
    });
    setHandoutTemplateId(nextTemplateId);
    setShowHandoutTemplateMenu(false);
    setHandoutSections(nextSections);
    setActiveHandoutSectionId(getDefaultActiveSectionId(nextTemplateId, nextSections));
    setHandoutTitle((current) =>
      !current.trim() || replaceableTitles.includes(current) ? nextTitle : current,
    );
    setHandoutFilename((current) =>
      !current.trim() || replaceableFilenames.includes(current) ? nextFilename : current,
    );
  };

  const selectHandoutIcon = (file) => {
    setHandoutOptions((prev) => ({
      ...prev,
      icons: {
        ...prev.icons,
        [prev.activeIconRole]: { ...file, ...Object.fromEntries(Object.entries(handoutIconSize(prev.icons[prev.activeIconRole]))
          .filter(([, value]) => value !== undefined)) },
      },
    }));
  };

  const updateHandoutIconSize = (field, value) => setHandoutOptions((current) => ({ ...current,
    icons: { ...current.icons, [current.activeIconRole]: { ...current.icons[current.activeIconRole], [field]: value } },
  }));

  const selectHandoutLayoutPreset = (value) => {
    setHandoutIconFolder("");
    const wordLayout = isHandoutWordLayout({ layoutPreset: value });
    setHandoutOptions((current) => wordLayout ? applyHandoutWordLayout(current, value) : applyHandoutSimpleLayout(current));
    if (!wordLayout) setHandoutSections((current) => current.map((section) => {
      if (section.type !== "focus") return section;
      const plain = { ...section };
      delete plain.focusBackgroundStyle;
      delete plain.background_style;
      return plain;
    }));
    if (wordLayout && !handoutSections.some((section) => section.chapter)) {
      const firstFocus = handoutSections.find((section) => section.type === "focus");
      if (firstFocus) setHandoutSections((current) => current.map((section) => section.id === firstFocus.id
        ? { ...section, chapter: { number: "1", title: handoutTitle || "單元名稱" } } : section));
    }
  };

  const clearHandoutIcon = (role) => {
    setHandoutOptions((prev) => {
      const nextIcons = { ...prev.icons };
      delete nextIcons[role];
      return { ...prev, icons: nextIcons };
    });
  };

  const buildHandoutDraftPayload = () => ({
    familyDraws: handoutFamilyDraws,
    templateId: handoutTemplateId,
    title: handoutTitle,
    filename: handoutFilename,
    outputMode: handoutOutputMode,
    options: handoutOptions,
    sections: handoutSections,
    activeSectionId: activeHandoutSectionId,
    filters: handoutFilters,
  });

  const buildPickerEditorPayload = () => ({
    templateId, title: documentTitle, filename: outputFilename, outputMode, sectionMode, examRange, familyDraws,
    questions: selectedIds.map((id) => selectedQuestionsById[id] || questionsById[id]),
    bilingualTranslations: selectBilingualTranslations(selectedIds, bilingualPreview?.translations, bilingualTranslations),
  });

  // Capture applied state after defaults have been restored. Navigation and
  // thumbnail rendering do not make an otherwise saved document unsaved.
  useLayoutEffect(() => {
    if (recordEditorBaselines.current.scope !== handoutRestoreScope) {
      recordEditorBaselines.current = { scope: handoutRestoreScope };
      pendingRecordEditorBaselines.current = { handout: true, picker: true };
    }
    for (const workspace of ["handout", "picker"]) {
      if (pendingRecordEditorBaselines.current[workspace]) {
        recordEditorBaselines.current[workspace] = recordEditorSignature(workspace === "handout"
          ? buildHandoutDraftPayload() : buildPickerEditorPayload());
        pendingRecordEditorBaselines.current[workspace] = false;
      }
    }
  });

  const rememberSavedHandout = (payload, draftId, version = handoutDocumentVersion.current, sourceRecord = activeHandoutRecordSource) => {
    if (liveHandoutRestoreScope.current !== handoutRestoreScope || version !== handoutDocumentVersion.current) return false;
    setSavedHandoutRestorePoint({ scope: handoutRestoreScope, payload: cloneHandoutPayload(payload), draftId, sourceRecord });
    recordEditorBaselines.current.handout = recordEditorSignature(payload);
    return true;
  };

  const applyQuestionsFromHandoutDraft = (sections) => {
    const questionMap = {};
    (sections || []).forEach((section) => {
      (section.questions || []).forEach((question) => {
        if (question?.id) questionMap[question.id] = question;
      });
    });
    if (!Object.keys(questionMap).length) return;
    setHandoutQuestionsById((current) => ({ ...current, ...questionMap }));
    setQuestionsById((current) => ({ ...current, ...questionMap }));
  };

  // ⌘S / Ctrl+S does what the 儲存講義紀錄 button does: update the open record,
  // or create one for a new handout. Handouts have no separate drafts.
  handoutSaveShortcutRef.current = {
    save: () => saveHandoutRecord("handout"),
    disabled: generating || previewPreparing || recordSaving || !canExportHandout || Boolean(backgroundRemovalTarget || backgroundRemovalPreparing || focusPythonTarget),
  };
  useEffect(() => {
    if (!routeActive || !isActive || activeWorkspace !== "handout") return undefined;
    const handleSaveShortcut = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey ||
        (event.key?.toLowerCase() !== "s" && event.code !== "KeyS")) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!event.repeat && !handoutSaveShortcutRef.current?.disabled) {
        handoutSaveShortcutRef.current.save();
      }
    };
    window.addEventListener("keydown", handleSaveShortcut, true);
    return () => window.removeEventListener("keydown", handleSaveShortcut, true);
  }, [activeWorkspace, isActive, routeActive]);

  const applyHandoutPayload = (payload, { sourceRecord = null } = {}) => {
    handoutHistoryResetRef.current?.();
    setHandoutPreviewSelection(null);
    pendingRecordEditorBaselines.current.handout = true;
    setHandoutRecordSource(sourceRecord);
    backgroundRemovalRequest.current += 1;
    setBackgroundRemovalTarget(null);
    setFocusPythonTarget(null);
    setBackgroundRemovalPreparing("");
    setHandoutFamilyDraws(payload.familyDraws || []);
    const nextTemplateId = payload.templateId || "junior_math_handout";
    const nextDraftLabel = nextTemplateId === "exam_paper" ? "檢測卷" : "講義";
    const nextSections = normalizeSectionsForTemplate(nextTemplateId, payload.sections);
    const nextFallbackTitle = nextTemplateId === "exam_paper"
      ? defaultExamHandoutTitle
      : defaultHandoutTitle;
    const nextFallbackFilename = nextTemplateId === "exam_paper"
      ? defaultExamHandoutFilename
      : defaultHandoutFilename;
    setHandoutTemplateId(nextTemplateId);
    setHandoutTitle(payload.title || nextFallbackTitle);
    setHandoutFilename(payload.filename || nextFallbackFilename);
    setHandoutOutputMode(getEditorOutputMode(payload.outputMode || "teaching", nextTemplateId));
    const payloadOptions = payload.options || {};
    setHandoutOptions({
      ...defaultHandoutOptions,
      ...payloadOptions,
      questionImageAlignments: payloadOptions.questionImageAlignments || payload.handout_options?.question_image_alignments || {},
      iconPrefix: payloadOptions.iconPrefix === legacyHandoutIconPrefix
        ? handoutIconPrefix
        : payloadOptions.iconPrefix || handoutIconPrefix,
      headerFooter: normalizeHandoutHeaderFooter(payloadOptions, defaultHandoutOptions.headerFooter),
      focus: {
        ...defaultHandoutOptions.focus,
        ...(payloadOptions.focus || {}),
      },
      exam: {
        ...defaultHandoutOptions.exam,
        ...(payloadOptions.exam || {}),
        parts: normalizeExamPartOptions(payloadOptions.exam?.parts),
      },
      margins: {
        ...getDefaultHandoutMargins(nextTemplateId),
        ...(payloadOptions.margins || {}),
      },
      icons: {
        ...defaultHandoutOptions.icons,
        ...(payloadOptions.icons || {}),
      },
    });
    setHandoutSections(nextSections);
    const nextActiveSectionId = nextSections.some(
      (section) => section.id === payload.activeSectionId,
    )
      ? payload.activeSectionId
      : getDefaultActiveSectionId(nextTemplateId, nextSections);
    setActiveHandoutSectionId(
      nextActiveSectionId,
    );
    setHandoutFilters({
      ...emptyFilters,
      ...(payload.filters || {}),
    });
    applyQuestionsFromHandoutDraft(nextSections);
    return nextDraftLabel;
  };

  const handoutHistoryValue = useMemo(() => ({
    familyDraws: handoutFamilyDraws, templateId: handoutTemplateId, title: handoutTitle,
    filename: handoutFilename, outputMode: handoutOutputMode, options: handoutOptions, sections: handoutSections,
  }), [handoutFamilyDraws, handoutTemplateId, handoutTitle, handoutFilename, handoutOutputMode, handoutOptions, handoutSections]);
  const handoutHistory = useEditorHistory(handoutHistoryValue, {
    scope: `${authToken}:${subject}:${activeHandoutRecordSource?.id || "new"}:${selectedHandoutDraftId}`,
    enabled: isActive && routeActive && activeWorkspace === "handout",
    blocked: generating || recordSaving || Boolean(backgroundRemovalTarget || backgroundRemovalPreparing || focusPythonTarget),
    onRestore: (snapshot) => {
      setHandoutFamilyDraws(snapshot.familyDraws);
      setHandoutTemplateId(snapshot.templateId);
      setHandoutTitle(snapshot.title); setHandoutFilename(snapshot.filename);
      setHandoutOutputMode(snapshot.outputMode); setHandoutOptions(snapshot.options);
      setHandoutSections(snapshot.sections);
      applyQuestionsFromHandoutDraft(snapshot.sections);
      handoutDocumentVersion.current += 1;
      if (!snapshot.sections.some((section) => section.id === activeHandoutSectionId)) {
        setActiveHandoutSectionId(snapshot.sections[0]?.id || "");
      }
    },
  });
  handoutHistoryResetRef.current = handoutHistory.reset;

  const saveHandoutRecord = async (workspace, { asNew = false } = {}) => {
    const isHandout = workspace === "handout";
    if (recordSaveInFlightRef.current || (isHandout && handoutSaveInFlightRef.current)) return;
    const ids = isHandout ? handoutQuestionIdsForOutput : selectedIds;
    if (!ids.length && !(isHandout && hasHandoutContent)) return;
    // Explain the backend limit up front instead of surfacing its rejection.
    if (ids.length > HANDOUT_MAX_QUESTIONS) {
      setNotice({ tone: "error", text: `一份紀錄最多 ${HANDOUT_MAX_QUESTIONS} 題，目前 ${ids.length} 題。請移除部分題目，或分成數份紀錄後再儲存。` });
      return;
    }
    const selectedTemplate = isHandout ? handoutTemplateId : templateId;
    const savedTranslations = selectBilingualTranslations(ids, bilingualPreview?.translations, bilingualTranslations);
    recordSaveInFlightRef.current = true;
    if (isHandout) handoutSaveInFlightRef.current = true;
    setRecordSaving(true);
    try {
      const isExam = selectedTemplate === "exam_paper" || pickerExamTemplateIds.has(selectedTemplate);
      const selectedMode = isHandout ? handoutOutputMode : outputMode;
      const handoutPayload = isHandout ? cloneHandoutPayload(buildHandoutDraftPayload()) : null;
      const pickerSignature = isHandout ? null : recordEditorSignature(buildPickerEditorPayload());
      const version = handoutDocumentVersion.current;
      const pickerVersion = pickerRecordLoadVersion.current;
      const sourceRecord = isHandout && !isExam ? activeHandoutRecordSource : null;
      const data = {
        title: (isHandout ? handoutTitle : documentTitle).trim() || defaultHandoutTitle,
        workspace,
        template_id: isHandout ? handoutTemplateId : templateId,
        filename: ensureDocxFilename(isHandout ? handoutFilename : outputFilename),
        output_mode: selectedMode === "student" ? "student" : "teaching",
        question_ids: ids,
        payload: prepareHandoutRecordPayload({
          ...(isHandout ? handoutPayload : { sectionMode, examRange, familyDraws }),
          outputMode: selectedMode,
          ...(Object.keys(savedTranslations).length ? { bilingualTranslations: savedTranslations } : {}),
        }),
      };
      let savedRecord;
      if (sourceRecord) {
        const layoutData = { ...data };
        delete layoutData.question_ids;
        delete layoutData.workspace;
        const revision = { ...(sourceRecord.updatedAt ? { expected_updated_at: sourceRecord.updatedAt } : {}) };
        if (asNew) {
          const copyData = { ...layoutData, record_payload: layoutData.payload, ...revision };
          delete copyData.payload;
          savedRecord = await copyHandoutRecord({ authToken, id: sourceRecord.id, data: copyData });
        } else savedRecord = await updateHandoutRecord({ authToken, id: sourceRecord.id, data: { ...layoutData, ...revision } });
      } else savedRecord = await (isExam ? createExamRecord : createHandoutRecord)({ authToken, data });
      let savedSource = null;
      if (isHandout) {
        const nextSource = !isExam && savedRecord?.id
          ? { id: savedRecord.id, updatedAt: savedRecord.updated_at, scope: handoutRestoreScope }
          : sourceRecord;
        if (rememberSavedHandout(handoutPayload, selectedHandoutDraftId, version, nextSource)) setHandoutRecordSource(nextSource);
        savedSource = nextSource;
      }
      else if (liveHandoutRestoreScope.current === handoutRestoreScope && pickerVersion === pickerRecordLoadVersion.current) recordEditorBaselines.current.picker = pickerSignature;
      setNotice({ tone: "success", text: `${sourceRecord ? asNew ? "已另存新" : "已更新" : "已儲存"}${isExam ? "考卷" : "講義"}紀錄，共 ${ids.length} 題。` });
      showEditorSaveToast(`${isExam ? "考卷" : "講義"}紀錄已儲存`);
      // Callers that continue after saving (save-then-export) need the new revision.
      return { saved: true, source: savedSource };
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "講義紀錄儲存失敗" });
    } finally {
      recordSaveInFlightRef.current = false;
      if (isHandout) handoutSaveInFlightRef.current = false;
      setRecordSaving(false);
    }
  };

  const loadHandoutRecord = async (record, templateIdOverride) => {
    const target = record.workspace === "handout" ? "handout" : "picker";
    const currentSignature = recordEditorSignature(target === "handout"
      ? buildHandoutDraftPayload() : buildPickerEditorPayload());
    if (currentSignature !== recordEditorBaselines.current[target]) {
      const { isConfirmed } = await confirmLoadRecord(record);
      if (!isConfirmed) return;
    }
    const questions = record.items.map((item) => item.snapshot);
    const questionMap = Object.fromEntries(questions.map((question) => [question.id, question]));
    const payload = record.payload || {};
    const savedBilingualCache = restoreRecordBilingualCache(record);
    setBilingualTranslations((current) => ({ ...current, ...savedBilingualCache.translations }));
    setBilingualPendingQuestions({});
    setBilingualError("");
    if (record.workspace === "handout") {
      const nextTemplateId = record.template_id || "junior_math_handout";
      const orderedItems = [...record.items].sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
      const usedItems = new Set();
      const sections = normalizeSectionsForTemplate(nextTemplateId, payload.sections).map((section) => ({
        ...section,
        questions: (section.questions || section.question_ids || []).map((reference) => {
          const id = String(typeof reference === "string" ? reference : reference?.id || reference?.question_uuid || "");
          const matches = (item) => String(item.question_uuid || item.snapshot?.id || "") === id;
          let index = orderedItems.findIndex((item, itemIndex) => !usedItems.has(itemIndex) && matches(item));
          // Older records can reference the same saved item more than once.
          if (index < 0) index = orderedItems.findIndex(matches);
          if (index < 0) return null;
          usedItems.add(index);
          const snapshot = orderedItems[index].snapshot;
          return { ...snapshot, question_uuid: orderedItems[index].question_uuid || snapshot.question_uuid || snapshot.id,
            pageBreakBefore: reference?.pageBreakBefore ?? snapshot.pageBreakBefore,
            pagination: reference?.pagination ?? snapshot.pagination,
          };
        }).filter(Boolean),
      }));
      const remaining = orderedItems.filter((_, index) => !usedItems.has(index)).map((item) => ({ ...item.snapshot, question_uuid: item.question_uuid || item.snapshot?.question_uuid || item.snapshot?.id }));
      if (remaining.length && !payload.snapshot_selection) {
        let target = sections.find((section) => section.type === "questions");
        if (!target) {
          target = { id: "record-questions", type: "questions", title: "題型", questions: [] };
          sections.push(target);
        }
        target.questions = [...(target.questions || []), ...remaining];
      }
      const loadedPayload = { ...payload, sections, templateId: nextTemplateId, title: record.title, filename: record.filename,
        outputMode: getEditorOutputMode(payload.outputMode || record.output_mode, nextTemplateId) };
      const sourceRecord = { id: record.id, updatedAt: record.updated_at, scope: handoutRestoreScope };
      applyHandoutPayload(loadedPayload, { sourceRecord });
      handoutDocumentVersion.current += 1;
      rememberSavedHandout(loadedPayload, "", handoutDocumentVersion.current, sourceRecord);
      setActiveWorkspace("handout");
    } else {
      pickerRecordLoadVersion.current += 1;
      pendingRecordEditorBaselines.current.picker = true;
      setSelectedIds([...new Set(questions.map((question) => question.id))]);
      setSelectedQuestionsById(questionMap);
      setQuestionsById((current) => ({ ...current, ...questionMap }));
      setDocumentTitle(record.title);
      setOutputFilename(record.filename);
      setTemplateId(templateIdOverride || record.template_id || "junior_exam_paper");
      setOutputMode(getEditorOutputMode(payload.outputMode || record.output_mode,
        templateIdOverride || record.template_id || "junior_exam_paper"));
      setSectionMode(payload.sectionMode || "selected");
      setExamRange(payload.examRange || "");
      setFamilyDraws(payload.familyDraws || []);
      setBilingualPreview(!templateIdOverride && savedBilingualCache.document ? {
        signature: getBilingualPreviewSignature(questions, payload.sectionMode || "selected"),
        translations: savedBilingualCache.translations, document: savedBilingualCache.document,
      } : null);
      setActiveWorkspace("picker");
      setPickerStep("layout");
    }
    const deletedIds = new Set((record.items || []).filter((item) => item.is_deleted).map((item) => String(item.question_uuid)));
    setHandoutDeletedQuestionIds(deletedIds);
    const loadedLabel = `已載入${record.workspace === "exam" ? "考卷" : "講義"}紀錄：${record.title}`;
    const warnings = [
      record.sync_failed && "題庫同步失敗，目前是上次儲存的內容",
      deletedIds.size && `其中 ${deletedIds.size} 題已從題庫刪除，保留當時內容、不再同步`,
    ].filter(Boolean);
    setNotice(warnings.length
      ? { tone: "error", text: `${loadedLabel}。${warnings.join("；")}。` }
      : { tone: "success", text: loadedLabel });
  };

  // Re-open the record from the server: it syncs every question with the bank
  // and reports any that were deleted while this handout was being edited.
  // Unsaved changes are confirmed by loadHandoutRecord itself.
  const syncOpenHandoutRecord = async () => {
    const source = activeHandoutRecordSource;
    if (!source || recordSaving || generating) return;
    try {
      const record = await getHandoutRecord({ authToken, id: source.id });
      if (record?.items?.some((item) => item.is_unlinked)) {
        setNotice({ tone: "error", text: "部分題目缺少題目 ID，無法重新載入這份紀錄。" });
        return;
      }
      await loadHandoutRecord(record);
    } catch (error) {
      if (error?.name === "AbortError") return;
      setNotice({ tone: "error", text: error.message || "同步題庫失敗，請再試一次。" });
    }
  };

  const handleQuestionCopyResult = (success) => setNotice({
    tone: success ? "success" : "error", text: success ? "題目 UUID 已複製" : "UUID 複製失敗",
  });

  const toggleQuestion = (id) => {
    const question =
      selectedQuestionsById[id] ||
      questionsById[id] ||
      questionStateRef.current.byId[id];

    setSelectedIds((prev) => {
      const isSelected = prev.includes(id);

      setSelectedQuestionsById((current) => {
        if (isSelected) {
          const next = { ...current };
          delete next[id];
          return next;
        }

        if (!question) return current;

        return {
          ...current,
          [id]: question,
        };
      });

      return isSelected
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id];
    });
  };

  const selectVisibleQuestions = () => {
    if (!filteredQuestions.length) return;

    setSelectedQuestionsById((current) => {
      const next = { ...current };
      filteredQuestions.forEach((question) => {
        next[question.id] = question;
      });
      return next;
    });

    setSelectedIds((prev) => {
      const next = [...prev];
      const nextSet = new Set(prev);
      filteredQuestions.forEach((question) => {
        if (!nextSet.has(question.id)) {
          next.push(question.id);
          nextSet.add(question.id);
        }
      });
      return next;
    });
  };

  const deselectVisibleQuestions = () => {
    if (!filteredQuestions.length) return;

    const visibleIds = new Set(filteredQuestions.map((question) => question.id));
    setSelectedIds((prev) => prev.filter((id) => !visibleIds.has(id)));
    setSelectedQuestionsById((current) => {
      const next = { ...current };
      visibleIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  };

  const clearSelectedQuestions = () => {
    setFamilyDraws([]);
    setSelectedIds([]);
    setSelectedQuestionsById({});
  };

  const insertSelectedQuestions = (questionsToInsert) => {
    const additions = questionsToInsert.filter((question) => !selectedIdSet.has(question.id));
    setSelectedQuestionsById((current) => ({
      ...current,
      ...Object.fromEntries(additions.map((question) => [question.id, question])),
    }));
    setSelectedIds((current) => [
      ...current,
      ...additions.map((question) => question.id).filter((id) => !current.includes(id)),
    ]);
    if (additions.length) flashInsertedQuestions(additions.map((question) => question.id));
    return additions.length;
  };

  const hydratePythonAssetPreviews = useCallback(async (questionsToHydrate, { signal, onProgress } = {}) => {
    checkWordExportSignal(signal);
    const total = questionsToHydrate.length;
    let current = 0;
    const report = () => {
      checkWordExportSignal(signal);
      onProgress?.({ stage: "preparing", current, total, message: "正在準備題目圖文與公式…" });
      checkWordExportSignal(signal);
    };
    report();
    const updatesByQuestionId = new Map();
    let generatedCount = 0;
    let failedCount = 0;

    for (const question of questionsToHydrate) {
      checkWordExportSignal(signal);
      const assets = question.assets || [];
      let changed = false;
      const nextAssets = [];

      for (const asset of assets) {
        checkWordExportSignal(signal);
        if (!isPythonSourceAsset(asset) || assetHasImageUrl(asset)) {
          nextAssets.push(asset);
          continue;
        }

        const sourceCode = String(asset.source_code || "").trim();
        const cachedImage = getCachedMatplotlibPreview(sourceCode);
        if (cachedImage) {
          nextAssets.push({
            ...asset,
            url: cachedImage,
            mime_type: asset.mime_type || "image/png",
          });
          changed = true;
          continue;
        }

        const result = await awaitWordPreview(
          () => getOrRenderMatplotlibPreview(sourceCode, previewMatplotlibCode, { signal }), signal,
        );
        if (result.success && result.image) {
          nextAssets.push({
            ...asset,
            url: result.image,
            mime_type: asset.mime_type || "image/png",
          });
          generatedCount += 1;
          changed = true;
        } else {
          failedCount += 1;
          nextAssets.push(asset);
        }
      }

      if (changed) {
        updatesByQuestionId.set(question.id, {
          ...question,
          assets: nextAssets,
        });
      }
      current += 1;
      report();
    }

    checkWordExportSignal(signal);
    if (!updatesByQuestionId.size) {
      return { generatedCount, failedCount };
    }

    const applyQuestionUpdates = (current) => {
      if (signal?.aborted) return current;
      let changed = false;
      const next = { ...current };
      updatesByQuestionId.forEach((question, id) => {
        if (next[id]) {
          next[id] = { ...next[id], ...question };
          changed = true;
        }
      });
      return changed ? next : current;
    };

    setSelectedQuestionsById((current) => {
      if (signal?.aborted) return current;
      const next = { ...current };
      updatesByQuestionId.forEach((question, id) => {
        next[id] = { ...(next[id] || {}), ...question };
      });
      return next;
    });
    setQuestionsById(applyQuestionUpdates);
    setHandoutQuestionsById(applyQuestionUpdates);
    setHandoutSections((currentSections) =>
      signal?.aborted ? currentSections : currentSections.map((section) => ({
        ...section,
        questions: (section.questions || []).map((question) =>
          updatesByQuestionId.has(question.id)
            ? { ...question, ...updatesByQuestionId.get(question.id) }
            : question,
        ),
      })),
    );
    questionStateRef.current = {
      ...questionStateRef.current,
      byId: applyQuestionUpdates(questionStateRef.current.byId),
    };
    handoutQuestionStateRef.current = {
      ...handoutQuestionStateRef.current,
      byId: applyQuestionUpdates(handoutQuestionStateRef.current.byId),
    };
    questionCacheRef.current.forEach((cachedState, signature) => {
      const nextById = applyQuestionUpdates(cachedState.byId);
      if (nextById !== cachedState.byId) {
        questionCacheRef.current.set(signature, {
          ...cachedState,
          byId: nextById,
        });
      }
    });
    handoutQuestionCacheRef.current.forEach((cachedState, signature) => {
      const nextById = applyQuestionUpdates(cachedState.byId);
      if (nextById !== cachedState.byId) {
        handoutQuestionCacheRef.current.set(signature, {
          ...cachedState,
          byId: nextById,
        });
      }
    });

    return { generatedCount, failedCount };
  }, [previewMatplotlibCode]);

  const handlePreviewAssetsRendered = useCallback((sourceQuestion, nextAssets) => {
    const questionId = sourceQuestion?.id;
    if (!questionId) return;

    const applyAssetsToMap = (current) => {
      if (!current[questionId]) return current;
      return {
        ...current,
        [questionId]: {
          ...current[questionId],
          assets: nextAssets,
        },
      };
    };
    const applyAssetsToSections = (sections) =>
      sections.map((section) => ({
        ...section,
        questions: (section.questions || []).map((question) =>
          question.id === questionId
            ? { ...question, assets: nextAssets }
            : question,
        ),
      }));

    setQuestionsById(applyAssetsToMap);
    setSelectedQuestionsById(applyAssetsToMap);
    setHandoutQuestionsById(applyAssetsToMap);
    setHandoutSections(applyAssetsToSections);
    questionStateRef.current = {
      ...questionStateRef.current,
      byId: applyAssetsToMap(questionStateRef.current.byId),
    };
    handoutQuestionStateRef.current = {
      ...handoutQuestionStateRef.current,
      byId: applyAssetsToMap(handoutQuestionStateRef.current.byId),
    };
    questionCacheRef.current.forEach((cachedState, signature) => {
      questionCacheRef.current.set(signature, {
        ...cachedState,
        byId: applyAssetsToMap(cachedState.byId),
      });
    });
    handoutQuestionCacheRef.current.forEach((cachedState, signature) => {
      handoutQuestionCacheRef.current.set(signature, {
        ...cachedState,
        byId: applyAssetsToMap(cachedState.byId),
      });
    });
  }, []);

  const prepareSelectedPreviewImages = useCallback(async ({ openDialog = false } = {}) => {
    if (!selectedIds.length) {
      setNotice({ tone: "error", text: "請先選擇題目" });
      return false;
    }

    setPreviewPreparing(true);
    setNotice(null);
    try {
      const result = await hydratePythonAssetPreviews(selectedQuestions);
      if (result.failedCount) {
        setNotice({
          tone: "error",
          text: `有 ${result.failedCount} 張 Python 圖產生失敗，已保留可預覽的題目。`,
        });
      } else if (result.generatedCount) {
        setNotice({ tone: "success", text: `已產生 ${result.generatedCount} 張講義預覽原圖` });
      } else {
        setNotice(null);
      }
      if (openDialog) setShowPreviewDialog(true);
      return true;
    } catch (error) {
      setNotice({ tone: "error", text: error.message || "講義預覽原圖產生失敗" });
      return false;
    } finally {
      setPreviewPreparing(false);
    }
  }, [hydratePythonAssetPreviews, selectedIds.length, selectedQuestions]);

  const prepareHandoutPreviewImages = useCallback(async () => {
    if (!handoutTotalQuestionCount) {
      setNotice({ tone: "error", text: `請先把題目拖到${handoutOutlineTitle}` });
      return false;
    }

    setPreviewPreparing(true);
    setNotice(null);
    try {
      const result = await hydratePythonAssetPreviews(handoutQuestionsForOutput);
      if (result.failedCount) {
        setNotice({
          tone: "error",
          text: `有 ${result.failedCount} 張 Python 圖產生失敗，已保留可預覽的題目。`,
        });
      } else if (result.generatedCount) {
        setNotice({
          tone: "success",
          text: `已整理 ${result.generatedCount} 張${handoutWorkspaceLabel}原圖`,
        });
      } else {
        setNotice(null);
      }
      return true;
    } catch (error) {
      setNotice({ tone: "error", text: error.message || `${handoutWorkspaceLabel}原圖整理失敗` });
      return false;
    } finally {
      setPreviewPreparing(false);
    }
  }, [
    handoutOutlineTitle,
    handoutQuestionsForOutput,
    handoutTotalQuestionCount,
    handoutWorkspaceLabel,
    hydratePythonAssetPreviews,
  ]);

  const openPreviewDialog = () => {
    prepareSelectedPreviewImages({ openDialog: true });
  };

  const handleBilingualPreview = async () => {
    if (!selectedIds.length || bilingualAbortRef.current || !isActive || !routeActive) return;
    if (hasBilingualPreview) {
      setPickerStep("layout");
      return;
    }
    const signature = bilingualSignature;
    const requestId = ++bilingualRequestRef.current;
    bilingualActiveSignatureRef.current = signature;
    const controller = new AbortController();
    bilingualAbortRef.current = controller;
    setBilingualLoading(true);
    setNotice({ tone: "info", text: "正在翻譯題目與選項為英文並建立雙語預覽..." });
    const preserveTranslationResult = (result) => {
      if (!result) return;
      const translations = result.bilingual_translations || {};
      setBilingualTranslations((current) => ({ ...current, ...translations }));
      setBilingualPendingQuestions((current) => {
        const next = { ...current };
        Object.keys(translations).forEach((id) => { delete next[id]; });
        (result.skipped_questions || []).forEach((failure) => {
          const question = selectedQuestions.find((item) => String(item.id) === failure.id);
          if (question) next[failure.id] = { ...failure, question };
        });
        return next;
      });
    };
    try {
      const result = await previewBilingualExam({
        questionIds: selectedIds, title: documentTitle, sectionMode, templateId,
        bilingualTranslations: Object.fromEntries(selectedIds
          .filter((id) => bilingualTranslations[id])
          .map((id) => [id, bilingualTranslations[id]])),
        signal: controller.signal,
        shouldContinue: () => requestId === bilingualRequestRef.current && signature === bilingualSignatureRef.current && bilingualEnabledRef.current,
        onProgress: (job) => {
          if (requestId !== bilingualRequestRef.current || signature !== bilingualSignatureRef.current || !bilingualEnabledRef.current) return;
          preserveTranslationResult(job.result);
          if (job.retrying) setBilingualError(job.retryMessage || "翻譯進度查詢失敗，正在重新連線。");
          setNotice({ tone: "info", text: job.retrying ? job.retryMessage || "正在重新查詢同一個英文翻譯工作..."
            : job.status === "queued" ? "英文翻譯排隊中..."
              : `正在建立雙語預覽${job.total ? `（${job.current || 0}/${job.total} 題）` : ""}...` });
        },
      });
      if (requestId !== bilingualRequestRef.current || signature !== bilingualSignatureRef.current || !bilingualEnabledRef.current) return;
      if (!result.success || !result.document) throw new Error(result.error || "雙語考卷翻譯失敗");
      preserveTranslationResult(result);
      const skipped = result.skipped_questions || [];
      if (!(result.document.sections || []).some((section) => section.questions?.length)) {
        throw new Error(`這次沒有題目翻譯成功，已保留先前翻譯。${skipped[0]?.error || ""}`);
      }
      setBilingualPreview(skipped.length ? null : { signature, document: result.document, translations: result.bilingual_translations || {}, jobId: result.jobId });
      setBilingualError(skipped.length ? `有 ${skipped.length} 題尚未翻譯成功；已保留所有選題與成功翻譯，請重試待處理題目。` : "");
      setPickerStep("layout");
      setNotice({ tone: skipped.length ? "error" : "success", text: skipped.length
        ? `有 ${skipped.length} 題翻譯失敗（原第 ${skipped.map((question) => question.number).join("、")} 題）。所有選題仍保留，成功題不會重複翻譯。${skipped[0].error}`
        : "英文翻譯已完成，確認右側雙語預覽後可下載 Word。" });
    } catch (error) {
      if (requestId === bilingualRequestRef.current && signature === bilingualSignatureRef.current && bilingualEnabledRef.current) {
        setBilingualPreview(null);
        setBilingualError(error.message || "雙語考卷翻譯失敗");
        setNotice({ tone: "error", text: error.message || "雙語考卷翻譯失敗" });
      }
    } finally {
      if (bilingualAbortRef.current === controller) bilingualAbortRef.current = null;
      if (requestId === bilingualRequestRef.current) setBilingualLoading(false);
    }
  };

  const moveSelectedQuestion = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;

    setSelectedIds((prev) => {
      const sourceIndex = prev.indexOf(sourceId);
      const targetIndex = prev.indexOf(targetId);
      if (sourceIndex < 0 || targetIndex < 0) return prev;

      const next = [...prev];
      const [movedId] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, movedId);
      return next;
    });
  };

  const handleSelectedDragStart = (event, id) => {
    setDraggedSelectedId(id);
    setDragOverSelectedId("");
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", id);
  };

  const handleSelectedDragOver = (event, id) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (id !== dragOverSelectedId) {
      setDragOverSelectedId(id);
    }
  };

  const handleSelectedDrop = (event, id) => {
    event.preventDefault();
    const sourceId = draggedSelectedId || event.dataTransfer.getData("text/plain");
    moveSelectedQuestion(sourceId, id);
    setDraggedSelectedId("");
    setDragOverSelectedId("");
  };

  const handleSelectedDragEnd = () => {
    setDraggedSelectedId("");
    setDragOverSelectedId("");
  };

  const downloadTemplateFile = async (file) => {
    const downloadUrl = getTemplateDownloadUrl(file);
    if (!downloadUrl) return;
    try {
      const response = await fetch(downloadUrl, {
        headers: file.download_url && authToken
          ? { Authorization: `Bearer ${authToken}` }
          : undefined,
      });
      if (!response.ok) throw new Error("template-download-failed");
      const blob = await response.blob();
      downloadBlob(blob, file.filename || `${file.label}.docx`);
      setNotice({ tone: "success", text: `${file.label}已開始下載` });
    } catch {
      const fallbackUrl = getTemplatePreviewSourceUrl(file) || downloadUrl;
      const link = document.createElement("a");
      link.href = fallbackUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = file.filename || `${file.label}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setNotice({ tone: "info", text: `已開啟${file.label}連結` });
    }
  };

  const getHandoutQuestionById = (id) =>
    handoutQuestionsById[id] ||
    handoutQuestionStateRef.current.byId[id] ||
    questionsById[id] ||
    questionStateRef.current.byId[id] ||
    selectedQuestionsById[id];

  const addQuestionToHandoutSection = (sectionId, question, insertIndex = null) => {
    if (!question?.id) return;

    const targetSection = handoutSections.find((section) => section.id === sectionId);
    if (targetSection?.type !== "questions") {
      setNotice({ tone: "error", text: `請拖到${handoutQuestionTargetLabel}` });
      return;
    }

    if (handoutQuestionIdSet.has(question.id)) {
      setNotice({ tone: "info", text: `這題已經在${handoutOutlineTitle}裡` });
      return;
    }

    setHandoutSections((currentSections) =>
      currentSections.map((section) => {
        if (section.id !== sectionId) return section;
        const nextQuestions = [...(section.questions || [])];
        const targetIndex =
          Number.isInteger(insertIndex)
            ? Math.max(0, Math.min(insertIndex, nextQuestions.length))
            : nextQuestions.length;
        nextQuestions.splice(targetIndex, 0, question);
        return { ...section, questions: nextQuestions };
      }),
    );
    setActiveHandoutSectionId(sectionId);
  };

  const insertHandoutQuestions = (questionsToInsert, targetId) => {
    const targetSection = handoutSections.find((section) => `${handoutTemplateId}:${section.id}` === targetId);
    if (targetSection?.type !== "questions") {
      throw new Error("原本選取的題目區塊已變更，請重新選取後再插入。");
    }
    const additions = questionsToInsert.filter((question) => !handoutQuestionIdSet.has(question.id));
    setHandoutSections((currentSections) => {
      const existing = new Set(currentSections.flatMap((section) =>
        (section.questions || []).map((question) => question.id),
      ));
      return currentSections.map((section) => section.id === targetSection.id
        ? { ...section, questions: [...section.questions, ...additions.filter((question) => !existing.has(question.id))] }
        : section);
    });
    if (additions.length) {
      handoutRevealQuestionIdRef.current = additions[0].id;
      flashInsertedQuestions(additions.map((question) => question.id));
    }
    return additions.length;
  };

  const moveHandoutQuestionToSection = (
    sourceSectionId,
    questionId,
    targetSectionId,
    insertIndex = null,
  ) => {
    if (!sourceSectionId || !questionId || !targetSectionId) return;

    setHandoutSections((currentSections) => {
      if (!currentSections.some((section) => section.id === targetSectionId && section.type === "questions")) return currentSections;
      let movingQuestion = null;
      let sourceIndex = -1;

      const sectionsWithoutQuestion = currentSections.map((section) => {
        if (section.id !== sourceSectionId || section.type !== "questions") return section;
        const nextQuestions = [...(section.questions || [])];
        sourceIndex = nextQuestions.findIndex((question) => question.id === questionId);
        if (sourceIndex < 0) return section;
        [movingQuestion] = nextQuestions.splice(sourceIndex, 1);
        return { ...section, questions: nextQuestions };
      });

      if (!movingQuestion) return currentSections;

      return sectionsWithoutQuestion.map((section) => {
        if (section.id !== targetSectionId || section.type !== "questions") return section;
        const nextQuestions = [...(section.questions || [])];
        let targetIndex = Number.isInteger(insertIndex) ? insertIndex : nextQuestions.length;
        if (Number.isInteger(insertIndex) && sourceSectionId === targetSectionId && sourceIndex < targetIndex) {
          targetIndex -= 1;
        }
        targetIndex = Math.max(0, Math.min(targetIndex, nextQuestions.length));
        nextQuestions.splice(targetIndex, 0, movingQuestion);
        return { ...section, questions: nextQuestions };
      });
    });
    setActiveHandoutSectionId(targetSectionId);
  };

  // Remove only the clicked occurrence; a repeated question keeps its other copies.
  const removeQuestionFromHandoutSection = (sectionId, questionId, index) => {
    setHandoutSections((currentSections) =>
      currentSections.map((section) => {
        if (section.id !== sectionId) return section;
        const questions = section.questions || [];
        const target = Number.isInteger(index) && questions[index]?.id === questionId
          ? index : questions.findIndex((question) => question.id === questionId);
        return target < 0 ? section : { ...section, questions: questions.filter((_, position) => position !== target) };
      }),
    );
  };

  const updateHandoutSectionTitle = (sectionId, title) => {
    setHandoutSections((currentSections) =>
      currentSections.map((section) =>
        section.id === sectionId ? { ...section, title } : section,
      ),
    );
  };

  const updateHandoutSectionDecoration = (sectionId, key, patch) => setHandoutSections((current) => current.map((section) =>
    section.id === sectionId ? { ...section, [key]: { ...section[key], ...patch } } : section));

  const updateExamPartTitle = (partId, title) => {
    updateHandoutSectionTitle(partId, title);
    updateExamPartOption(partId, "title", title);
  };

  const addHandoutQuestionSection = () => {
    const questionSectionCount = handoutSections.filter(
      (section) => section.type === "questions",
    ).length;
    const nextSection = {
      id: `type-${Date.now()}`,
      type: "questions",
      title: `題型 ${questionSectionCount + 1}`,
      questions: [],
    };
    setHandoutSections((currentSections) => [...currentSections, nextSection]);
    setActiveHandoutSectionId(nextSection.id);
  };

  const addHandoutFocusSection = () => {
    let focusNumber = handoutSections.filter((section) => section.type === "focus").length + 1;
    while (handoutSections.some((section) => section.title === `重點整理 ${focusNumber}`)) {
      focusNumber += 1;
    }
    const nextSection = {
      id: `focus-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: "focus",
      title: `重點整理 ${focusNumber}`,
      lines: "",
      questions: [],
    };
    setHandoutSections((currentSections) => {
      const firstQuestionIndex = currentSections.findIndex((section) => section.type === "questions");
      const insertIndex = firstQuestionIndex < 0 ? currentSections.length : firstQuestionIndex;
      return [
        ...currentSections.slice(0, insertIndex),
        nextSection,
        ...currentSections.slice(insertIndex),
      ];
    });
    setActiveHandoutSectionId(nextSection.id);
  };

  const addHandoutBreak = (type) => {
    const nextSection = {
      id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      title: type === "page_break" ? "分頁符號" : "分節符號",
      ...(type === "page_break" ? { forcePageBreak: true } : {}),
      questions: [],
    };
    setHandoutSections((currentSections) => {
      const selectedIndex = currentSections.findIndex((section) => section.id === activeHandoutSectionId);
      const index = selectedIndex < 0 ? currentSections.length : selectedIndex + 1;
      return [...currentSections.slice(0, index), nextSection, ...currentSections.slice(index)];
    });
    selectHandoutOutlineSection(nextSection.id);
  };

  const addHandoutImage = (asset, insertIndex = handoutSections.length) => {
    const nextSection = {
      id: `image-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: "image",
      title: asset.name || "講義圖片",
      asset: { key: asset.key, name: asset.name, url: asset.url },
      widthCm: 8,
      alignment: "center",
      text: "",
      textLayout: "below",
      questions: [],
    };
    setHandoutSections((current) => [
      ...current.slice(0, insertIndex), nextSection, ...current.slice(insertIndex),
    ]);
    setActiveHandoutSectionId(nextSection.id);
  };

  const addHandoutFocusImage = (sectionId, assetOrImage, { prebuilt = false } = {}) => {
    const image = prebuilt ? assetOrImage : createHandoutFocusImage(assetOrImage);
    setHandoutSections((current) => current.map((section) => section.id === sectionId && section.type === "focus"
      ? { ...section, images: [...getHandoutFocusImages(section), image] } : section));
  };

  const updateHandoutFocusImage = (sectionId, imageId, key, value) => {
    setHandoutSections((current) => current.map((section) => section.id === sectionId
      ? { ...section, images: getHandoutFocusImages(section).map((image) => image.id === imageId
        ? { ...image, [key]: value,
          ...(key === "anchorSection" ? { anchor_section: undefined } : {}),
          ...(key === "alignment" && value === "center" ? { layout: "block" } : {}),
          ...(key === "layout" && value === "float" && image.alignment === "center" ? { alignment: "right" } : {}),
        } : image) } : section));
  };

  const removeHandoutFocusImage = (sectionId, imageId) => {
    setHandoutSections((current) => current.map((section) => section.id === sectionId
      ? { ...section, images: getHandoutFocusImages(section).filter((image) => image.id !== imageId) } : section));
  };

  const moveHandoutImageIntoFocus = (imageId, focusId) => {
    setHandoutSections((current) => moveImageSectionIntoFocus(current, imageId, focusId));
    setActiveHandoutSectionId(focusId);
  };

  const replaceHandoutImage = (sectionId, imageId, updatedImage) => {
    const mergeImage = (current) => {
      const next = { ...current, ...updatedImage, id: current.id,
        widthCm: current.widthCm, alignment: current.alignment,
        ...(current.layout !== undefined ? { layout: current.layout } : {}),
      };
      if (!updatedImage.backgroundRemoval) delete next.backgroundRemoval;
      return next;
    };
    setHandoutSections((current) => current.map((section) => {
      if (section.id !== sectionId) return section;
      if (imageId) return { ...section, images: getHandoutFocusImages(section).map((image) =>
        image.id === imageId ? mergeImage(image) : image) };
      return section.type === "image" ? { ...mergeImage(section), type: "image" } : section;
    }));
  };

  const openImageBackgroundRemoval = async (sectionId, imageId, image) => {
    if (backgroundRemovalPreparing || generating) return;
    const request = ++backgroundRemovalRequest.current;
    const key = `${sectionId}:${imageId || "image"}`;
    setBackgroundRemovalPreparing(key);
    try {
      const source = await prepareHandoutBackgroundSource(image, { authToken, previewMatplotlibCode });
      if (request !== backgroundRemovalRequest.current) return;
      if (!source) throw new Error("圖片尚未載入，請重新預覽後再試。");
      setBackgroundRemovalTarget({ sectionId, imageId, image, source });
    } catch (error) {
      if (request === backgroundRemovalRequest.current) setNotice({ tone: "error", text: error.message || "圖片準備失敗" });
    } finally {
      if (request === backgroundRemovalRequest.current) setBackgroundRemovalPreparing("");
    }
  };

  const updateHandoutImageOption = (sectionId, key, value) => {
    setHandoutSections((current) => current.map((section) => section.id === sectionId
      ? { ...section, [key]: value }
      : section));
  };

  const updateHandoutImageWidth = (sectionId, widthCm) => {
    setHandoutSections((current) => current.map((section) => section.id === sectionId
      ? { ...section, widthCm: Math.min(16, Math.max(2, Number(widthCm) || 8)) }
      : section));
  };

  const updateQuestionImageAlignment = (assetId, alignment) => {
    setHandoutOptions((current) => {
      const alignments = { ...current.questionImageAlignments };
      if (alignment) alignments[assetId] = alignment;
      else delete alignments[assetId];
      return { ...current, questionImageAlignments: alignments };
    });
  };

  const updateQuestionImageWidth = (assetId, widthCm) => {
    setHandoutOptions((current) => ({
      ...current,
      questionImageWidths: {
        ...current.questionImageWidths,
        [assetId]: Math.min(16, Math.max(2, Number(widthCm) || 8)),
      },
    }));
  };

  const toggleQuestionPageBreak = (sectionId, questionId) => {
    setHandoutSections((current) => current.map((section) => section.id === sectionId
      ? {
        ...section,
        questions: section.questions.map((question) => question.id === questionId
          ? { ...question, pageBreakBefore: !question.pageBreakBefore }
          : question),
      }
      : section));
  };

  const updateHandoutQuestionPagination = (sectionId, questionId, key, value) => {
    setHandoutSections((current) => current.map((section) => section.id === sectionId
      ? { ...section, questions: section.questions.map((question) => question.id === questionId
        ? key === "pageBreakBefore" ? { ...question, pageBreakBefore: value }
          : { ...question, pagination: { ...question.pagination, [key]: value } }
        : question) }
      : section));
  };

  const deleteHandoutFocusSection = (sectionId) => {
    const nextSections = handoutSections.filter((section) => section.id !== sectionId);
    setHandoutSections(nextSections);
    if (activeHandoutSectionId === sectionId) {
      setActiveHandoutSectionId(
        nextSections.find((section) => section.type === "focus")?.id ||
          nextSections.find((section) => section.type === "questions")?.id ||
          nextSections[0]?.id || "",
      );
    }
  };

  const updateHandoutFocusLines = (sectionId, lines) => {
    setHandoutSections((currentSections) =>
      currentSections.map((section) =>
        section.id === sectionId ? { ...section, lines, markdown: lines } : section,
      ),
    );
    if (sectionId === "focus") updateHandoutNestedOption("focus", "lines", lines);
  };

  const deleteHandoutQuestionSection = (sectionId) => {
    const targetSection = handoutSections.find((section) => section.id === sectionId);
    if (targetSection?.type !== "questions") return;

    const nextSections = handoutSections.filter((section) => section.id !== sectionId);
    setHandoutSections(nextSections);
    if (activeHandoutSectionId === sectionId) {
      const nextActive =
        nextSections.find((section) => section.type === "questions") || nextSections[0];
      setActiveHandoutSectionId(nextActive?.id || "");
    }
  };

  const moveHandoutOutlineSection = (sourceId, targetId, afterTarget = false) => {
    setHandoutSections((currentSections) => {
      const source = currentSections.find((section) => section.id === sourceId);
      const target = currentSections.find((section) => section.id === targetId);
      if (!source || !target || sourceId === targetId || target.type === "toc") {
        return currentSections;
      }
      const reordered = currentSections.filter((section) => section.id !== sourceId);
      const targetIndex = reordered.findIndex((section) => section.id === targetId);
      reordered.splice(targetIndex + (afterTarget ? 1 : 0), 0, source);
      return reordered;
    });
  };

  const handleHandoutOutlineDragStart = (event, sectionId) => {
    if (event.target.closest("[data-outline-delete]")) {
      event.preventDefault();
      return;
    }
    setDraggedHandoutOutlineSectionId(sectionId);
    setHandoutOutlineInsertTarget(null);
    setDragOverHandoutSectionId("");
    setHandoutInsertTarget(null);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-handout-outline-section", sectionId);
  };

  const handleHandoutOutlineDragOver = (event, sectionId) => {
    if (draggedHandoutOutlineSectionId) {
      const source = handoutSections.find((section) => section.id === draggedHandoutOutlineSectionId);
      const target = handoutSections.find((section) => section.id === sectionId);
      if (!source || !target || target.type === "toc" || source.id === target.id) {
        setHandoutOutlineInsertTarget(null);
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      const rect = event.currentTarget.getBoundingClientRect();
      const afterTarget = event.clientY >= rect.top + rect.height / 2;
      setHandoutOutlineInsertTarget((current) =>
        current?.sectionId === sectionId && current.afterTarget === afterTarget
          ? current : { sectionId, afterTarget },
      );
      return;
    }
    handleHandoutDragOver(event, sectionId);
  };

  const handleHandoutOutlineDrop = (event, sectionId) => {
    if (draggedHandoutOutlineSectionId) {
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      moveHandoutOutlineSection(
        draggedHandoutOutlineSectionId,
        sectionId,
        event.clientY >= rect.top + rect.height / 2,
      );
      setDraggedHandoutOutlineSectionId("");
      setHandoutOutlineInsertTarget(null);
      setDragOverHandoutSectionId("");
      return;
    }
    handleHandoutDrop(event, sectionId);
  };

  const handleHandoutQuestionDragStart = (event, question) => {
    setDraggedHandoutQuestion({ type: "library", question });
    setDragOverHandoutSectionId("");
    setHandoutInsertTarget(null);
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("application/x-math-bank-question-id", question.id);
    event.dataTransfer.setData("text/plain", question.id);
  };

  const handleHandoutSectionQuestionDragStart = (
    event,
    sectionId,
    questionId,
    index,
  ) => {
    setDraggedHandoutQuestion({
      type: "section",
      sectionId,
      questionId,
      index,
    });
    setDragOverHandoutSectionId("");
    setHandoutInsertTarget(null);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "application/x-handout-section-question",
      JSON.stringify({ sectionId, questionId, index }),
    );
    event.dataTransfer.setData("text/plain", questionId);
  };

  const getHandoutCardInsertIndex = (event, index) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientY < rect.top + rect.height / 2 ? index : index + 1;
  };

  const getHandoutDropZoneInsertIndex = (event, fallbackIndex) => {
    const cards = Array.from(
      event.currentTarget.querySelectorAll("[data-handout-question-card='true']"),
    );
    for (let index = 0; index < cards.length; index += 1) {
      const rect = cards[index].getBoundingClientRect();
      if (event.clientY < rect.top + rect.height / 2) return index;
    }
    return fallbackIndex;
  };

  const handleHandoutDrop = (event, sectionId, insertIndex = null) => {
    event.preventDefault();
    const dragState = draggedHandoutQuestion;
    const resolvedInsertIndex =
      Number.isInteger(insertIndex)
        ? insertIndex
        : handoutInsertTarget?.sectionId === sectionId
          ? handoutInsertTarget.index
          : null;

    if (dragState?.type === "section") {
      moveHandoutQuestionToSection(
        dragState.sectionId,
        dragState.questionId,
        sectionId,
        resolvedInsertIndex,
      );
    } else {
      const questionId =
        dragState?.question?.id ||
        event.dataTransfer.getData("application/x-math-bank-question-id") ||
        event.dataTransfer.getData("text/plain");
      const question = dragState?.question || getHandoutQuestionById(questionId);
      addQuestionToHandoutSection(sectionId, question, resolvedInsertIndex);
    }

    setDraggedHandoutQuestion(null);
    setDragOverHandoutSectionId("");
    setHandoutInsertTarget(null);
  };

  const handleHandoutDragOver = (event, sectionId, insertIndex = null) => {
    const targetSection = handoutSections.find((section) => section.id === sectionId);
    if (targetSection?.type !== "questions") return;
    event.preventDefault();
    event.dataTransfer.dropEffect = draggedHandoutQuestion?.type === "section" ? "move" : "copy";
    setDragOverHandoutSectionId((currentSectionId) =>
      currentSectionId === sectionId ? currentSectionId : sectionId,
    );
    const questionCount = targetSection.questions?.length || 0;
    const nextInsertIndex = Number.isInteger(insertIndex)
      ? Math.max(0, Math.min(insertIndex, questionCount))
      : questionCount;
    setHandoutInsertTarget((currentTarget) =>
      currentTarget?.sectionId === sectionId && currentTarget?.index === nextInsertIndex
        ? currentTarget
        : { sectionId, index: nextInsertIndex },
    );
  };

  const handleHandoutQuestionCardDragOver = (event, sectionId, index) => {
    event.stopPropagation();
    handleHandoutDragOver(event, sectionId, getHandoutCardInsertIndex(event, index));
  };

  const handleHandoutQuestionCardDrop = (event, sectionId, index) => {
    event.stopPropagation();
    handleHandoutDrop(event, sectionId, getHandoutCardInsertIndex(event, index));
  };

  const handleHandoutDropZoneDragOver = (event, section) => {
    if (!section?.id) return;
    const questionCount = section?.questions?.length || 0;
    handleHandoutDragOver(
      event,
      section.id,
      getHandoutDropZoneInsertIndex(event, questionCount),
    );
  };

  const handleHandoutDropZoneDrop = (event, section) => {
    if (!section?.id) return;
    const questionCount = section?.questions?.length || 0;
    handleHandoutDrop(
      event,
      section.id,
      getHandoutDropZoneInsertIndex(event, questionCount),
    );
  };

  const handleHandoutDragLeave = (event) => {
    if (
      (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) ||
      isPointerInsideElement(event)
    ) return;
    setDragOverHandoutSectionId("");
    setHandoutInsertTarget(null);
  };

  const handleHandoutDragEnd = () => {
    setDraggedHandoutQuestion(null);
    setDragOverHandoutSectionId("");
    setHandoutInsertTarget(null);
  };

  const getHandoutPreviewInteractionProps = (section, question = null, index = 0) => {
    const isQuestion = Boolean(question);
    const sectionInsert = handoutOutlineInsertTarget?.sectionId === section.id;
    const questionInsert = !draggedHandoutOutlineSectionId && handoutInsertTarget?.sectionId === section.id;
    const endDrag = () => {
      handleHandoutDragEnd();
      setDraggedHandoutOutlineSectionId("");
      setHandoutOutlineInsertTarget(null);
    };
    return {
      draggable: true,
      tabIndex: 0,
      "data-preview-editable": "true",
      "data-preview-question-id": question?.id,
      "data-preview-section-id": isQuestion ? undefined : section.id,
      "data-preview-dragging": (isQuestion
        ? draggedHandoutQuestion?.type === "section" && draggedHandoutQuestion.sectionId === section.id && draggedHandoutQuestion.questionId === question.id
        : draggedHandoutOutlineSectionId === section.id) || undefined,
      "data-preview-insert": isQuestion
        ? questionInsert && (handoutInsertTarget.index === index ? "before" : index === section.questions.length - 1 && handoutInsertTarget.index === index + 1 ? "after" : undefined)
        : sectionInsert ? (handoutOutlineInsertTarget.afterTarget ? "after" : "before") : undefined,
      "aria-label": isQuestion ? `預覽第 ${getHandoutQuestionNumber(handoutQuestionIndexById, section.id, index, question.id)} 題` : `預覽區塊 ${section.title || "題型"}`,
      title: "拖曳調整順序；雙擊定位區塊內容",
      onDoubleClick: (event) => {
        if (isHandoutPreviewControl(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        setActiveHandoutSectionId(section.id);
        setHandoutPreviewEditTarget({ sectionId: section.id, questionId: question?.id });
      },
      onDragStart: (event) => {
        if (isHandoutPreviewControl(event.target)) { event.preventDefault(); return; }
        event.stopPropagation();
        if (isQuestion) handleHandoutSectionQuestionDragStart(event, section.id, question.id, index);
        else handleHandoutOutlineDragStart(event, section.id);
      },
      onDragOver: (event) => {
        if (draggedHandoutAsset) return;
        if (!draggedHandoutOutlineSectionId && !draggedHandoutQuestion) return;
        event.stopPropagation();
        if (draggedHandoutOutlineSectionId) {
          if (draggedHandoutOutlineSectionId === section.id) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          const afterTarget = getHandoutPreviewInsertAfter(event);
          setHandoutOutlineInsertTarget((current) => current?.sectionId === section.id && current.afterTarget === afterTarget ? current : { sectionId: section.id, afterTarget });
        } else if (section.type === "questions") {
          handleHandoutDragOver(event, section.id, isQuestion ? index + Number(getHandoutPreviewInsertAfter(event)) : 0);
        }
      },
      onDrop: (event) => {
        if (draggedHandoutAsset) return;
        if (!draggedHandoutOutlineSectionId && !draggedHandoutQuestion) return;
        event.stopPropagation();
        event.preventDefault();
        if (draggedHandoutOutlineSectionId) {
          moveHandoutOutlineSection(draggedHandoutOutlineSectionId, section.id, getHandoutPreviewInsertAfter(event));
        } else if (section.type === "questions") {
          handleHandoutDrop(event, section.id, isQuestion ? index + Number(getHandoutPreviewInsertAfter(event)) : 0);
        }
        endDrag();
      },
      onDragLeave: (event) => {
        if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
        setHandoutOutlineInsertTarget(null);
        handleHandoutDragLeave(event);
      },
      onDragEnd: endDrag,
    };
  };

  const handleGenerate = async ({
    includeHandoutOptions = false,
    sourceIds = selectedIds,
    sourceQuestions = selectedQuestions,
    sourceSections = handoutSections,
    emptyMessage = "請先選擇題目",
    progressText = "",
    sectionModeOverride = sectionMode,
    templateIdOverride = templateId,
    modeOverride = outputMode,
    mixedVersion,
    titleOverride = documentTitle,
    filenameOverride = outputFilename,
    sourceRecordOverride = null,
  } = {}) => {
    if (!isActive || !routeActive || liveWordExportScope.current !== wordExportScope || wordExportTaskRef.current || (includeHandoutOptions && (handoutSaveInFlightRef.current || recordSaveInFlightRef.current))) return;
    if (!sourceIds.length && !(includeHandoutOptions && mathHandoutTemplateIds.has(templateIdOverride) &&
      hasHandoutSectionContent(sourceSections, handoutOptions))) {
      setNotice({ tone: "error", text: emptyMessage });
      return;
    }
    if (isBilingualExamTemplate(templateIdOverride) && !hasBilingualPreview) {
      setNotice({ tone: "error", text: "請先翻譯並預覽雙語考卷。" });
      return;
    }
    const maxExportQuestions = isBilingualExamTemplate(templateIdOverride) ? BILINGUAL_MAX_QUESTIONS : HANDOUT_MAX_QUESTIONS;
    if (sourceIds.length > maxExportQuestions) {
      setNotice({ tone: "error", text: `一次最多匯出 ${maxExportQuestions} 題，目前 ${sourceIds.length} 題。請移除部分題目，或分成數份後再匯出。` });
      return;
    }

    const defaultFilename = pickerTemplateIds.has(templateIdOverride)
      ? `${pickerTemplateTitles[templateIdOverride]}.docx`
      : defaultHandoutFilename;
    const filename = ensureDocxFilename(filenameOverride, defaultFilename);
    const task = { controller: new AbortController(), scope: wordExportScope, includeHandoutOptions };
    const sourceRecord = includeHandoutOptions && !pickerExamTemplateIds.has(templateIdOverride) && templateIdOverride !== "exam_paper"
      ? sourceRecordOverride || activeHandoutRecordSource : null;
    wordExportTaskRef.current = task;
    const isCurrent = () => wordExportTaskRef.current === task && liveWordExportScope.current === task.scope;
    const assertActive = () => {
      if (!isCurrent() || task.controller.signal.aborted) throw cancelledWordExport();
    };
    const onProgress = (progress) => {
      if (isCurrent() && !task.controller.signal.aborted) setWordExportProgress(progress);
    };
    const retry = () => handleGenerate({ includeHandoutOptions, sourceIds, sourceQuestions, sourceSections, emptyMessage,
      progressText, sectionModeOverride, templateIdOverride, modeOverride, mixedVersion, titleOverride, filenameOverride });
    setWordExportFilename(filename);
    setWordExportProgress(modeOverride === "mixed" && !mixedVersion ? null
      : { stage: "preparing", current: 0, total: sourceQuestions.length, message: "正在準備題目圖文與公式…" });
    setWordExportCancelling(false);
    setGenerating(true);
    if (includeHandoutOptions) handoutSaveInFlightRef.current = true;
    const version = handoutDocumentVersion.current;
    const pickerVersion = pickerRecordLoadVersion.current;
    const exportEditorSignature = recordEditorSignature(includeHandoutOptions
      ? buildHandoutDraftPayload() : buildPickerEditorPayload());
    setNotice({ tone: "info", text: progressText || (isBilingualExamTemplate(templateIdOverride)
      ? "正在使用已確認的英文翻譯產生雙語考卷 Word..."
      : "正在由題庫選題產生 Word...") });

    try {
      if (modeOverride === "mixed" && !mixedVersion) {
        mixedVersion = await chooseMixedWordExport(task.controller.signal);
        if (!mixedVersion || !isCurrent()) { if (isCurrent()) setNotice(null); return; }
      }
      if (!sourceRecord) await hydratePythonAssetPreviews(sourceQuestions, { signal: task.controller.signal, onProgress });
      assertActive();
      onProgress({ stage: "queued", message: "正在送出 Word 匯出工作…" });
      const defaultTitle = includeHandoutOptions && templateIdOverride === "exam_paper"
        ? defaultExamHandoutTitle
        : defaultHandoutTitle;
      const title = String(titleOverride || "").trim() || defaultTitle;
      const savedTranslations = selectBilingualTranslations(sourceIds, bilingualPreview?.translations, bilingualTranslations);
      const recordPayload = {
        ...(includeHandoutOptions ? { ...buildHandoutDraftPayload(), sections: sourceSections }
          : { sectionMode: sectionModeOverride, examRange, familyDraws }),
        ...(Object.keys(savedTranslations).length ? { bilingualTranslations: savedTranslations } : {}),
      };
      const savedHandoutPayload = includeHandoutOptions ? cloneHandoutPayload({ ...recordPayload,
        title, filename, templateId: templateIdOverride, outputMode: modeOverride,
      }) : null;
      const result = sourceRecord ? await exportEditedHandoutRecord({
        authToken, id: sourceRecord.id, expectedUpdatedAt: sourceRecord.updatedAt,
        recordPayload: prepareHandoutRecordPayload(savedHandoutPayload), mode: modeOverride,
        ...(modeOverride === "mixed" ? { mixedVersion } : {}),
        signal: task.controller.signal, onProgress,
      }) : await generateWordFromBank({
        subject,
        signal: task.controller.signal,
        onProgress,
        questionIds: sourceIds,
        title,
        filename,
        templateId: templateIdOverride,
        examRange: pickerExamTemplateIds.has(templateIdOverride) ? examRange.trim() : "",
        mode: modeOverride === "teaching" && (pickerExamTemplateIds.has(templateIdOverride) ||
          (includeHandoutOptions && templateIdOverride === "exam_paper")) ? "teaching_brief" : modeOverride,
        ...(modeOverride === "mixed" ? { mixedVersion } : {}),
        sectionMode: sectionModeOverride,
        handoutOptions: includeHandoutOptions
          ? buildHandoutOptionsPayload(handoutOptions, sourceSections, sourceIds.length)
          : undefined,
        recordWorkspace: includeHandoutOptions ? "handout" : "picker",
        ...(isBilingualExamTemplate(templateIdOverride) ? { bilingualTranslations: savedTranslations } : {}),
        recordPayload: prepareHandoutRecordPayload(recordPayload),
      });

      if (!isCurrent()) return;
      if (task.controller.signal.aborted || result.cancelled) {
        const cancellation = cancelledWordExport();
        if (result.cancellationUnconfirmed) Object.assign(cancellation, { cancellationUnconfirmed: true, message: result.error });
        throw cancellation;
      }
      if (result.success) {
        if (!sourceRecord && savedHandoutPayload && (result.handoutRecordId || result.examRecordId)) {
          if (rememberSavedHandout(savedHandoutPayload, selectedHandoutDraftId, version)) {
            recordEditorBaselines.current.handout = exportEditorSignature;
          }
        } else if (!includeHandoutOptions && pickerVersion === pickerRecordLoadVersion.current && (result.handoutRecordId || result.examRecordId)) {
          recordEditorBaselines.current.picker = exportEditorSignature;
        }
        downloadBlob(result.blob, result.filename || filename);
        setNotice({ tone: "success", text: sourceRecord ? "Word 已依目前版面產生並開始下載。" : result.examRecordId ? "Word 已產生並開始下載，考卷紀錄已儲存。" : result.handoutRecordId ? "Word 已產生並開始下載，講義紀錄已儲存。" : "Word 已產生並開始下載" });
        setShowPreviewDialog(false);
      } else {
        const needsBilingualPreview = ["bilingual_preview_stale", "bilingual_preview_required"].includes(result.code);
        if (needsBilingualPreview) setBilingualPreview(null);
        setNotice({ tone: "error", text: result.error || "Word 產生失敗", ...(needsBilingualPreview ? {} : { retry }) });
      }
    } catch (error) {
      if (!isCurrent()) return;
      if (task.controller.signal.aborted || error.cancelled) setNotice({ tone: "info", text: error.cancellationUnconfirmed ? error.message : "已取消 Word 匯出。" });
      else setNotice({ tone: "error", text: error.message || "題庫講義產生失敗", retry });
    } finally {
      if (isCurrent()) {
        wordExportTaskRef.current = null;
        if (includeHandoutOptions) handoutSaveInFlightRef.current = false;
        setGenerating(false);
        setWordExportProgress(null);
        setWordExportCancelling(false);
      }
    }
  };

  const cancelWordExport = () => {
    const task = wordExportTaskRef.current;
    if (!task || task.controller.signal.aborted) return;
    setWordExportCancelling(true);
    task.controller.abort();
  };

  // An opened record exports its saved layout, so unsaved edits (for example
  // newly added questions) would be rejected. Offer to save first instead.
  const handoutHasUnsavedChanges = () =>
    recordEditorSignature(buildHandoutDraftPayload()) !== recordEditorBaselines.current.handout;
  const handleHandoutGenerate = async () => {
    let sourceRecordOverride = null;
    if (activeHandoutRecordSource && !isExamHandoutTemplate && handoutHasUnsavedChanges()) {
      const { isConfirmed } = await confirmSaveBeforeExport();
      if (!isConfirmed) return;
      const result = await saveHandoutRecord("handout");
      if (!result?.saved) return;
      sourceRecordOverride = result.source;
    }
    return runHandoutGenerate(sourceRecordOverride);
  };
  const runHandoutGenerate = (sourceRecordOverride) =>
    handleGenerate({
      sourceRecordOverride,
      includeHandoutOptions: true,
      sourceIds: handoutQuestionIdsForOutput,
      sourceQuestions: handoutQuestionsForOutput,
      sourceSections: handoutSections,
      emptyMessage: `請先把題目拖到${handoutOutlineTitle}`,
      progressText: isExamHandoutTemplate ? "正在產生檢測卷 Word..." : "正在產生講義 Word...",
      sectionModeOverride: "selected",
      templateIdOverride: handoutTemplateId,
      modeOverride: handoutOutputMode,
      titleOverride: handoutTitle,
      filenameOverride: handoutFilename,
    });

  const activeCountText =
    activeWorkspace === "handout"
      ? `${handoutWorkspaceLabel} ${handoutTotalQuestionCount} 題，題池 ${filteredHandoutQuestionPool.length}${handoutHasMore ? "+" : ""} 題`
      : shouldLoadQuestionResults
        ? `${selectedIds.length} / ${filteredQuestions.length}${hasMore ? "+" : ""} 題`
        : "尚未篩選";

  const canRefreshActiveWorkspace =
    activeWorkspace === "handout"
      ? Boolean(handoutRestorePoint) && !recordSaving && !generating && !previewPreparing
      : shouldLoadQuestionResults && !questionLoading;

  const refreshActiveWorkspace = () => {
    if (activeWorkspace === "handout") {
      if (!handoutRestorePoint) return;
      applyHandoutPayload(cloneHandoutPayload(handoutRestorePoint.payload), { sourceRecord: handoutRestorePoint.sourceRecord });
      setHandoutPreviewPage(0);
      setHandoutVimTarget(null);
      setHandoutPreviewEditTarget(null);
      setNotice({ tone: "success", text: "已還原為最近一次儲存或載入的內容。" });
      return;
    }
    loadQuestionPage({ reset: true, force: true });
  };

  const handoutVimSection = handoutSections.find((section) => section.id === handoutVimTarget?.sectionId);
  const handoutVimEditor = handoutVimTarget?.kind === "exam" ? {
    key: `handout-exam-${handoutVimTarget.field}`,
    title: handoutVimTarget.title,
    value: handoutExamSettings[handoutVimTarget.field] || "",
    onChange: (value) => updateHandoutNestedOption("exam", handoutVimTarget.field, value),
    defaultVimMode: true,
    preview: true,
  } : handoutVimTarget?.kind === "focus" && handoutVimSection ? {
    key: `handout-focus-${handoutVimSection.id}`,
    title: "重點整理內容",
    value: getHandoutFocusLines(handoutVimSection, handoutOptions),
    onChange: (value) => updateHandoutFocusLines(handoutVimSection.id, value),
    defaultVimMode: true,
    preview: true,
    previewContent: <MarkdownMathText source={getHandoutFocusLines(handoutVimSection, handoutOptions)} />,
  } : null;

  const renderExamSettingsFields = () => (
    <ExamSettingsGrid>
      <Field>
        <span>題本標</span>
        <input
          value={handoutExamSettings.coverTitle}
          onChange={(event) =>
            updateHandoutNestedOption("exam", "coverTitle", event.target.value)
          }
        />
      </Field>
      <Field>
        <span>說明標題</span>
        <input
          value={handoutExamSettings.instructionTitle}
          onChange={(event) =>
            updateHandoutNestedOption(
              "exam",
              "instructionTitle",
              event.target.value,
            )
          }
        />
      </Field>
      <Field>
        <span>考試時間</span>
        <input
          type="number"
          min="1"
          max="300"
          value={handoutExamSettings.examTimeMinutes}
          onChange={(event) =>
            updateHandoutNestedOption(
              "exam",
              "examTimeMinutes",
              event.target.value,
            )
          }
        />
      </Field>
      <Field>
        <span>題數</span>
        <input
          type="number"
          min="1"
          max="300"
          placeholder={`${handoutTotalQuestionCount || 0}`}
          value={handoutExamSettings.questionCount}
          onChange={(event) =>
            updateHandoutNestedOption("exam", "questionCount", event.target.value)
          }
        />
      </Field>
      <Field>
        <span>題本頁數</span>
        <input
          type="number"
          min="1"
          max="80"
          value={handoutExamSettings.pageCount}
          onChange={(event) =>
            updateHandoutNestedOption("exam", "pageCount", event.target.value)
          }
        />
      </Field>
      <Field>
        <span>滿分</span>
        <input
          type="number"
          min="1"
          max="500"
          value={handoutExamSettings.totalScore}
          onChange={(event) =>
            updateHandoutNestedOption("exam", "totalScore", event.target.value)
          }
        />
      </Field>
      <ExamPartSettingsPanel>
        <ExamPartSettingsHeader>
          <strong>檢測題目配分</strong>
          <span>拖題後題數會自動更新，配分文字可手動調整。</span>
        </ExamPartSettingsHeader>
        <ExamPartSettingsList>
          {handoutExamParts.map((part) => (
            <ExamPartSettingsRow key={part.id}>
              <Field>
                <span>部分名稱</span>
                <input
                  value={part.title}
                  onChange={(event) => updateExamPartTitle(part.id, event.target.value)}
                />
              </Field>
              <Field>
                <span>題數</span>
                <input readOnly value={`${part.questionCount || 0} 題`} />
              </Field>
              <Field>
                <span>配分文字</span>
                <input
                  value={part.scoreText}
                  onChange={(event) =>
                    updateExamPartOption(part.id, "scoreText", event.target.value)
                  }
                />
              </Field>
            </ExamPartSettingsRow>
          ))}
        </ExamPartSettingsList>
      </ExamPartSettingsPanel>
      <Field as="div">
        <span>首頁提醒</span>
        <TextareaWithVim>
          <textarea
            aria-label="首頁提醒"
            value={handoutExamSettings.noticeText}
            onChange={(event) =>
              updateHandoutNestedOption("exam", "noticeText", event.target.value)
            }
          />
          <PendingVimFieldButton type="button" aria-label="Vim 編輯首頁提醒" onClick={() => setHandoutVimTarget({ kind: "exam", field: "noticeText", title: "首頁提醒" })}>Vim 編輯</PendingVimFieldButton>
        </TextareaWithVim>
      </Field>
      <Field as="div">
        <span>檢測說明</span>
        <TextareaWithVim>
          <textarea
            aria-label="檢測說明"
            value={handoutExamSettings.description}
            onChange={(event) =>
              updateHandoutNestedOption("exam", "description", event.target.value)
            }
          />
          <PendingVimFieldButton type="button" aria-label="Vim 編輯檢測說明" onClick={() => setHandoutVimTarget({ kind: "exam", field: "description", title: "檢測說明" })}>Vim 編輯</PendingVimFieldButton>
        </TextareaWithVim>
      </Field>
      <Field as="div">
        <span>注意事項</span>
        <TextareaWithVim>
          <textarea
            aria-label="注意事項"
            value={handoutExamSettings.cautions}
            onChange={(event) =>
              updateHandoutNestedOption("exam", "cautions", event.target.value)
            }
          />
          <PendingVimFieldButton type="button" aria-label="Vim 編輯注意事項" onClick={() => setHandoutVimTarget({ kind: "exam", field: "cautions", title: "注意事項" })}>Vim 編輯</PendingVimFieldButton>
        </TextareaWithVim>
      </Field>
      <Field as="div">
        <span>作答方式</span>
        <TextareaWithVim>
          <textarea
            aria-label="作答方式"
            value={handoutExamSettings.answerRules}
            onChange={(event) =>
              updateHandoutNestedOption("exam", "answerRules", event.target.value)
            }
          />
          <PendingVimFieldButton type="button" aria-label="Vim 編輯作答方式" onClick={() => setHandoutVimTarget({ kind: "exam", field: "answerRules", title: "作答方式" })}>Vim 編輯</PendingVimFieldButton>
        </TextareaWithVim>
      </Field>
    </ExamSettingsGrid>
  );

  const renderExamTemplateFileActions = (ids) => (
    <TemplateWordPanel>
      {examTemplateFilesForUi
        .filter((file) => ids.includes(file.id))
        .map((file) => (
          <TemplateWordItem key={file.id}>
            <strong>{file.label}</strong>
            <TemplateWordButtons>
              <IconButton type="button" onClick={() => setTemplatePreviewFile(file)}>
                <FaFileWord />
                預覽空白模板
              </IconButton>
              <IconButton type="button" onClick={() => downloadTemplateFile(file)}>
                <FaDownload />
                下載空白模板
              </IconButton>
            </TemplateWordButtons>
          </TemplateWordItem>
        ))}
    </TemplateWordPanel>
  );

  const renderExamInstructionTemplatePreview = () => {
    const noticeLines = splitHandoutLines(handoutExamSettings.noticeText);
    const cautionLines = handoutExamCautionLines;
    const answerRuleLines = splitHandoutLines(handoutExamSettings.answerRules);

    return (
      <ExamTemplatePage>
        <ExamTemplateHeader>寰宇　{subjectLabel}研發團隊</ExamTemplateHeader>
        <ExamInstructionTemplateBody>
          <ExamIdentityLine>班級：＿＿＿＿　姓名：＿＿＿＿　座號：＿＿</ExamIdentityLine>
          <ExamCoverTitle>{handoutTitle || defaultExamHandoutTitle}</ExamCoverTitle>
          <ExamBrandPlate>{handoutExamSettings.coverTitle || "GM/MAC試題本"}</ExamBrandPlate>
          <ExamNoticeBox>
            {(noticeLines.length
              ? noticeLines
              : [
                "請不要翻到次頁！",
                "讀完本頁的說明，聽從監考老師的指示才開始作答！",
              ]
            ).map((line, index) => (
              <span key={`${line}-${index}`}>{line}</span>
            ))}
          </ExamNoticeBox>
          <ExamInfoBox>
            <ExamWatermark>4</ExamWatermark>
            <strong>{handoutExamSettings.instructionTitle || "檢測說明"}：</strong>
            <span>
              {handoutExamSettings.description || "本測驗題本採雙面印刷。"}
              題目共 {handoutExamQuestionCount || handoutTotalQuestionCount || "　"} 題，
              題本 {handoutExamSettings.pageCount || "　"} 頁，
              滿分 {handoutExamSettings.totalScore || "　"} 分。
            </span>
            <span>測驗時間 {handoutExamSettings.examTimeMinutes || "　"} 分鐘。</span>
            <strong>注意事項：</strong>
            {(cautionLines.length
              ? cautionLines
              : [
                "題本分三個部分，第一部分10題，第二部分10題，第三部分6題，皆為應用題。",
                "試題中參考的附圖，不一定代表實際大小。",
                "請勿故意汙損答案卷、損壞試題本，考試不予計分。",
              ]
            ).slice(0, 4).map((line, index) => (
              <span key={`caution-${line}-${index}`}>{line}</span>
            ))}
            <strong>作答方式：</strong>
            {(answerRuleLines.length
              ? answerRuleLines
              : [
                "作答時，可利用題本中空白部分計算，過程不扣分，以最終答案作依據給分。",
                "請依照題意將解答過程及最後結果，清楚完整地呈現出來。",
                "若解答過程使用了題目敘述中沒有出現的符號，則必須說明。",
                "更正時請使用橡皮擦或修正帶(液)修正後，重新書寫解答過程。",
              ]
            ).slice(0, 4).map((line, index) => (
              <span key={`answer-${line}-${index}`}>{line}</span>
            ))}
          </ExamInfoBox>
          <ExamSignatureBox />
        </ExamInstructionTemplateBody>
        <ExamTemplateFooter>{handoutTitle || defaultExamHandoutTitle}</ExamTemplateFooter>
      </ExamTemplatePage>
    );
  };

  const renderExamQuestionTemplatePreview = ({
    questions,
    title,
    mode,
    compact = false,
    parts = [],
    showOrganizationName = true,
    range = "",
  }) => {
    const examParts = Array.isArray(parts) ? parts : [];
    const pages = [];
    const hasPartQuestions = examParts.some((part) => (part.questions || []).length);

    if (examParts.length) {
      let questionOffset = 0;
      examParts.forEach((part, partIndex) => {
        const partQuestions = part.questions || [];
        const shouldShowEmptyPart = !hasPartQuestions && partIndex === 0;
        if (!partQuestions.length && !shouldShowEmptyPart) return;

        const previewQuestions = partQuestions.length
          ? partQuestions
          : Array.from({ length: examPreviewQuestionsPerPage });
        chunkItems(previewQuestions, examPreviewQuestionsPerPage).forEach(
          (pageQuestions, pageOffset) => {
            pages.push({
              part,
              partIndex,
              pageQuestions,
              questionStart: questionOffset + pageOffset * examPreviewQuestionsPerPage + 1,
            });
          },
        );
        questionOffset += partQuestions.length;
      });
    }

    if (!pages.length) {
      chunkItems(
        questions.length ? questions : Array.from({ length: examPreviewQuestionsPerPage }),
        examPreviewQuestionsPerPage,
      ).forEach((pageQuestions, pageIndex) => {
        pages.push({
          part: null,
          partIndex: pageIndex,
          pageQuestions,
          questionStart: pageIndex * examPreviewQuestionsPerPage + 1,
        });
      });
    }

    return (
      <ExamPreviewStack $compact={compact}>
        {pages.map((page, pageIndex) => {
          const slots = Array.from(
            { length: examPreviewQuestionsPerPage },
            (_, slotIndex) => page.pageQuestions[slotIndex] || null,
          );
          return (
            <ExamTemplatePage key={`exam-preview-page-${pageIndex}`} $compact={compact}>
              <ExamTemplateHeader>{showOrganizationName ? "寰宇　" : ""}{subjectLabel}研發團隊</ExamTemplateHeader>
              <ExamQuestionTemplateBody $compact={compact}>
                <ExamQuestionPartTitle>
                  {range && pageIndex === 0 && <div>範圍：{range}</div>}
                  {page.part
                    ? getExamPreviewPartHeading(page.part, page.partIndex)
                    : getExamPreviewPartTitle(pageIndex)}
                </ExamQuestionPartTitle>
                {slots.map((question, slotIndex) => {
                  const questionNumber = page.questionStart + slotIndex;
                  return (
                    <ExamQuestionSlot key={`exam-slot-${pageIndex}-${slotIndex}`}>
                      <ExamQuestionLine>
                        <ExamQuestionNumber>{questionNumber}.</ExamQuestionNumber>
                        <ExamQuestionBody>
                          {question ? (
                            <>
                              <MarkdownAwareMathText
                                source={getHandoutPreviewPrompt(question)}
                                fallback=""
                              />
                              <AssetPreviewStrip
                                assets={question.assets || []}
                                compact
                                limit={compact ? 1 : 2}
                              />
                            </>
                          ) : (
                            <ExamQuestionPlaceholder>？</ExamQuestionPlaceholder>
                          )}
                        </ExamQuestionBody>
                      </ExamQuestionLine>
                      <AnswerSourcePreview question={question}>
                        {mode !== "student" && <ExamSlotAnswer style={{ color: "#ff0000" }}>
                          <strong>Ans：</strong>
                          {question && <MarkdownAwareMathText source={getHandoutPreviewAnswer(question)} fallback="" />}
                        </ExamSlotAnswer>}
                      </AnswerSourcePreview>
                    </ExamQuestionSlot>
                  );
                })}
              </ExamQuestionTemplateBody>
              <ExamTemplateFooter>{title}</ExamTemplateFooter>
            </ExamTemplatePage>
          );
        })}
      </ExamPreviewStack>
    );
  };

  const renderPickerA4Preview = () => {
    if (isBilingualExamTemplate(templateId) && hasBilingualPreview) {
      return <BilingualDocumentPreview document={bilingualPreviewDocument} title={documentTitle} range={examRange.trim()} mode={outputMode === "teaching" ? "teaching_brief" : outputMode} />;
    }
    const items = previewSections.flatMap((section) => section.questions.map((question, index) => ({
      id: String(question.id),
      content: <>
        {index === 0 && <HandoutSectionTitle>{section.title}</HandoutSectionTitle>}
        {renderPickerQuestionContent(question)}
      </>,
    })));
    return <PaginatedQuestionPreview items={items}
      fontFamily={previewFontFamilies[previewFont]} fontSize={previewFontConfig.bodySize * 96 / 72}
      header={<><h3>{documentTitle.trim() || defaultHandoutTitle}</h3>{examRange.trim() && <p>範圍：{examRange.trim()}</p>}</>} />;
  };

  const startHandoutImageResize = (event, widthCm, updateWidth) => {
    event.preventDefault();
    event.stopPropagation();
    handoutImageResizeCleanupRef.current?.();
    const startX = event.clientX;
    const resizeElement = event.currentTarget;
    const originScroll = dragScrollOffset(resizeElement);
    const figure = resizeElement.parentElement;
    let nextWidth = widthCm;
    const onMove = (moveEvent) => {
      const deltaCm = (moveEvent.clientX - startX + dragScrollOffset(resizeElement).x - originScroll.x) / ((96 / 2.54) * handoutPreviewScale);
      nextWidth = Math.round(Math.min(16, Math.max(2, widthCm + deltaCm)) * 10) / 10;
      figure.style.width = `${nextWidth}cm`;
      const imageCard = figure.querySelector("figure");
      if (imageCard) imageCard.style.width = "100%";
      const caption = figure.querySelector("small");
      if (caption) caption.textContent = `${nextWidth} cm`;
    };
    const cleanup = (finishEvent) => {
      // Re-render Python labels once at the final physical width, not on every
      // pointer move. The drag itself scales the current preview immediately.
      const finalWidth = finishEvent?.type === "pointerup" ? nextWidth : widthCm;
      figure.style.width = `${finalWidth}cm`;
      const caption = figure.querySelector("small");
      if (caption) caption.textContent = `${finalWidth} cm`;
      if (finishEvent?.type === "pointerup" && nextWidth !== widthCm) updateWidth(nextWidth);
      imageResizeAutoScroll.stop();
      window.removeEventListener("pointercancel", cleanup);
      window.removeEventListener("blur", cleanup);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", cleanup);
      handoutImageResizeCleanupRef.current = null;
    };
    handoutImageResizeCleanupRef.current = cleanup;
    imageResizeAutoScroll.start(event, { axes: "x" });
    window.addEventListener("pointercancel", cleanup);
    window.addEventListener("blur", cleanup);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", cleanup);
  };

  const dropHandoutAssetOnPreview = (event) => {
    if (!draggedHandoutAsset) return;
    event.preventDefault();
    const target = event.target.closest?.("[data-handout-section-id]");
    const targetId = target?.getAttribute("data-handout-section-id");
    const targetIndex = handoutSections.findIndex((section) => section.id === targetId);
    const insertIndex = targetIndex < 0 ? handoutSections.length
      : targetIndex + (event.clientY > target.getBoundingClientRect().top +
        target.getBoundingClientRect().height / 2 ? 1 : 0);
    addHandoutImage(draggedHandoutAsset, insertIndex);
    setDraggedHandoutAsset(null);
  };

  const renderHandoutResizableImage = ({ src, alt, widthCm, onResize, label, inline = false, asset }) => (
    <HandoutResizableImage $inline={inline} style={{ width: `${widthCm}cm` }}>
      {asset?.source_language === "python" && asset.source_code
        ? <QuestionAssetPreviewStrip assets={[asset]} widthCm={widthCm} bodyFontSize={handoutOptions.bodyFontSize} transparentBackground showCaption={false} />
        : <img src={src} alt={alt} {...handoutImageLoadProps(asset)} onLoad={() => setHandoutPreviewMeasureTick((tick) => tick + 1)} />}
      <button type="button" aria-label={`調整${label}大小`} title="向左右拖曳調整圖片寬度"
        onPointerDown={(event) => startHandoutImageResize(event, widthCm, onResize)}>
        ↔
      </button>
      <small>{widthCm} cm</small>
    </HandoutResizableImage>
  );

  const renderHandoutBuilderA4Preview = () => {
    if (isExamHandoutTemplate) {
      return (
        <PageChrome>
          {activeHandoutSection?.type === "exam_info"
            ? renderExamInstructionTemplatePreview()
            : renderExamQuestionTemplatePreview({
              questions: handoutQuestionsForOutput,
              title: handoutTitle.trim() || defaultExamHandoutTitle,
              mode: handoutOutputMode,
              parts: handoutExamParts,
            })}
        </PageChrome>
      );
    }

    return <HandoutBuilderA4Preview {...{ handoutPreviewMode, handoutPreviewPage, handoutPreviewPageCount, handoutPreviewScale, setHandoutPreviewPage, draggedHandoutAsset, dropHandoutAssetOnPreview, handoutContentLeft, handoutContentRight, handoutContentTop, handoutContentBottom, handoutPageContentWidth, handoutColumnGap, handoutOptions, handoutLineHeight, handoutFlowRef, handoutTitle, handoutSections, activeHandoutSectionId, setActiveHandoutSectionId, renderHandoutResizableImage, updateHandoutImageWidth, updateHandoutFocusImage, handoutQuestionIndexById, updateQuestionImageWidth, handoutOutputMode, canExportHandout, handoutPickerTitle, handoutQuestionTargetLabel, getHandoutPreviewInteractionProps, showFormattingMarks: handoutShowFormattingMarks, flashQuestionIds }} />;
  };

  const renderPickerQuestionContent = (question) => (
              <HandoutQuestion key={question.id}>
                <HandoutQuestionText>
                  <HandoutQuestionNumber>
                    {previewSections.flatMap((section) => section.questions).findIndex((item) => item.id === question.id) + 1}.
                  </HandoutQuestionNumber>
                  <HandoutQuestionBody>
                    <MarkdownAwareMathText
                      source={getHandoutPreviewPrompt(question)}
                      fallback=""
                    />
                  </HandoutQuestionBody>
                </HandoutQuestionText>
                <AssetPreviewStrip
                  assets={question.assets || []}
                  compact
                  limit={(question.assets || []).length}
                />
                {outputMode !== "student" && <AnswerSourcePreview question={question}>
                  {outputMode !== "student" && getHandoutPreviewAnswer(question) && <HandoutAnswer style={{ color: "#ff0000" }}>
                    <strong>【答案】</strong>
                    <MarkdownAwareMathText source={getHandoutPreviewAnswer(question)} fallback="" />
                  </HandoutAnswer>}
                </AnswerSourcePreview>}
                {(outputMode === "teaching_detailed" || (!isPickerExamTemplate && outputMode === "teaching")) && question.solution_md && (
                  <HandoutSolution style={outputMode === "mixed" ? { color: "#ff0000" } : undefined}>
                    <MarkdownAwareMathText
                      source={question.solution_md}
                      fallback=""
                    />
                  </HandoutSolution>
                )}
              </HandoutQuestion>
  );

  const bilingualIssuesPanel = (bilingualError || Object.keys(bilingualPendingQuestions).length > 0) && (
    <Notice $tone="error" role="region" aria-label="翻譯問題與待處理題目">
      <strong>翻譯問題與待處理題目</strong>
      <p>以下訊息會保留，成功題的翻譯仍在快取中。待處理題目會另外列出。</p>
      {bilingualError && <div>
        <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{bilingualError}</p>
        <IconButton type="button" onClick={() => setBilingualError("")}>關閉這則錯誤</IconButton>
      </div>}
      {Object.values(bilingualPendingQuestions).map((failure) => (
        <div key={failure.id} style={{ borderTop: "1px solid currentColor", padding: "12px 0" }}>
          <strong>原第 {failure.number} 題</strong>
          <div style={{ overflowWrap: "anywhere" }}>題目 ID：{failure.id}</div>
          <MathText source={getQuestionPrompt(failure.question)} fallback="" />
          <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>失敗原因：{failure.error}</p>
          <IconButton type="button" onClick={() => setPreviewQuestion(failure.question)}>查看原題</IconButton>
        </div>
      ))}
      {Object.keys(bilingualPendingQuestions).length > 0 && <IconButton type="button" disabled={bilingualLoading || generating}
        onClick={handleBilingualPreview}>重試待處理題目</IconButton>}
    </Notice>
  );

  return (
    <PageShell>
      {generating && <WordExportProgressModal filename={wordExportFilename} progress={wordExportProgress}
        onCancel={cancelWordExport} cancelling={wordExportCancelling} />}
      <Header>
        <div>
          <Eyebrow>Question Bank · {subjectLabel}</Eyebrow>
          <PageTitle>
            {activeWorkspace === "exam-records" ? "考卷紀錄" : activeWorkspace === "records" ? "講義紀錄" : activeWorkspace === "short-answer" ? "簡答製作" : activeWorkspace === "handout" ? handoutWorkspaceTitle : "題庫挑題"}
          </PageTitle>
        </div>
        {activeWorkspace !== "short-answer" && activeWorkspace !== "records" && activeWorkspace !== "exam-records" && <HeaderActions>
          <CountText>{activeCountText}</CountText>
          {activeWorkspace === "handout" && activeHandoutRecordSource && !isExamHandoutTemplate && (
            <HandoutActionButton type="button" onClick={syncOpenHandoutRecord}
              title="重新載入這份紀錄，並讓題目跟題庫目前的內容同步" disabled={recordSaving || generating}>
              <FaSyncAlt />同步題庫
            </HandoutActionButton>
          )}
          <IconButton
            type="button"
            onClick={refreshActiveWorkspace}
            title={activeWorkspace === "handout" ? handoutRestorePoint
              ? "放棄目前修改，還原最近一次儲存或載入的內容"
              : "請先儲存或載入講義，再使用還原" : "重新整理題目"}
            disabled={!canRefreshActiveWorkspace}
          >
            <FaSyncAlt />
            {activeWorkspace === "handout" ? "還原已儲存" : "重新整理"}
          </IconButton>
        </HeaderActions>}
      </Header>

      {notice && (
        <Notice $tone={notice.tone} role={notice.tone === "error" ? "alert" : "status"}>
          {notice.text}
          {notice.retry && <IconButton type="button" onClick={notice.retry} disabled={generating}>重試匯出</IconButton>}
        </Notice>
      )}

      <FeatureTabs aria-label="題庫、講義、紀錄與簡答功能">
        {workspaceTabs.map((tab) => (
          <FeatureTabButton
            key={tab.value}
            type="button"
            $active={activeWorkspace === tab.value}
            aria-pressed={activeWorkspace === tab.value}
            onClick={() => setActiveWorkspace(tab.value)}
          >
            {tab.value === "picker" ? <FaListOl /> : tab.value === "short-answer" ? <FaRegEdit /> : tab.value === "records" ? <FaArchive /> : <FaBookOpen />}
            {tab.value === "handout" ? handoutWorkspaceTitle : tab.label}
          </FeatureTabButton>
        ))}
      </FeatureTabs>

      {activeWorkspace === "picker" ? (
        <>
          <PickerSteps aria-label="選題與排版步驟">
            <button type="button" aria-current={pickerStep === "select" ? "step" : undefined} onClick={() => setPickerStep("select")}><b>1</b> 選擇題目 <small>{selectedIds.length} 題已選</small></button>
            <button type="button" aria-current={pickerStep === "layout" ? "step" : undefined} disabled={!selectedIds.length} onClick={() => setPickerStep("layout")}><b>2</b> 預覽與排版 <small>確認後匯出 Word</small></button>
          </PickerSteps>
          <div hidden={pickerStep !== "select"}>
          <Toolbar>
            <QuestionCategorySelect subject={subject} value={filters.category} filter label="挑題分類" onChange={(value) => updateFilter("category", value)} />
            <SearchBox>
              <FaSearch />
              <DeferredSearchInput
                value={filters.search}
                placeholder="搜尋 UUID / 題目 / 答案 / 詳解 / 思維 / 來源"
                onCommit={(value) => updateFilter("search", value)}
              />
            </SearchBox>
            <QuestionFilterSelect
              aria-label="挑題年級" value={filters.grade_id}
              onChange={(event) => updateFilter("grade_id", event.target.value)}
            >
              <option value={filterNoneValue}>無</option>
              <option value={filterAllValue}>全部年級</option>
              {grades.map((grade) => (
                <option key={grade.id} value={grade.id}>
                  {grade.name}
                </option>
              ))}
            </QuestionFilterSelect>
            <SearchableSelect
              allowMultiple placeholderValues={["", "__none__"]} placeholder="單元"
              value={filters.unit_id}
              aria-label="挑題單元"
              onChange={(value) => updateFilter("unit_id", value)}
              options={[
                { value: filterNoneValue, label: "無" },
                { value: filterAllValue, label: "全部單元" },
                ...filteredUnits.map((unit) => ({ value: unit.id, label: unit.name })),
              ]}
            />
            <QuestionFilterSelect
              aria-label="挑題題型" value={filters.type}
              onChange={(event) => updateFilter("type", event.target.value)}
            >
              {questionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </QuestionFilterSelect>
            <QuestionFilterSelect
              aria-label="挑題難度" value={filters.difficulty}
              onChange={(event) => updateFilter("difficulty", event.target.value)}
            >
              {questionDifficulties.map((difficulty) => (
                <option key={difficulty.value} value={difficulty.value}>
                  {difficulty.label}
                </option>
              ))}
            </QuestionFilterSelect>
            <QuestionFilterSelect
              aria-label="挑題狀態" value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
            >
              {questionStatuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </QuestionFilterSelect>
            <QuestionFilterSelect
              aria-label="挑題可見性" value={filters.visibility}
              onChange={(event) => updateFilter("visibility", event.target.value)}
            >
              {questionVisibilities.map((visibility) => (
                <option key={visibility.value} value={visibility.value}>
                  {visibility.label}
                </option>
              ))}
            </QuestionFilterSelect>
            <QuestionFilterSelect
              aria-label="挑題好題" value={filters.is_good_question}
              onChange={(event) => updateFilter("is_good_question", event.target.value)}
            >
              {questionGoodStarFilters.map((filter) => (
                <option key={filter.value || "all-good-star"} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </QuestionFilterSelect>
            <SearchableSelect
              allowMultiple placeholderValues={["", "__none__"]} placeholder="來源"
              value={filters.question_source}
              aria-label="挑題來源"
              onChange={(value) => updateFilter("question_source", value)}
              options={[
                { value: "", label: "全部來源" },
                ...questionSources.map((source) => ({ value: source, label: source })),
              ]}
            />
            <IconOnlyButton
              type="button"
              title="清除篩選"
              onClick={() => setFilters(emptyFilters)}
            >
              <FaTimes />
            </IconOnlyButton>
          </Toolbar>

          <QuestionFamilyDraw subject={subject} authToken={authToken} families={questionFamilies} familyId={filters.family_id} gradeId={filters.grade_id} onFamilyChange={(value) => updateFilter("family_id", value)} excludeIds={selectedIds} onInsert={insertSelectedQuestions} onRecorded={(draw) => setFamilyDraws((current) => [...current, draw])} />
          <BulkQuestionInsert
            authToken={authToken}
            subject={subject}
            existingIds={selectedIds}
            targetLabel="已選題目"
            onInsert={insertSelectedQuestions}
          />

          </div>
          <Workspace $layout={pickerStep === "layout"}>
        {pickerStep === "select" && <QuestionPanel>
          <SelectionBar>
            <SelectionSummary>
              目前列表已選 {visibleSelectedCount} / {filteredQuestions.length} 題
              {selectedIds.length ? `，總共 ${selectedIds.length} 題` : ""}
            </SelectionSummary>
            <SelectionActions>
              <IconButton
                type="button"
                onClick={selectVisibleQuestions}
                disabled={!filteredQuestions.length || allVisibleQuestionsSelected}
              >
                <FaCheck />
                選取目前列表
              </IconButton>
              <IconButton type="button" onClick={() => setSelectedIds((current) => [...current].reverse())}
                disabled={selectedIds.length < 2 || generating || bilingualLoading}>
                <FaSortAmountDownAlt />
                全部反轉順序
              </IconButton>
              <IconButton
                type="button"
                onClick={deselectVisibleQuestions}
                disabled={!visibleSelectedCount}
              >
                <FaTimes />
                取消列表已選
              </IconButton>
              <IconButton
                type="button"
                onClick={clearSelectedQuestions}
                disabled={!selectedIds.length}
              >
                <FaTimes />
                清空所有已選
              </IconButton>
            </SelectionActions>
          </SelectionBar>
          {loading || questionLoading ? (
            <EmptyState>Loading...</EmptyState>
          ) : !shouldLoadQuestionResults ? (
            <EmptyState>請選擇條件</EmptyState>
          ) : questions.length ? (
            <QuestionResultsScroll>
              {filteredQuestions.map((question) => {
                const selected = selectedIds.includes(question.id);
                return (
                  <QuestionRow
                    key={question.id}
                    $selected={selected}
                    $flash={flashQuestionIds.includes(question.id)}
                    role="button"
                    tabIndex={0}
                    aria-label="單擊預覽題目，雙擊編輯題目"
                    aria-haspopup="dialog"
                    title="單擊預覽題目，雙擊編輯題目"
                    onClick={(event) => queueQuestionPreview(event, question)}
                    onDoubleClick={(event) => openQuestionEditorFromCard(event, question)}
                    onKeyDown={(event) => {
                      if (
                        event.target === event.currentTarget &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        setPreviewQuestion(question);
                      }
                    }}
                  >
                    <SelectButton
                      type="button"
                      $selected={selected}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleQuestion(question.id);
                      }}
                    >
                      {selected ? <FaCheck /> : null}
                      </SelectButton>
                    <QuestionContent>
                      <QuestionIdLine>
                        <QuestionUuidCopyButton buttonAs={SmallIconButton} value={getQuestionUuid(question)} onResult={handleQuestionCopyResult} />
                        {question.is_good_question && (
                          <GoodQuestionPill title="好題">
                            <FaStar />
                            好題
                          </GoodQuestionPill>
                        )}
                        <span>UUID：{shortId(getQuestionUuid(question))}</span>
                      </QuestionIdLine>
                      <QuestionTitle>
                        <MathText
                          source={getQuestionPrompt(question)}
                          fallback=""
                        />
                      </QuestionTitle>
                      <AssetPreviewStrip assets={question.assets || []} limit={4} />
                      {question.solution_md && (
                        <QuestionSolution>
                          <QuestionSolutionLabel>詳解</QuestionSolutionLabel>
                          <QuestionSolutionText>
                            <MathText
                              source={question.solution_md}
                              fallback=""
                            />
                          </QuestionSolutionText>
                        </QuestionSolution>
                      )}
                      <QuestionMeta>
                        <span>{question.grade?.name || "-"}</span>
                        <span>{question.unit?.name || "-"}</span>
                        <QuestionDifficultyBadge difficulty={question.difficulty} />
                        <StatusPill $status={question.status}>
                          {getStatusIcon(question.status)}
                          {getStatusLabel(question.status)}
                        </StatusPill>
                        <VisibilityPill $visibility={question.visibility}>
                          {question.visibility === "private" ? (
                            <FaLock />
                          ) : (
                            <FaGlobe />
                          )}
                          {getVisibilityLabel(question.visibility)}
                        </VisibilityPill>
                      </QuestionMeta>
                      {formatThinking(question.thinking) && (
                        <ThinkingLine>
                          {formatThinking(question.thinking)}
                        </ThinkingLine>
                      )}
                    </QuestionContent>
                  </QuestionRow>
                );
              })}
              {filteredQuestions.length === 0 && (
                <EmptyState>目前已載入題目中沒有符合條件的題目</EmptyState>
              )}
              <QuestionListFooter ref={sentinelRef}>
                {loadingMore ? (
                  <>
                    <Spinner aria-hidden="true" />
                    載入更多題目...
                  </>
                ) : hasMore ? (
                  "往下滑載入更多"
                ) : (
                  "已載入全部符合條件的題目"
                )}
              </QuestionListFooter>
            </QuestionResultsScroll>
          ) : (
            <EmptyState>沒有符合條件的題目</EmptyState>
          )}
        </QuestionPanel>}

        <ExportPanel>
          {pickerStep === "select" && <><PanelTitle>已選題目 · {selectedIds.length} 題</PanelTitle><HandoutHint>拖曳調整順序，完成後進入排版。</HandoutHint></>}
          {pickerStep === "layout" && <>
          <IconButton type="button" onClick={() => setPickerStep("select")}>← 返回選題</IconButton>
          <PanelTitle>
            <FaFileWord />
            輸出 Word
          </PanelTitle>
          <Field>
            <span>檔名輸入</span>
            <input
              value={outputFilename}
              onChange={(event) => setOutputFilename(event.target.value)}
            />
          </Field>
          <Field>
            <span>標題文字</span>
            <input
              aria-label="題庫選題標題"
              value={documentTitle}
              placeholder={defaultHandoutTitle}
              onChange={(event) => setDocumentTitle(event.target.value)}
            />
          </Field>
          {isPickerExamTemplate && (
            <Field>
              <span>範圍</span>
              <input
                aria-label="段考卷範圍"
                value={examRange}
                onChange={(event) => setExamRange(event.target.value)}
              />
            </Field>
          )}
          <Field>
            <span>模板</span>
            <select
              value={templateId}
              onChange={(event) => selectPickerTemplate(event.target.value)}
            >
              {fallbackTemplates.filter((template) => pickerTemplateIds.has(template.id)).map((fallback) => (
                <option key={fallback.id} value={fallback.id}>
                  {templateMetadataById.get(fallback.id)?.name || fallback.name}
                </option>
              ))}
            </select>
          </Field>
          {isBilingualExamTemplate(templateId) && (
            <HandoutHint>{templateId === "bilingual_chinese_first_exam_paper"
              ? "先翻譯並預覽：原題中文在上、英文在下；下載 Word 會沿用預覽的英文翻譯。"
              : "先翻譯並預覽：英文在上、原題中文在下；下載 Word 會沿用預覽的英文翻譯。"}</HandoutHint>
          )}
          <Field>
            <span>版本</span>
            <select
              value={outputMode === "teaching" && !pickerExamTemplateIds.has(templateId) ? "teaching_detailed" : outputMode}
              onChange={(event) => setOutputMode(event.target.value)}
            >
              {(pickerExamTemplateIds.has(templateId) ? examOutputModes : outputModes).map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </Field>
          <HandoutHint>{outputModeHint(outputMode)}</HandoutHint>
          <Field>
            <span>分組方式</span>
            <select
              value={sectionMode}
              onChange={(event) => setSectionMode(event.target.value)}
            >
              {sectionModes.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </Field>
          </>}
          <SelectedList>
            {selectedQuestions.length ? (
              selectedQuestions.map((question, index) => (
                <SelectedItem
                  key={question.id}
                  role="group"
                  aria-label={`已選第 ${index + 1} 題 ${getQuestionUuid(question)}`}
                  title="單擊預覽題目，雙擊編輯題目"
                  draggable
                  $dragging={draggedSelectedId === question.id}
                  $dragOver={dragOverSelectedId === question.id}
                  onDragStart={(event) => handleSelectedDragStart(event, question.id)}
                  onDragOver={(event) => handleSelectedDragOver(event, question.id)}
                  onDrop={(event) => handleSelectedDrop(event, question.id)}
                  onDragEnd={handleSelectedDragEnd}
                  onClick={(event) => queueQuestionPreview(event, question)}
                  onDoubleClick={(event) => openQuestionEditorFromCard(event, question)}
                >
                  <SelectedCardHeader>
                    <DragHandle title="拖曳調整順序">
                      <FaGripVertical />
                    </DragHandle>
                    <SelectedIndex>{index + 1}.</SelectedIndex>
                    <QuestionUuidCopyButton buttonAs={SelectedUuidButton} value={getQuestionUuid(question)} label={shortId(getQuestionUuid(question))} onResult={handleQuestionCopyResult} />
                    <SelectedRemoveButton
                      type="button"
                      title="移除"
                      aria-label={`移除第 ${index + 1} 題`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleQuestion(question.id);
                      }}
                    >
                      <FaTimes />
                    </SelectedRemoveButton>
                  </SelectedCardHeader>
                  <SelectedPrompt>
                    <MarkdownMathText source={getQuestionPrompt(question)} />
                  </SelectedPrompt>
                  <AssetPreviewStrip
                    assets={question.assets || []}
                    compact
                    limit={3}
                  />
                  {authToken && (
                    <SelectedCardFooter>{familyActionButton(question)}</SelectedCardFooter>
                  )}
                </SelectedItem>
              ))
            ) : (
              <EmptyState>尚未選題</EmptyState>
            )}
          </SelectedList>
          {pickerStep === "select" ? <IconButton type="button" $primary disabled={!selectedIds.length} onClick={() => setPickerStep("layout")}>下一步：預覽與排版 →</IconButton> : <>
          <IconButton
            type="button"
            onClick={isBilingualExamTemplate(templateId) ? handleBilingualPreview : openPreviewDialog}
            disabled={previewPreparing || generating || bilingualLoading || !selectedIds.length}
          >
            <FaFileWord />
            {bilingualLoading ? "英文翻譯中..." : previewPreparing ? "產生預覽圖..." : isBilingualExamTemplate(templateId) ? "翻譯並預覽" : "A4 快速預覽"}
          </IconButton>
          {isBilingualExamTemplate(templateId) && !hasBilingualPreview && <HandoutHint>請先翻譯並預覽，再產生 Word。</HandoutHint>}
          <IconButton
            type="button"
            $primary
            disabled={previewPreparing || generating || bilingualLoading || !selectedIds.length || (isBilingualExamTemplate(templateId) && !hasBilingualPreview)}
            onClick={handleGenerate}
          >
            <FaDownload />
            {generating ? "產生中..." : "產生並下載 Word"}
          </IconButton>
          <IconButton type="button" onClick={() => saveHandoutRecord("picker")} disabled={recordSaving || generating || !selectedIds.length}>
            <FaSave />{recordSaving ? "儲存中..." : pickerExamTemplateIds.has(templateId) ? "儲存考卷紀錄" : "儲存講義紀錄"}
          </IconButton>
          </>}
        </ExportPanel>
          {pickerStep === "layout" && <PickerA4PreviewBlock aria-label="排版預覽工作區">
            <PreviewPanelTitle>A4 預覽</PreviewPanelTitle>
            {bilingualIssuesPanel}
            {isBilingualExamTemplate(templateId) && !hasBilingualPreview && <HandoutHint>目前顯示原題；請按「翻譯並預覽」產生完整雙語版本。所有已選題目會保留。</HandoutHint>}
            {renderPickerA4Preview()}
          </PickerA4PreviewBlock>}
          </Workspace>
        </>
      ) : activeWorkspace === "handout" ? (
        <HandoutBuilderGrid>
          <HandoutWorkspace ref={handoutWorkspaceRef}>
            <HandoutTopBar>
              <HandoutTopHeading>
                <PanelTitle>
                  <FaBookOpen />
                  {isExamHandoutTemplate ? "檢測卷模板" : "講義重點"}
                </PanelTitle>
                <HandoutSummaryLine>
                  {selectedHandoutTemplate.label} · {handoutDisplayTitle || `未命名${handoutWorkspaceLabel}`} ·
                  {handoutSections.filter((section) => section.type !== "image").length} 個區塊 · {handoutTotalQuestionCount} 題
                </HandoutSummaryLine>
              </HandoutTopHeading>
              <HandoutTopControls>
                <HandoutTemplatePicker ref={handoutTemplatePickerRef}>
                  <span>{handoutWorkspaceLabel}模板</span>
                  <HandoutTemplateButton
                    type="button"
                    onClick={() => setShowHandoutTemplateMenu((current) => !current)}
                    aria-expanded={showHandoutTemplateMenu}
                  >
                    <TemplateMark $variant={selectedHandoutTemplate.icon}>
                      {selectedHandoutTemplate.icon === "exam" ? (
                        <FaClipboardList />
                      ) : (
                        <FaBookOpen />
                      )}
                    </TemplateMark>
                    <TemplateButtonText>
                      <strong>{selectedHandoutTemplate.label}</strong>
                      <small>{selectedHandoutTemplate.title}</small>
                    </TemplateButtonText>
                    <FaChevronDown />
                  </HandoutTemplateButton>
                  {showHandoutTemplateMenu && (
                    <HandoutTemplateMenu>
                      {availableHandoutTemplates.map((template) => (
                        <HandoutTemplateMenuItem
                          key={template.id}
                          type="button"
                          $active={template.id === selectedHandoutTemplate.id}
                          onClick={() => selectHandoutTemplate(template.id)}
                        >
                          <TemplateMark $variant={template.icon}>
                            {template.icon === "exam" ? <FaClipboardList /> : <FaBookOpen />}
                          </TemplateMark>
                          <TemplateButtonText>
                            <strong>{template.label}</strong>
                            <small>{template.description}</small>
                          </TemplateButtonText>
                          {template.id === selectedHandoutTemplate.id && <FaCheck />}
                        </HandoutTemplateMenuItem>
                      ))}
                    </HandoutTemplateMenu>
                  )}
                </HandoutTemplatePicker>
                {!isExamHandoutTemplate && <HandoutControlField>
                  <span>講義版型</span>
                  <select aria-label="講義版面樣式" value={handoutOptions.layoutPreset || ""}
                    onChange={(event) => selectHandoutLayoutPreset(event.target.value)}>
                    {[HANDOUT_WORD_PRESET, HANDOUT_WORD_NARROW_PRESET, ""].map((preset) => (
                      <option key={preset || "simple"} value={preset}>{getHandoutLayoutPresetLabel(preset)}</option>
                    ))}
                  </select>
                </HandoutControlField>}
                <HandoutControlField $span={2}>
                  <span>{isExamHandoutTemplate ? "檢測卷標題" : "講義標題"}</span>
                  <input
                    value={handoutTitle}
                    onChange={(event) => setHandoutTitle(event.target.value)}
                  />
                </HandoutControlField>
                <HandoutControlField $span={2}>
                  <span>檔名</span>
                  <input
                    value={handoutFilename}
                    onChange={(event) => setHandoutFilename(event.target.value)}
                  />
                </HandoutControlField>
                <HandoutControlField>
                  <span>版本</span>
                  <select
                    value={handoutOutputMode === "teaching" && !isExamHandoutTemplate ? "teaching_detailed" : handoutOutputMode}
                    onChange={(event) => setHandoutOutputMode(event.target.value)}
                  >
                    {(isExamHandoutTemplate ? examOutputModes : outputModes).map((mode) => (
                      <option key={mode.value} value={mode.value}>
                        {mode.label}
                      </option>
                    ))}
                  </select>
                </HandoutControlField>
              </HandoutTopControls>
              <HandoutHint>{outputModeHint(handoutOutputMode)}</HandoutHint>
              <HandoutActionBar>
                <HandoutExportActions>
                  <HandoutActionButton
                    type="button"
                    onClick={prepareHandoutPreviewImages}
                    disabled={previewPreparing || !handoutTotalQuestionCount}
                  >
                    <FaImage />
                    {previewPreparing ? "整理中..." : "整理原圖"}
                  </HandoutActionButton>
                  <HandoutActionButton type="button" onClick={() => saveHandoutRecord("handout")} disabled={recordSaving || generating || !canExportHandout || Boolean(backgroundRemovalTarget || backgroundRemovalPreparing || focusPythonTarget)}>
                    <FaSave />{recordSaving ? "儲存中..." : isExamHandoutTemplate ? "儲存考卷紀錄" : "儲存講義紀錄"}
                  </HandoutActionButton>
                  {activeHandoutRecordSource && !isExamHandoutTemplate && <HandoutActionButton type="button"
                    onClick={() => saveHandoutRecord("handout", { asNew: true })}
                    disabled={recordSaving || generating || !canExportHandout || Boolean(backgroundRemovalTarget || backgroundRemovalPreparing || focusPythonTarget)}>
                    <FaCopy />另存新講義
                  </HandoutActionButton>}
                  <HandoutActionButton
                    type="button"
                    onClick={() => setShowHandoutSettingsDialog(true)}
                  >
                    <FaSlidersH />
                    細節設定
                  </HandoutActionButton>
                  <HandoutActionButton
                    type="button"
                    $primary
                    disabled={previewPreparing || recordSaving || generating || !canExportHandout}
                    onClick={handleHandoutGenerate}
                  >
                    <FaDownload />
                    {generating
                      ? "產生中..."
                      : isExamHandoutTemplate
                        ? "產生檢測卷 Word"
                        : "產生講義 Word"}
                  </HandoutActionButton>
                </HandoutExportActions>
              </HandoutActionBar>
            </HandoutTopBar>

            {recordSaving && (
              <HandoutBusyIndicator role="status" aria-live="polite" title="儲存中">
                <Spinner aria-hidden="true" />
                <span>儲存中</span>
              </HandoutBusyIndicator>
            )}

            <HandoutMainGrid>
              <HandoutPanel>
                <HandoutOutlineHeader>
                  <PanelTitle><FaListOl />{handoutOutlineTitle}</PanelTitle>
                  {!isExamHandoutTemplate && (
                    <HandoutOutlineVisibilityButton
                      type="button"
                      aria-label={showHandoutOutlineImages ? "隱藏列表圖片物件" : "顯示列表圖片物件"}
                      title={showHandoutOutlineImages ? "隱藏列表圖片物件" : "顯示列表圖片物件"}
                      aria-pressed={showHandoutOutlineImages}
                      onClick={() => {
                        setShowHandoutOutlineImages((visible) => !visible);
                        setHandoutOutlineInsertTarget(null);
                      }}
                    >
                      <FaImage />{showHandoutOutlineImages ? <FaEye /> : <FaEyeSlash />}
                    </HandoutOutlineVisibilityButton>
                  )}
                </HandoutOutlineHeader>
                <HandoutOutlineList>
                  {visibleHandoutSections.map((section) => {
                    const sectionLabel = (isExamHandoutTemplate ? section.title : withoutHandoutScores(section.title)) || (section.type === "questions" ? "題目區塊" : "未命名區塊");
                    const active = activeHandoutSection?.id === section.id;
                    const questionCount = section.questions?.length || 0;
                    const metaText =
                      section.type === "questions"
                        ? `${questionCount} 題`
                        : section.type === "focus"
                          ? `${splitHandoutLines(getHandoutFocusLines(section, handoutOptions)).length} 個重點`
                          : section.type === "image"
                            ? `圖片 · ${section.widthCm || 8} 公分`
                            : section.type === "page_break"
                              ? handoutBreakPageLabel(section, handoutBreakPages[section.id])
                              : section.type === "section_break"
                                ? handoutBreakPageLabel(section, handoutBreakPages[section.id])
                                : section.type === "exam_info"
                                  ? "首頁說明"
                                  : `${handoutSections.length} 個區塊`;
                    return (
                      <HandoutOutlineItem
                        key={section.id}
                        $break={section.type === "page_break" || section.type === "section_break"}
                        data-outline-kind={section.type}
                        $active={active}
                        $drop={dragOverHandoutSectionId === section.id}
                        draggable={!isExamHandoutTemplate && section.type !== "toc"}
                        $dragging={draggedHandoutOutlineSectionId === section.id}
                        $insertBefore={handoutOutlineInsertTarget?.sectionId === section.id && !handoutOutlineInsertTarget.afterTarget}
                        $insertAfter={handoutOutlineInsertTarget?.sectionId === section.id && handoutOutlineInsertTarget.afterTarget}
                        data-handout-outline-section={section.id}
                        data-insert-position={handoutOutlineInsertTarget?.sectionId === section.id ? (handoutOutlineInsertTarget.afterTarget ? "after" : "before") : undefined}
                        onDragStart={(event) => {
                          if (!isExamHandoutTemplate && section.type !== "toc") {
                            handleHandoutOutlineDragStart(event, section.id);
                          }
                        }}
                        onDragEnd={() => {
                          setDraggedHandoutOutlineSectionId("");
                          setHandoutOutlineInsertTarget(null);
                          setDragOverHandoutSectionId("");
                        }}
                        onDragOver={(event) => handleHandoutOutlineDragOver(event, section.id)}
                        onDrop={(event) => handleHandoutOutlineDrop(event, section.id)}
                        onDragLeave={(event) => {
                          if (
                            (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) ||
                            isPointerInsideElement(event)
                          ) return;
                          setHandoutOutlineInsertTarget(null);
                          setDragOverHandoutSectionId("");
                          setHandoutInsertTarget(null);
                        }}
                      >
                        {!isExamHandoutTemplate && section.type !== "toc" && (
                          <HandoutOutlineGrip
                            role="button"
                            tabIndex={0}
                            aria-label={`拖曳排序 ${sectionLabel}`}
                            title="拖曳排序；鍵盤可用上下鍵調整"
                            onKeyDown={(event) => {
                              if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                              const currentIndex = visibleHandoutSections.findIndex((item) => item.id === section.id);
                              const targetIndex = currentIndex + (event.key === "ArrowUp" ? -1 : 1);
                              const target = visibleHandoutSections[targetIndex];
                              if (!target || target.type === "toc") return;
                              event.preventDefault();
                              moveHandoutOutlineSection(
                                section.id,
                                target.id,
                                event.key === "ArrowDown",
                              );
                            }}
                          >
                            <FaGripVertical />
                          </HandoutOutlineGrip>
                        )}
                        <HandoutOutlineSelect
                          type="button"
                          onClick={() => switchHandoutOutlineSectionWithFeedback(section.id)}
                        >
                          <strong>{sectionLabel}</strong>
                          <span title={["page_break", "section_break"].includes(section.type) ? `編輯預覽估算：${metaText}；實際分頁以 Word 為準。` : undefined}>{metaText}</span>
                        </HandoutOutlineSelect>
                        {!isExamHandoutTemplate &&
                          (section.type === "questions" || section.type === "focus" ||
                            section.type === "page_break" || section.type === "section_break" ||
                            section.type === "image") && (
                            <PendingHandoutOutlineDelete
                              data-outline-delete="true"
                              type="button"
                              aria-label={`刪除 ${sectionLabel}`}
                              title={`刪除 ${sectionLabel}`}
                              onClick={() => {
                                if (section.type !== "questions") {
                                  deleteHandoutFocusSection(section.id);
                                } else {
                                  deleteHandoutQuestionSection(section.id);
                                }
                              }}
                            >
                              <FaTimes />
                            </PendingHandoutOutlineDelete>
                          )}
                      </HandoutOutlineItem>
                    );
                  })}
                </HandoutOutlineList>
                {!isExamHandoutTemplate && (
                  <HandoutOutlineActions role="group" aria-label="新增講義區塊">
                    <HandoutActionButton type="button" onClick={addHandoutFocusSection}>
                      <FaPlus />
                      新增重點整理
                    </HandoutActionButton>
                    <HandoutActionButton type="button" onClick={addHandoutQuestionSection}>
                      <FaPlus />
                      新增題型區塊
                    </HandoutActionButton>
                    <HandoutActionButton type="button" onClick={() => addHandoutBreak("page_break")}>
                      <FaPlus />
                      新增分頁符號
                    </HandoutActionButton>
                    <HandoutActionButton type="button" onClick={() => addHandoutBreak("section_break")}>
                      <FaPlus />
                      新增分節符號
                    </HandoutActionButton>
                  </HandoutOutlineActions>
                )}
              </HandoutPanel>

              <HandoutPanel ref={handoutContentPanelRef} tabIndex={-1} aria-label="區塊內容">
                <PanelTitle>
                  <FaRegEdit />
                  區塊內容
                </PanelTitle>
                {activeHandoutSection?.type === "toc" ? (
                  <SectionStateCard>
                    <Field>
                      <span>區塊名稱</span>
                      <input
                        value={activeHandoutSection.title}
                        onChange={(event) =>
                          updateHandoutSectionTitle(activeHandoutSection.id, event.target.value)
                        }
                      />
                    </Field>
                    <TocPreviewList>
                      {handoutSections
                        .filter((section) => section.id !== activeHandoutSection.id &&
                          section.type !== "page_break" && section.type !== "section_break" &&
                          section.type !== "image")
                        .map((section, index) => (
                          <li key={section.id}>
                            <strong>{index + 1}. {section.title}</strong>
                            <span>
                              {section.type === "questions"
                                ? `${section.questions.length} 題`
                                : section.type === "focus"
                                  ? `${splitHandoutLines(getHandoutFocusLines(section, handoutOptions)).length} 個重點`
                                  : ""}
                            </span>
                          </li>
                        ))}
                    </TocPreviewList>
                  </SectionStateCard>
                ) : activeHandoutSection?.type === "page_break" ||
                  activeHandoutSection?.type === "section_break" ? (
                  <SectionStateCard>
                    <strong>{activeHandoutSection.title}</strong>
                    <span>{activeHandoutSection.type === "page_break"
                      ? "Word 會從下一頁繼續內容。"
                      : "Word 會從下一頁開始新節，可獨立設定該節的版面。"}</span>
                    <span>可在左側拖曳或用方向鍵調整位置。</span>
                    <HandoutActionButton type="button" onClick={() => deleteHandoutFocusSection(activeHandoutSection.id)}>
                      <FaTimes />刪除{activeHandoutSection.title}
                    </HandoutActionButton>
                  </SectionStateCard>
                ) : activeHandoutSection?.type === "image" ? (
                  <SectionStateCard>
                    <strong>{activeHandoutSection.title}</strong>
                    <img src={activeHandoutSection.asset?.url} alt={activeHandoutSection.title} style={{ maxWidth: "100%", maxHeight: 220, objectFit: "contain" }} />
                    <Field>
                      <span>圖片寬度（公分）</span>
                      <HandoutNumberInput key={`${activeHandoutSection.id}-width`} min={2} max={16} step="0.5" emptyValue={8}
                        value={activeHandoutSection.widthCm || 8}
                        onChange={(value) => updateHandoutImageWidth(activeHandoutSection.id, value)} />
                    </Field>
                    <Field>
                      <span>圖片對齊</span>
                      <select aria-label="圖片對齊" value={activeHandoutSection.alignment || "center"}
                        onChange={(event) => updateHandoutImageOption(activeHandoutSection.id, "alignment", event.target.value)}>
                        <option value="left">靠左</option>
                        <option value="center">置中</option>
                        <option value="right">靠右</option>
                      </select>
                    </Field>
                    <Field>
                      <span>後續文字位置</span>
                      <select aria-label="後續文字位置" value={activeHandoutSection.textLayout || "below"}
                        onChange={(event) => updateHandoutImageOption(activeHandoutSection.id, "textLayout", event.target.value)}>
                        <option value="below">圖片下方繼續</option>
                        <option value="inline">圖片同一行接文字</option>
                      </select>
                    </Field>
                    <SettingGrid>
                      <Field>
                        <span>後續文字大小（點）</span>
                        <HandoutNumberInput key={`${activeHandoutSection.id}-font`} min={6} max={72} step="1" emptyValue={null}
                          value={activeHandoutSection.textFontSize ?? handoutOptions.bodyFontSize}
                          onChange={(value) => updateHandoutImageOption(activeHandoutSection.id, "textFontSize", value)} />
                      </Field>
                      <Field>
                        <span>後續文字上下位移（點）</span>
                        <HandoutNumberInput key={`${activeHandoutSection.id}-offset`} aria-label="後續文字上下位移（點）" min={-72} max={72} step="1" emptyValue={0}
                          value={activeHandoutSection.textOffsetPt ?? 0}
                          onChange={(value) => updateHandoutImageOption(activeHandoutSection.id, "textOffsetPt", value)} />
                        <small>正值上移，負值下移</small>
                      </Field>
                    </SettingGrid>
                    <Field>
                      <span>素材後續文字</span>
                      <textarea aria-label="素材後續文字" rows={6} maxLength={20000}
                        placeholder="可持續輸入文字、換行或數學公式"
                        value={activeHandoutSection.text || ""}
                        onChange={(event) => updateHandoutImageOption(activeHandoutSection.id, "text", event.target.value)} />
                    </Field>
                    <HeaderActions>
                      <HandoutActionButton type="button" disabled={Boolean(backgroundRemovalPreparing) || generating}
                        aria-label="去背景色" onClick={() => openImageBackgroundRemoval(activeHandoutSection.id, null, activeHandoutSection)}>
                        {backgroundRemovalPreparing === `${activeHandoutSection.id}:image` ? <LoadingText>準備圖片</LoadingText> : "去背景色"}
                      </HandoutActionButton>
                      {activeHandoutSection.backgroundRemoval && <HandoutActionButton type="button"
                        onClick={() => replaceHandoutImage(activeHandoutSection.id, null, restoreImageBackground(activeHandoutSection))}>還原原圖</HandoutActionButton>}
                    </HeaderActions>
                    <Field>
                      <span>移入觀念區塊</span>
                      <select aria-label="移入觀念區塊" value=""
                        onChange={(event) => event.target.value && moveHandoutImageIntoFocus(activeHandoutSection.id, event.target.value)}>
                        <option value="">選擇觀念，將此圖放入其中</option>
                        {handoutSections.filter((section) => section.type === "focus").map((section) =>
                          <option key={section.id} value={section.id}>{section.title || "重點整理"}</option>)}
                      </select>
                      <small>保留原圖與附帶文字，可在觀念內調整文繞圖。</small>
                    </Field>
                    <HandoutActionButton type="button" onClick={() => deleteHandoutFocusSection(activeHandoutSection.id)}>
                      <FaTimes />刪除圖片
                    </HandoutActionButton>
                  </SectionStateCard>
                ) : activeHandoutSection?.type === "exam_info" ? (
                  <SectionStateCard>
                    <Field>
                      <span>區塊名稱</span>
                      <input
                        value={activeHandoutSection.title}
                        onChange={(event) =>
                          updateHandoutSectionTitle(activeHandoutSection.id, event.target.value)
                        }
                      />
                    </Field>
                    {renderExamTemplateFileActions(["instructions"])}
                    {renderExamSettingsFields()}
                  </SectionStateCard>
                ) : activeHandoutSection?.type === "focus" ? (
                  <HandoutFocusEditor role="region" aria-label="觀念區塊編輯">
                    {!isExamHandoutTemplate && !isHandoutWordLayout(handoutOptions) && <Field as="div">
                      <span>觀念底樣式</span>
                      <small>啟用灰底／框線會套用寰宇講義的單元、重點與題型版式；文字和圖片會保留。</small>
                      <HandoutActionButton type="button" onClick={() => selectHandoutLayoutPreset(HANDOUT_WORD_PRESET)}>啟用灰底／框線</HandoutActionButton>
                    </Field>}
                    {!isExamHandoutTemplate && isHandoutWordLayout(handoutOptions) && <HandoutFocusBackgroundPicker
                      value={getHandoutFocusBackgroundStyle(activeHandoutSection, handoutOptions)}
                      onChange={(focusBackgroundStyle) => setHandoutSections((current) => current.map((section) =>
                        section.id === activeHandoutSection.id ? { ...section, focusBackgroundStyle } : section))} />}
                    {isHandoutWordLayout(handoutOptions) && <>
                      <Field><span>單元標題圖</span><span><input type="checkbox" aria-label="顯示單元標題圖" checked={Boolean(activeHandoutSection.chapter)}
                        onChange={(event) => setHandoutSections((current) => current.map((section) => section.id === activeHandoutSection.id
                          ? { ...section, chapter: event.target.checked ? { number: "1", title: getHandoutFocusCaption(section.title) || "單元名稱" } : undefined } : section))} />在此重點前顯示單元標題</span></Field>
                      <Field><span>接續觀念</span><span><input type="checkbox" aria-label="接續前段觀念，不重複標題" checked={activeHandoutSection.continuePreviousFocus === true}
                        onChange={(event) => setHandoutSections((current) => current.map((section) => section.id === activeHandoutSection.id
                          ? { ...section, continuePreviousFocus: event.target.checked } : section))} />接續前段，不重複顯示重點整理標題</span></Field>
                      {activeHandoutSection.chapter && <SettingGrid>
                        <Field><span>單元編號</span><input aria-label="單元編號" value={activeHandoutSection.chapter.number || ""}
                          onChange={(event) => updateHandoutSectionDecoration(activeHandoutSection.id, "chapter", { number: event.target.value })} /></Field>
                        <Field><span>單元名稱</span><input aria-label="單元名稱" value={activeHandoutSection.chapter.title || ""}
                          onChange={(event) => updateHandoutSectionDecoration(activeHandoutSection.id, "chapter", { title: event.target.value })} /></Field>
                        <Field><span>單元頁首</span><input aria-label="單元頁首" value={activeHandoutSection.chapter.headerTitle ?? activeHandoutSection.chapter.title ?? ""}
                          onChange={(event) => updateHandoutSectionDecoration(activeHandoutSection.id, "chapter", { headerTitle: event.target.value })} />
                          <small>顯示在本單元每頁上方；留白可隱藏。</small></Field>
                      </SettingGrid>}
                    </>}
                    {!isHandoutWordLayout(handoutOptions) && <Field>
                      <span>單元標題</span>
                      <input
                        value={handoutOptions.focus.unitTitle}
                        onChange={(event) =>
                          updateHandoutNestedOption("focus", "unitTitle", event.target.value)
                        }
                      />
                    </Field>}
                    <SettingGrid>
                      <Field>
                        <span>重點標題</span>
                        <input
                          value={activeHandoutSection.title}
                          onChange={(event) => {
                            updateHandoutSectionTitle(activeHandoutSection.id, event.target.value);
                            if (activeHandoutSection.id === "focus") {
                              updateHandoutNestedOption("focus", "title", event.target.value);
                            }
                          }}
                        />
                      </Field>
                      <Field>
                        <span>題型預設標題</span>
                        <input
                          value={handoutOptions.focus.typeTitle}
                          onChange={(event) =>
                            updateHandoutNestedOption("focus", "typeTitle", event.target.value)
                          }
                        />
                      </Field>
                    </SettingGrid>
                    <Field as="div">
                      <span>重點整理內容</span>
                      <TextareaWithVim>
                        <textarea
                          aria-label="重點整理內容"
                          value={getHandoutFocusLines(activeHandoutSection, handoutOptions)}
                          onChange={(event) =>
                            updateHandoutFocusLines(activeHandoutSection.id, event.target.value)
                          }
                        />
                        <PendingVimFieldButton type="button" aria-label="Vim 編輯重點整理內容" onClick={() => setHandoutVimTarget({ kind: "focus", sectionId: activeHandoutSection.id })}>Vim 編輯</PendingVimFieldButton>
                      </TextareaWithVim>
                    </Field>
                    <Field as="div">
                      <span>觀念附圖</span>
                      <HandoutFocusImagePicker files={draggableHandoutImages}
                        disabled={generating || Boolean(focusPythonTarget)}
                        onPickAsset={(asset) => addHandoutFocusImage(activeHandoutSection.id, asset)}
                        onAddUrl={(url) => addHandoutFocusImage(activeHandoutSection.id, {
                          url, name: decodeURIComponent(url.split(/[?#]/)[0].split("/").pop() || "") || "網址圖片",
                        })}
                        onAddCode={() => setFocusPythonTarget({ sectionId: activeHandoutSection.id, isNew: true,
                          image: createHandoutFocusImage({ name: "Python 圖", source_code: "", source_language: "python", mime_type: "image/png" }) })} />
                      <small>可指定附圖對應的觀念；文繞圖放在文字旁，段落圖放在該觀念下方。</small>
                    </Field>
                    {getHandoutFocusImages(activeHandoutSection).map((image, index) => <FocusImageEditor key={image.id} role="group" aria-label={`觀念附圖 ${index + 1}`}>
                      <strong>{image.asset.name || image.asset.alt_text || `附圖 ${index + 1}`}</strong>
                      <QuestionAssetPreviewStrip assets={[image.asset]} showCaption={false} compact lazyRender />
                      <SettingGrid>
                        <Field>
                          <span>寬度（公分）</span>
                          <HandoutNumberInput aria-label={`觀念附圖 ${index + 1} 寬度`} min={2} max={16} step="0.5" emptyValue={4}
                            value={image.widthCm} onChange={(value) => updateHandoutFocusImage(activeHandoutSection.id, image.id, "widthCm", value)} />
                        </Field>
                        <Field>
                          <span>圖片對齊</span>
                          <select aria-label={`觀念附圖 ${index + 1} 對齊`} value={image.alignment}
                            onChange={(event) => updateHandoutFocusImage(activeHandoutSection.id, image.id, "alignment", event.target.value)}>
                            <option value="right">靠右</option><option value="center">置中</option><option value="left">靠左</option>
                          </select>
                        </Field>
                        {getHandoutFocusSubsections(getHandoutFocusLines(activeHandoutSection, handoutOptions)).length > 1 && <Field>
                          <span>放置觀念</span>
                          <select aria-label={`觀念附圖 ${index + 1} 放置觀念`} value={image.anchorSection ?? ""}
                            onChange={(event) => updateHandoutFocusImage(activeHandoutSection.id, image.id, "anchorSection", event.target.value === "" ? undefined : Number(event.target.value))}>
                            <option value="">整個區塊</option>
                            {getHandoutFocusSubsections(getHandoutFocusLines(activeHandoutSection, handoutOptions)).map((part, partIndex) =>
                              <option key={partIndex} value={partIndex}>{part.title}</option>)}
                          </select>
                        </Field>}
                        <Field>
                          <span>排列方式</span>
                          <select aria-label={`觀念附圖 ${index + 1} 排列`} value={image.layout}
                            onChange={(event) => updateHandoutFocusImage(activeHandoutSection.id, image.id, "layout", event.target.value)}>
                            <option value="float">文繞圖</option><option value="block">段落圖</option>
                          </select>
                        </Field>
                      </SettingGrid>
                      <HeaderActions>
                        {getHandoutImagePythonCode(image) && <HandoutActionButton type="button" aria-label={`觀念附圖 ${index + 1} Python 圖碼`}
                          disabled={Boolean(backgroundRemovalPreparing) || generating}
                          onClick={() => setFocusPythonTarget({ sectionId: activeHandoutSection.id, image })}>Python 圖碼</HandoutActionButton>}
                        <HandoutActionButton type="button" aria-label={`觀念附圖 ${index + 1} 去背景色`}
                          disabled={Boolean(backgroundRemovalPreparing) || generating}
                          onClick={() => openImageBackgroundRemoval(activeHandoutSection.id, image.id, image)}>
                          {backgroundRemovalPreparing === `${activeHandoutSection.id}:${image.id}` ? <LoadingText>準備圖片</LoadingText> : "去背景色"}
                        </HandoutActionButton>
                        {image.backgroundRemoval && <HandoutActionButton type="button" aria-label={`觀念附圖 ${index + 1} 還原原圖`}
                          onClick={() => replaceHandoutImage(activeHandoutSection.id, image.id, restoreImageBackground(image))}>還原原圖</HandoutActionButton>}
                      </HeaderActions>
                      <HandoutActionButton type="button" aria-label={`移除觀念附圖 ${index + 1}`}
                        onClick={() => removeHandoutFocusImage(activeHandoutSection.id, image.id)}><FaTimes />移除附圖</HandoutActionButton>
                    </FocusImageEditor>)}
                    <HandoutActionButton
                      type="button"
                      onClick={() => deleteHandoutFocusSection(activeHandoutSection.id)}
                    >
                      <FaTimes />
                      刪除重點整理
                    </HandoutActionButton>
                  </HandoutFocusEditor>
                ) : (
                  <SectionStateCard>
                    {isHandoutWordLayout(handoutOptions) && <SettingGrid>
                      <Field><span>標題用途</span><select aria-label="區塊標題用途" value={activeHandoutSection?.heading?.role || "plain"}
                        onChange={(event) => updateHandoutSectionDecoration(activeHandoutSection.id, "heading", { role: event.target.value })}>
                        <option value="plain">一般標題</option><option value="type">題型</option><option value="practice">類題</option>
                      </select></Field>
                      {activeHandoutSection?.heading?.role === "type" && <Field><span>題型編號</span>
                        <HandoutNumberInput aria-label="題型編號" min={1} max={20} step="1" emptyValue={1}
                          value={activeHandoutSection.heading.number || 1}
                          onChange={(number) => updateHandoutSectionDecoration(activeHandoutSection.id, "heading", { number })} />
                      </Field>}
                    </SettingGrid>}
                    <Field>
                      <span>{isExamHandoutTemplate ? "檢測題目標題" : "區塊名稱"}</span>
                      <input
                        value={activeHandoutSection?.title || ""}
                        onChange={(event) =>
                          isExamHandoutTemplate
                            ? updateExamPartTitle(activeHandoutSection.id, event.target.value)
                            : updateHandoutSectionTitle(
                              activeHandoutSection.id,
                              event.target.value,
                            )
                        }
                      />
                    </Field>
                    {!isExamHandoutTemplate && (
                      <Field>
                        <span>起始題號</span>
                        <input
                          type="number"
                          aria-label="起始題號"
                          min="1"
                          max="9999"
                          step="1"
                          placeholder="自動接續"
                          value={activeHandoutSection?.startNumber ?? ""}
                          onChange={(event) => {
                            const startNumber = event.target.value;
                            setHandoutSections((sections) => sections.map((section) =>
                              section.id === activeHandoutSection.id
                                ? { ...section, startNumber }
                                : section,
                            ));
                          }}
                        />
                        <small>填 1 從頭編號；留空接續前一區塊。</small>
                      </Field>
                    )}
                    {isExamHandoutTemplate && renderExamTemplateFileActions(["questions"])}
                    <HandoutDropZone
                      ref={handoutContentListRef}
                      style={{ "--handout-two-card-height": `${handoutContentListHeight}px` }}
                      role="region"
                      aria-label="區塊題目清單"
                      $active={dragOverHandoutSectionId === activeHandoutSection?.id}
                      onDragOver={(event) =>
                        handleHandoutDropZoneDragOver(event, activeHandoutSection)
                      }
                      onDrop={(event) =>
                        handleHandoutDropZoneDrop(event, activeHandoutSection)
                      }
                      onDragLeave={handleHandoutDragLeave}
                    >
                      {activeHandoutSection?.questions?.length ? (
                        <>
                          {activeHandoutSection.questions.map((question, index) => (
                            <React.Fragment key={getHandoutQuestionOccurrenceKey(activeHandoutSection.questions, index)}>
                              {!isExamHandoutTemplate && (
                                <HandoutQuestionBreakButton
                                  type="button"
                                  $active={Boolean(question.pageBreakBefore)}
                                  aria-label={`${question.pageBreakBefore ? "移除" : "新增"}第 ${index + 1} 題前的分頁符號`}
                                  onClick={() => toggleQuestionPageBreak(activeHandoutSection.id, question.id)}
                                >
                                  {question.pageBreakBefore ? "— 分頁符號：下一頁開始 —" : `＋ 在第 ${index + 1} 題前分頁`}
                                </HandoutQuestionBreakButton>
                              )}
                              <HandoutSectionQuestionCard
                                data-handout-question-card="true"
                                data-handout-editor-question={question.id}
                                tabIndex={-1}
                                role="group"
                                aria-label={`講義第 ${getHandoutQuestionNumber(handoutQuestionIndexById, activeHandoutSection.id, index, question.id) || index + 1} 題 ${getQuestionUuid(question)}`}
                                draggable
                                $dragging={
                                  draggedHandoutQuestion?.type === "section" &&
                                  draggedHandoutQuestion.questionId === question.id
                                }
                                $insertBefore={
                                  handoutInsertTarget?.sectionId === activeHandoutSection.id &&
                                  handoutInsertTarget.index === 0 &&
                                  index === 0
                                }
                                $insertAfter={
                                  handoutInsertTarget?.sectionId === activeHandoutSection.id &&
                                  handoutInsertTarget.index === index + 1
                                }
                                onDragStart={(event) =>
                                  handleHandoutSectionQuestionDragStart(
                                    event,
                                    activeHandoutSection.id,
                                    question.id,
                                    index,
                                  )
                                }
                                onDragOver={(event) =>
                                  handleHandoutQuestionCardDragOver(
                                    event,
                                    activeHandoutSection.id,
                                    index,
                                  )
                                }
                                onDrop={(event) =>
                                  handleHandoutQuestionCardDrop(
                                    event,
                                    activeHandoutSection.id,
                                    index,
                                  )
                                }
                                onDragEnd={handleHandoutDragEnd}
                                title="單擊預覽題目，雙擊編輯題目"
                                onClick={(event) => queueQuestionPreview(event, question)}
                                onDoubleClick={(event) => openQuestionEditorFromCard(event, question)}
                              >
                                <SelectedCardHeader>
                                  <DragHandle title="拖曳調整順序">
                                    <FaGripVertical />
                                  </DragHandle>
                                  <SelectedIndex>
                                    {getHandoutQuestionNumber(handoutQuestionIndexById, activeHandoutSection.id, index, question.id) || index + 1}.
                                  </SelectedIndex>
                                  <QuestionUuidCopyButton buttonAs={SelectedUuidButton} value={getQuestionUuid(question)} label={shortId(getQuestionUuid(question))} onResult={handleQuestionCopyResult} />
                                  {handoutDeletedQuestionIds.has(String(getQuestionUuid(question))) && (
                                    <DeletedQuestionBadge title="這題已從題庫刪除，保留當時內容，不再跟題庫同步">題庫已刪除</DeletedQuestionBadge>
                                  )}
                                  <PendingSelectedRemoveButton
                                    type="button"
                                    title="移除"
                                    aria-label={`移除第 ${getHandoutQuestionNumber(handoutQuestionIndexById, activeHandoutSection.id, index, question.id) || index + 1} 題`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      removeQuestionFromHandoutSection(
                                        activeHandoutSection.id,
                                        question.id,
                                        index,
                                      );
                                    }}
                                  >
                                    <FaTimes />
                                  </PendingSelectedRemoveButton>
                                </SelectedCardHeader>
                                {!isExamHandoutTemplate && <QuestionPaginationSettings data-preview-no-drag
                                  onClick={(event) => event.stopPropagation()}
                                  onDoubleClick={(event) => event.stopPropagation()}
                                  onDragStart={(event) => { event.preventDefault(); event.stopPropagation(); }}>
                                  <summary>分頁設定</summary>
                                  {[
                                    ["widowControl", "段落遺留字串控制", "避免頁首或頁尾只有一行文字。"],
                                    ["keepWithNext", "與下段同頁", "盡量將本題與下一題留在同頁。"],
                                    ["keepTogether", "段中不分頁", "盡量將本題內容留在同頁；超過一頁時仍可分頁。"],
                                    ["pageBreakBefore", "段落前分頁", "與上方的題目前分頁共用同一設定，不會重複插入。"],
                                  ].map(([key, label, hint]) => <label key={key} title={hint}>
                                    <input type="checkbox" aria-label={`第 ${getHandoutQuestionNumber(handoutQuestionIndexById, activeHandoutSection.id, index, question.id)} 題${label}`}
                                      checked={key === "pageBreakBefore" ? Boolean(question.pageBreakBefore) : question.pagination?.[key] ?? key === "widowControl"}
                                      onChange={(event) => updateHandoutQuestionPagination(activeHandoutSection.id, question.id, key, event.target.checked)} />
                                    {label}
                                  </label>)}
                                </QuestionPaginationSettings>}
                                {getPreviewableQuestionAssets(question.assets || []).some((asset) => asset.id) && <QuestionPaginationSettings data-preview-no-drag
                                  onClick={(event) => event.stopPropagation()}
                                  onDoubleClick={(event) => event.stopPropagation()}
                                  onDragStart={(event) => { event.preventDefault(); event.stopPropagation(); }}>
                                  <summary>圖片對齊</summary>
                                  {getPreviewableQuestionAssets(question.assets || []).map((asset, assetIndex) => asset.id && <label key={asset.id}>
                                    圖片 {assetIndex + 1}
                                    <select aria-label={`第 ${getHandoutQuestionNumber(handoutQuestionIndexById, activeHandoutSection.id, index, question.id)} 題圖片 ${assetIndex + 1} 對齊`}
                                      value={getQuestionImageAlignment(asset, handoutOptions.questionImageAlignments)}
                                      onChange={(event) => updateQuestionImageAlignment(String(asset.id), event.target.value)}>
                                      <option value="right">靠右</option><option value="center">置中</option><option value="left">靠左</option>
                                    </select>
                                  </label>)}
                                </QuestionPaginationSettings>}
                                <SelectedPrompt>
                                  <MarkdownMathText source={getHandoutPreviewPrompt(question)} />
                                </SelectedPrompt>
                                <AssetPreviewStrip
                                  assets={question.assets || []}
                                  imageAlignments={handoutOptions.questionImageAlignments}
                                  compact
                                  limit={3}
                                />
                                {(authToken || question.is_good_question) && (
                                  <SelectedCardFooter>
                                    {question.is_good_question && (
                                      <GoodQuestionPill title="好題">
                                        <FaStar />
                                        好題
                                      </GoodQuestionPill>
                                    )}
                                    {authToken && familyActionButton(question, activeHandoutSection.id)}
                                  </SelectedCardFooter>
                                )}
                              </HandoutSectionQuestionCard>
                            </React.Fragment>
                          ))}
                        </>
                      ) : (
                        <>
                          {handoutInsertTarget?.sectionId === activeHandoutSection.id && (
                            <HandoutInsertMarker>
                              <span>放在這裡</span>
                            </HandoutInsertMarker>
                          )}
                          <HandoutDropHint>
                            從右邊題池拖題目到{handoutQuestionTargetLabel}
                          </HandoutDropHint>
                        </>
                      )}
                      <HandoutDropHint $compact>拖到這裡會放在{handoutQuestionTargetLabel}最後</HandoutDropHint>
                    </HandoutDropZone>
                    {!isExamHandoutTemplate && (
                      <SectionActions>
                        <HandoutActionButton
                          type="button"
                          onClick={() => deleteHandoutQuestionSection(activeHandoutSection.id)}
                          disabled={handoutQuestionSections.length <= 1}
                        >
                          <FaTimes />
                          刪除區塊
                        </HandoutActionButton>
                      </SectionActions>
                    )}
                  </SectionStateCard>
                )}
              </HandoutPanel>

              <HandoutPanel>
                <PanelTitle>
                  <FaSearch />
                  {handoutPickerTitle}
                </PanelTitle>
                <HandoutHint>
                  這裡的挑題只給{handoutWorkspaceLabel}製作用，不會影響左邊「題庫挑題」。
                </HandoutHint>
                <HandoutFilterGrid>
                  <QuestionCategorySelect subject={subject} value={handoutFilters.category} filter label="講義挑題分類" onChange={(value) => updateHandoutFilter("category", value)} />
                  <SearchBox>
                    <FaSearch />
                    <DeferredSearchInput
                      value={handoutFilters.search}
                      placeholder="搜尋題目 / 答案 / 詳解 / 來源"
                      onCommit={(value) => updateHandoutFilter("search", value)}
                    />
                  </SearchBox>
                  <QuestionFilterSelect
                    aria-label="講義挑題年級" value={handoutFilters.grade_id}
                    onChange={(event) => updateHandoutFilter("grade_id", event.target.value)}
                  >
                    <option value={filterNoneValue}>無</option>
                    <option value={filterAllValue}>全部年級</option>
                    {grades.map((grade) => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name}
                      </option>
                    ))}
                  </QuestionFilterSelect>
                  <SearchableSelect
              allowMultiple placeholderValues={["", "__none__"]} placeholder="單元"
                    value={handoutFilters.unit_id}
                    aria-label="講義挑題單元"
                    onChange={(value) => updateHandoutFilter("unit_id", value)}
                    options={[
                      { value: filterNoneValue, label: "無" },
                      { value: filterAllValue, label: "全部單元" },
                      ...handoutFilteredUnits.map((unit) => ({ value: unit.id, label: unit.name })),
                    ]}
                  />
                  <QuestionFilterSelect
                    aria-label="講義挑題題型" value={handoutFilters.type}
                    onChange={(event) => updateHandoutFilter("type", event.target.value)}
                  >
                    {questionTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </QuestionFilterSelect>
                  <QuestionFilterSelect
                    aria-label="講義挑題難度" value={handoutFilters.difficulty}
                    onChange={(event) => updateHandoutFilter("difficulty", event.target.value)}
                  >
                    {questionDifficulties.map((difficulty) => (
                      <option key={difficulty.value} value={difficulty.value}>
                        {difficulty.label}
                      </option>
                    ))}
                  </QuestionFilterSelect>
                  <QuestionFilterSelect
                    aria-label="講義挑題好題" value={handoutFilters.is_good_question}
                    onChange={(event) =>
                      updateHandoutFilter("is_good_question", event.target.value)
                    }
                  >
                    {questionGoodStarFilters.map((filter) => (
                      <option key={filter.value || "all-handout-good-star"} value={filter.value}>
                        {filter.label}
                      </option>
                    ))}
                  </QuestionFilterSelect>
                  <SearchableSelect
              allowMultiple placeholderValues={["", "__none__"]} placeholder="來源"
                    value={handoutFilters.question_source}
                    aria-label="講義挑題來源"
                    onChange={(value) => updateHandoutFilter("question_source", value)}
                    options={[
                      { value: "", label: "全部來源" },
                      ...questionSources.map((source) => ({ value: source, label: source })),
                    ]}
                  />
                  <IconOnlyButton
                    type="button"
                    title={`清除${handoutPickerTitle}篩選`}
                    aria-label={`清除${handoutPickerTitle}篩選`}
                    onClick={() => setHandoutFilters(emptyFilters)}
                  >
                    <FaTimes />
                  </IconOnlyButton>
                </HandoutFilterGrid>
                <QuestionFamilyDraw subject={subject} authToken={authToken} families={questionFamilies} familyId={handoutFilters.family_id} gradeId={handoutFilters.grade_id} onFamilyChange={(value) => updateHandoutFilter("family_id", value)} excludeIds={handoutQuestionIdsForOutput} targetId={`${handoutTemplateId}:${activeHandoutSection?.id}`} disabled={activeHandoutSection?.type !== "questions"} onInsert={insertHandoutQuestions} onRecorded={(draw) => setHandoutFamilyDraws((current) => [...current, draw])} />
                <BulkQuestionInsert
                  authToken={authToken}
                  subject={subject}
                  existingIds={handoutQuestionIdsForOutput}
                  targetId={`${handoutTemplateId}:${activeHandoutSection?.id}`}
                  targetLabel={`「${activeHandoutSection?.title || "目前區塊"}」`}
                  disabled={activeHandoutSection?.type !== "questions"}
                  onInsert={insertHandoutQuestions}
                />
                <HandoutQuestionPool>
                  {loading || handoutQuestionLoading ? (
                    <EmptyState>Loading...</EmptyState>
                  ) : !shouldLoadHandoutQuestionResults ? (
                    <EmptyState>請先選擇{handoutPickerTitle}條件</EmptyState>
                  ) : filteredHandoutQuestionPool.length ? (
                    <>
                      {filteredHandoutQuestionPool.map((question) => {
                        const alreadyInHandout = handoutQuestionIdSet.has(question.id);
                        const alreadyInActiveSection =
                          activeHandoutSectionQuestionIds.has(question.id);
                        const canAddToActiveSection =
                          activeHandoutSection?.type === "questions" && !alreadyInHandout;
                        return (
                          <HandoutPoolQuestion
                            key={question.id}
                            draggable={!alreadyInHandout}
                            $selected={alreadyInHandout}
                            role="button"
                            tabIndex={0}
                            aria-label="單擊預覽題目，雙擊編輯題目"
                            aria-haspopup="dialog"
                            title="單擊預覽題目，雙擊編輯題目"
                            onDragStart={(event) =>
                              handleHandoutQuestionDragStart(event, question)
                            }
                            onDragEnd={handleHandoutDragEnd}
                            onClick={(event) => queueQuestionPreview(event, question)}
                            onDoubleClick={(event) => openQuestionEditorFromCard(event, question)}
                            onKeyDown={(event) => {
                              if (
                                event.target === event.currentTarget &&
                                (event.key === "Enter" || event.key === " ")
                              ) {
                                event.preventDefault();
                                setPreviewQuestion(question);
                              }
                            }}
                          >
                            <QuestionContent>
                              <QuestionIdLine>
                                <QuestionUuidCopyButton buttonAs={SmallIconButton} value={getQuestionUuid(question)} onResult={handleQuestionCopyResult} />
                                {question.is_good_question && (
                                  <GoodQuestionPill title="好題">
                                    <FaStar />
                                    好題
                                  </GoodQuestionPill>
                                )}
                                <span>UUID：{shortId(getQuestionUuid(question))}</span>
                              </QuestionIdLine>
                              <QuestionTitle>
                                <MathText source={getQuestionPrompt(question)} fallback="" />
                              </QuestionTitle>
                              <AssetPreviewStrip assets={question.assets || []} limit={3} />
                              <QuestionMeta>
                                <span>{question.grade?.name || "-"}</span>
                                <span>{question.unit?.name || "-"}</span>
                                <QuestionDifficultyBadge difficulty={question.difficulty} />
                                <StatusPill $status={question.status}>
                                  {getStatusIcon(question.status)}
                                  {getStatusLabel(question.status)}
                                </StatusPill>
                              </QuestionMeta>
                            </QuestionContent>
                            <HandoutActionButton
                              type="button"
                              disabled={!canAddToActiveSection}
                              onClick={(event) => {
                                event.stopPropagation();
                                addQuestionToHandoutSection(activeHandoutSection.id, question);
                              }}
                            >
                              {alreadyInActiveSection
                                ? `已在${handoutQuestionTargetLabel}`
                                : alreadyInHandout
                                  ? `已在${handoutWorkspaceLabel}`
                                  : isExamHandoutTemplate
                                    ? `加入${handoutQuestionTargetLabel}`
                                    : "加入目前區塊"}
                            </HandoutActionButton>
                          </HandoutPoolQuestion>
                        );
                      })}
                      <QuestionListFooter ref={handoutSentinelRef}>
                        {handoutLoadingMore ? (
                          <>
                            <Spinner aria-hidden="true" />
                            載入更多題目...
                          </>
                        ) : handoutHasMore ? (
                          "往下滑載入更多"
                        ) : (
                          "已載入全部符合條件的題目"
                        )}
                      </QuestionListFooter>
                    </>
                  ) : (
                    <EmptyState>沒有符合條件的題目</EmptyState>
                  )}
                </HandoutQuestionPool>
              </HandoutPanel>

              <HandoutPreviewPanel ref={handoutPreviewPanelRef}>
                <PreviewPanelHeader>
                  <PreviewTitleGroup>
                    <PreviewTitleText>
                      <PanelTitle>
                        <FaBookOpen />
                        編輯預覽
                        <PreviewCountBadge>{handoutTotalQuestionCount} 題</PreviewCountBadge>
                      </PanelTitle>
                      <small>編輯圖文與順序；實際字型與頁數以下載的 Word 為準。</small>
                    </PreviewTitleText>
                    {!isExamHandoutTemplate && (
                      <PreviewSearchBox role="search" title="Enter 下一筆，Shift＋Enter 上一筆，Esc 清除">
                        <FaSearch aria-hidden="true" />
                        <input aria-label="搜尋題目文字或 UUID" value={handoutJumpQuery}
                          placeholder="搜尋題目文字或 UUID" enterKeyHint="search"
                          onChange={(event) => { setHandoutJumpQuery(event.target.value); setHandoutJumpMessage(""); }}
                          onKeyDown={(event) => {
                            if (event.key === "Escape") { setHandoutJumpQuery(""); setHandoutJumpMessage(""); return; }
                            if (event.key !== "Enter" || event.nativeEvent.isComposing || event.keyCode === 229) return;
                            event.preventDefault();
                            jumpToHandoutQuestion(event.shiftKey ? -1 : 1);
                          }} />
                        {handoutJumpMessage && <span role="status" data-empty={handoutJumpMessage === "無結果" || undefined}>{handoutJumpMessage}</span>}
                        {handoutJumpQuery && <>
                          <PreviewSearchStep type="button" aria-label="上一筆" title="上一筆（Shift＋Enter）" onClick={() => jumpToHandoutQuestion(-1)}><FaChevronLeft aria-hidden="true" /></PreviewSearchStep>
                          <PreviewSearchStep type="button" aria-label="下一筆" title="下一筆（Enter）" onClick={() => jumpToHandoutQuestion(1)}><FaChevronRight aria-hidden="true" /></PreviewSearchStep>
                        </>}
                      </PreviewSearchBox>
                    )}
                  </PreviewTitleGroup>
                  <PreviewToolbar>
                    <PreviewSegment role="group" aria-label="編輯紀錄">
                      <HandoutActionButton type="button" aria-label="復原" title="復原（Ctrl／⌘＋Z）" disabled={!handoutHistory.canUndo} onClick={handoutHistory.undo}><FaUndo /></HandoutActionButton>
                      <HandoutActionButton type="button" aria-label="重做" title="重做（Ctrl／⌘＋Y 或 Shift＋Z）" disabled={!handoutHistory.canRedo} onClick={handoutHistory.redo}><FaRedo /></HandoutActionButton>
                    </PreviewSegment>
                    {!isExamHandoutTemplate && <>
                      <PreviewSegment>
                        <HandoutActionButton type="button" aria-label="顯示格式標記" title="顯示或隱藏分頁控制標記（不列印）" aria-pressed={handoutShowFormattingMarks} onClick={() => setHandoutShowFormattingMarks((value) => !value)}>¶</HandoutActionButton>
                      </PreviewSegment>
                      <PreviewPageJump aria-live="polite"
                        aria-label={`第 ${handoutPreviewPage + 1}${handoutPreviewMode === "two" && handoutPreviewPage + 1 < handoutPreviewPageCount ? `–${handoutPreviewPage + 2}` : ""} / ${handoutPreviewPageCount} 頁`}
                        title={handoutPreviewPendingImages > 0 ? `圖片尺寸待載入 ${handoutPreviewPendingImages} 張，頁數仍在估算` : "輸入頁數後按 Enter 跳轉"}>
                        第
                        <input aria-label="輸入頁數跳轉" inputMode="numeric"
                          value={handoutJumpPageInput ?? String(handoutPreviewPage + 1)}
                          style={{ width: `${Math.max(2, String(handoutPreviewPageCount).length) + 1}ch` }}
                          onFocus={(event) => { setHandoutJumpPageInput(String(handoutPreviewPage + 1)); event.target.select(); }}
                          onChange={(event) => setHandoutJumpPageInput(event.target.value.replace(/\D/g, ""))}
                          onBlur={() => setHandoutJumpPageInput(null)}
                          onKeyDown={(event) => {
                            if (event.key === "Escape") { setHandoutJumpPageInput(null); event.currentTarget.blur(); return; }
                            if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
                            event.preventDefault();
                            jumpToHandoutPage();
                            event.currentTarget.blur();
                          }} />
                        {handoutPreviewMode === "two" && handoutPreviewPage + 1 < handoutPreviewPageCount ? `–${handoutPreviewPage + 2}` : ""} / {handoutPreviewPageCount} 頁
                      </PreviewPageJump>
                      <PreviewSegment role="group" aria-label="預覽版面">
                        <HandoutActionButton type="button" aria-pressed={handoutPreviewMode === "single"}
                          onClick={() => setHandoutPreviewMode("single")}>單頁</HandoutActionButton>
                        <HandoutActionButton type="button" aria-pressed={handoutPreviewMode === "two"}
                          onClick={() => {
                            setHandoutPreviewMode("two");
                            setHandoutPreviewPage((page) => Math.floor(page / 2) * 2);
                          }}>雙頁</HandoutActionButton>
                      </PreviewSegment>
                    </>}
                  </PreviewToolbar>
                </PreviewPanelHeader>

                {!isExamHandoutTemplate && (
                  <HandoutAssetShelf>
                    <strong><FaImage aria-hidden="true" />講義圖片素材</strong>
                    <PreviewSearchBox $compact>
                      <FaSearch aria-hidden="true" />
                      <input aria-label="搜尋講義圖片素材" value={handoutIconSearch}
                        placeholder="搜尋素材名稱" onChange={(event) => setHandoutIconSearch(event.target.value)} />
                    </PreviewSearchBox>
                    <PreviewSegment>
                      <HandoutActionButton type="button" aria-label="重新讀取" title="重新讀取素材" onClick={loadHandoutIcons} disabled={handoutIconLoading}>
                        {handoutIconLoading ? <LoadingText /> : <><FaSyncAlt aria-hidden="true" />重新讀取</>}
                      </HandoutActionButton>
                    </PreviewSegment>
                    <HandoutAssetShelfItems>
                      {draggableHandoutImages.map((file) => (
                        <button key={file.key} type="button" draggable
                          title={activeHandoutSection?.type === "focus" ? `加入目前觀念：${file.name}` : `拖到 A4 預覽插入 ${file.name}；點擊可加在最後`}
                          onDragStart={(event) => {
                            setDraggedHandoutAsset(file);
                            event.dataTransfer.effectAllowed = "copy";
                            event.dataTransfer.setData("text/plain", file.key);
                          }}
                          onDragEnd={() => setDraggedHandoutAsset(null)}
                          onClick={() => activeHandoutSection?.type === "focus"
                            ? addHandoutFocusImage(activeHandoutSection.id, file) : addHandoutImage(file)}>
                          <img src={file.url} alt="" /><span>{file.name}</span>
                        </button>
                      ))}
                      {!draggableHandoutImages.length && <span>沒有可插入 Word 的圖片素材</span>}
                    </HandoutAssetShelfItems>
                  </HandoutAssetShelf>
                )}

                <PreviewUuidTools resetKey={`${subject}:${activeHandoutRecordSource?.id || "new"}:${selectedHandoutDraftId}`}>
                  {renderHandoutBuilderA4Preview()}
                </PreviewUuidTools>
              </HandoutPreviewPanel>
            </HandoutMainGrid>
          </HandoutWorkspace>
        </HandoutBuilderGrid>
      ) : null}
      {activeWorkspace === "records" && <HandoutRecords subject={subject} authToken={authToken} grades={grades} onLoad={loadHandoutRecord} renderHandoutPreview={(record, keyboardEnabled, readerControls) => <SavedHandoutPreview record={record} keyboardEnabled={keyboardEnabled} readerControls={readerControls} />} />}
      {activeWorkspace === "exam-records" && <HandoutRecords key="exam-records" recordKind="exam" subject={subject} authToken={authToken} grades={grades} onLoad={loadHandoutRecord} onEditQuestion={onEditQuestion} />}
      <div hidden={activeWorkspace !== "short-answer"}>
        <ShortAnswerWorkspace authToken={authToken} active={activeWorkspace === "short-answer"} />
      </div>
      {familyReplacementTarget && <QuestionFamilyReplacement key={`${subject}:${familyReplacementTarget.question.id}:${familyReplacementTarget.sectionId || "picker"}`} subject={subject} authToken={authToken} question={familyReplacementTarget.question} families={questionFamilies} excludeIds={familyReplacementTarget.sectionId ? handoutQuestionIdsForOutput : selectedIds} onReplace={replaceFamilyQuestion} onQuestionSaved={rememberFamilyQuestion} onClose={() => setFamilyReplacementTarget(null)} />}
      {familyAnalysisIds && <QuestionFamilyDialog title="AI 建議題族" subtitle="先確認題族定義，再為題目歸族。" onClose={() => setFamilyAnalysisIds(null)}><QuestionFamilyAnalysis subject={subject} authToken={authToken} families={questionFamilies} initialQuestionIds={familyAnalysisIds} autoStart onChanged={refreshFamilyAnalysis} onClose={() => setFamilyAnalysisIds(null)} /></QuestionFamilyDialog>}
      <QuestionPreviewModal
        question={previewQuestion}
        onClose={() => setPreviewQuestion(null)}
        previewMatplotlibCode={previewMatplotlibCode}
        onAssetsRendered={handlePreviewAssetsRendered}
        meta={previewQuestion ? [
          previewQuestion.grade?.name || "未標示年級",
          previewQuestion.unit?.name || "未標示單元",
        ] : []}
      />
      {showPreviewDialog && (
        <ModalOverlay onClick={() => setShowPreviewDialog(false)}>
          <PreviewDialog role="dialog" aria-label={isBilingualExamTemplate(templateId) ? "雙語考卷預覽" : "A4 快速預覽"} onClick={(event) => event.stopPropagation()}>
            <DialogHeader>
              <div>
                <DialogTitle>A4 快速預覽</DialogTitle>
                <DialogMeta>
                  {outputModes.find((mode) => mode.value === (outputMode === "teaching" ? "teaching_detailed" : outputMode))?.label}
                  {" · "}
                  {sectionModes.find((mode) => mode.value === sectionMode)?.label}
                  {" · "}
                  {selectedIds.length} 題
                </DialogMeta>
              </div>
              <IconOnlyButton
                type="button"
                title="關閉"
                onClick={() => setShowPreviewDialog(false)}
              >
                <FaTimes />
              </IconOnlyButton>
            </DialogHeader>
            <PreviewDialogBody>
              {bilingualIssuesPanel}
              <PreviewOrderPanel>
                <PreviewSettingsPanel>
                  <PreviewPanelTitle>預覽設定</PreviewPanelTitle>
                  <PreviewSettingField $wide>
                    <span>標題文字</span>
                    <input
                      value={documentTitle}
                      onChange={(event) => setDocumentTitle(event.target.value)}
                    />
                  </PreviewSettingField>
                  <PreviewSettingField>
                    <span>字體</span>
                    <select
                      value={previewFont}
                      onChange={(event) => setPreviewFont(event.target.value)}
                    >
                      {previewFonts.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </PreviewSettingField>
                  <PreviewSettingField>
                    <span>內容文字大小</span>
                    <select
                      value={previewFontSize}
                      onChange={(event) => setPreviewFontSize(event.target.value)}
                    >
                      {previewFontSizes.map((size) => (
                        <option key={size.value} value={size.value}>
                          {size.label}
                        </option>
                      ))}
                    </select>
                  </PreviewSettingField>

                </PreviewSettingsPanel>
                <PreviewPanelTitle>題目順序</PreviewPanelTitle>
                <PreviewOrderList>
                  {selectedQuestions.map((question, index) => (
                    <PreviewOrderItem
                      key={question.id}
                      draggable
                      $dragging={draggedSelectedId === question.id}
                      $dragOver={dragOverSelectedId === question.id}
                      onDragStart={(event) => handleSelectedDragStart(event, question.id)}
                      onDragOver={(event) => handleSelectedDragOver(event, question.id)}
                      onDrop={(event) => handleSelectedDrop(event, question.id)}
                      onDragEnd={handleSelectedDragEnd}
                    >
                      <DragHandle title="拖曳調整順序">
                        <FaGripVertical />
                      </DragHandle>
                      <SelectedIndex>{index + 1}.</SelectedIndex>
                      <PreviewOrderText>
                        <MathText
                          source={getHandoutPreviewPrompt(question)}
                          fallback=""
                        />
                      </PreviewOrderText>
                    </PreviewOrderItem>
                  ))}
                </PreviewOrderList>
              </PreviewOrderPanel>
              {renderPickerA4Preview()}
            </PreviewDialogBody>
            <DialogActions>
              <IconButton type="button" onClick={() => setShowPreviewDialog(false)}>
                <FaTimes />
                關閉
              </IconButton>
              <IconButton
                type="button"
                $primary
                disabled={previewPreparing || generating || bilingualLoading || !selectedIds.length || (isBilingualExamTemplate(templateId) && !hasBilingualPreview)}
                onClick={handleGenerate}
              >
                <FaDownload />
                {generating ? "產生中..." : "產生並下載 Word"}
              </IconButton>
            </DialogActions>
          </PreviewDialog>
        </ModalOverlay>
      )}
      {templatePreviewFile && (
        <ModalOverlay onClick={() => setTemplatePreviewFile(null)}>
          <TemplatePreviewDialog onClick={(event) => event.stopPropagation()}>
            <DialogHeader>
              <div>
                <DialogTitle>{templatePreviewFile.label}</DialogTitle>
                <DialogMeta>模板 Word 預覽</DialogMeta>
              </div>
              <IconOnlyButton
                type="button"
                title="關閉"
                onClick={() => setTemplatePreviewFile(null)}
              >
                <FaTimes />
              </IconOnlyButton>
            </DialogHeader>
            <TemplatePreviewFrame
              title={templatePreviewFile.label}
              src={getDocxPreviewUrl(getTemplatePreviewSourceUrl(templatePreviewFile))}
            />
            <DialogActions>
              <IconButton
                type="button"
                onClick={() => downloadTemplateFile(templatePreviewFile)}
              >
                <FaDownload />
                下載 Word
              </IconButton>
              <IconButton type="button" onClick={() => setTemplatePreviewFile(null)}>
                <FaTimes />
                關閉
              </IconButton>
            </DialogActions>
          </TemplatePreviewDialog>
        </ModalOverlay>
      )}
      {showHandoutSettingsDialog && (
        <ModalOverlay onClick={() => setShowHandoutSettingsDialog(false)}>
          <HandoutSettingsDialog onClick={(event) => event.stopPropagation()}>
            <DialogHeader>
              <div>
                <DialogTitle>{handoutWorkspaceLabel}細節設定</DialogTitle>
                <DialogMeta>
                  {selectedHandoutTemplate.label} · 字體、行距、版面、頁首頁尾、Cloudflare 圖示素材
                </DialogMeta>
              </div>
              <IconOnlyButton
                type="button"
                title="關閉"
                onClick={() => setShowHandoutSettingsDialog(false)}
              >
                <FaTimes />
              </IconOnlyButton>
            </DialogHeader>
            <HandoutSettingsBody>
              <HandoutSettingsColumn>
                {isExamHandoutTemplate && (
                  <>
                    <SectionMiniTitle>檢測卷首頁</SectionMiniTitle>
                    {renderExamSettingsFields()}
                  </>
                )}
                <SectionMiniTitle>輸出與字體</SectionMiniTitle>
                {isHandoutWordLayout(handoutOptions) && <HandoutLayoutControls settings={handoutOptions} onChange={updateHandoutOption} />}
                <SettingGrid>
                  <Field>
                    <span>內文字體</span>
                    <select
                      value={handoutOptions.bodyFontName}
                      onChange={(event) => updateHandoutOption("bodyFontName", event.target.value)}
                    >
                      {handoutFonts.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field>
                    <span>標題字體</span>
                    <select
                      value={handoutOptions.headingFontName}
                      onChange={(event) =>
                        updateHandoutOption("headingFontName", event.target.value)
                      }
                    >
                      {handoutFonts.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field>
                    <span>內文字級</span>
                    <input
                      type="number"
                      min="10"
                      max="18"
                      step="0.5"
                      value={handoutOptions.bodyFontSize}
                      onChange={(event) => updateHandoutOption("bodyFontSize", event.target.value)}
                    />
                  </Field>
                  <Field>
                    <span>標題字級</span>
                    <input
                      type="number"
                      min="12"
                      max="22"
                      step="0.5"
                      value={handoutOptions.headingFontSize}
                      onChange={(event) =>
                        updateHandoutOption("headingFontSize", event.target.value)
                      }
                    />
                  </Field>
                  <Field>
                    <span>單元字級</span>
                    <input
                      type="number"
                      min="14"
                      max="28"
                      step="0.5"
                      value={handoutOptions.titleFontSize}
                      onChange={(event) => updateHandoutOption("titleFontSize", event.target.value)}
                    />
                  </Field>
                  <Field>
                    <span>行距</span>
                    <select
                      value={handoutOptions.lineSpacing}
                      onChange={(event) => updateHandoutOption("lineSpacing", event.target.value)}
                    >
                      <option value="1">1.0</option>
                      <option value="1.15">1.15</option>
                      <option value="1.25">1.25</option>
                      <option value="1.5">1.5</option>
                    </select>
                  </Field>
                </SettingGrid>

                <SectionMiniTitle>版面邊界</SectionMiniTitle>
                <MarginGrid>
                  {[
                    ["top", "上"],
                    ["bottom", "下"],
                    ["left", "左"],
                    ["right", "右"],
                    ["gutter", "裝訂邊"],
                  ].map(([key, label]) => (
                    <Field key={key}>
                      <span>{label} cm</span>
                      <input
                        type="number"
                        min={key === "gutter" ? "0" : "0.5"}
                        max={key === "gutter" ? "2" : "6"}
                        step="0.05"
                        value={handoutOptions.margins[key]}
                        onChange={(event) =>
                          updateHandoutNestedOption("margins", key, event.target.value)
                        }
                      />
                    </Field>
                  ))}
                </MarginGrid>

                <SectionMiniTitle>頁首、頁尾、頁碼</SectionMiniTitle>
                <label><input type="checkbox" checked={handoutOptions.headerFooter.differentOddEvenPages}
                  disabled={isHandoutWordLayout(handoutOptions)}
                  onChange={(event) => updateHandoutNestedOption("headerFooter", "differentOddEvenPages", event.target.checked)} /> 區分奇偶頁</label>
                {isHandoutWordLayout(handoutOptions) && <small>寰宇講義固定奇數頁頁首靠左、偶數頁頁首靠右。</small>}
                <HeaderFooterGrid>
                  {[
                    ["oddHeaderLeft", "奇數頁首左"],
                    ["oddHeaderRight", "奇數頁首右"],
                    ["evenHeaderLeft", "偶數頁首左"],
                    ["evenHeaderRight", "偶數頁首右"],
                    ["oddFooterLeft", "奇數頁尾左"],
                    ["oddFooterRight", "奇數頁尾右"],
                    ["evenFooterLeft", "偶數頁尾左"],
                    ["evenFooterRight", "偶數頁尾右"],
                  ].filter(([key]) => handoutOptions.headerFooter.differentOddEvenPages || !key.startsWith("even")).map(([key, label]) => (
                    <Field key={key}>
                      <span>{handoutOptions.headerFooter.differentOddEvenPages ? label : label.replace("奇數", "")}</span>
                      <input
                        value={handoutOptions.headerFooter[key]}
                        onChange={(event) =>
                          updateHandoutNestedOption("headerFooter", key, event.target.value)
                        }
                      />
                    </Field>
                  ))}
                  <Field>
                    <span>頁碼位置</span>
                    <select
                      value={handoutOptions.headerFooter.pageNumberPosition}
                      onChange={(event) =>
                        updateHandoutNestedOption(
                          "headerFooter",
                          "pageNumberPosition",
                          event.target.value,
                        )
                      }
                    >
                      {handoutPageNumberPositions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </HeaderFooterGrid>
              </HandoutSettingsColumn>

              <HandoutSettingsColumn>
                <SectionMiniTitle>圖示素材</SectionMiniTitle>
                <Field>
                  <span>素材資料夾</span>
                  <input
                    value={handoutOptions.iconPrefix}
                    onChange={(event) => updateHandoutOption("iconPrefix", event.target.value)}
                  />
                </Field>
                <IconButton type="button" onClick={loadHandoutIcons} disabled={handoutIconLoading}>
                  <FaSyncAlt />
                  {handoutIconLoading ? <LoadingText /> : "讀取素材"}
                </IconButton>

                <RoleTabs aria-label="圖示用途">
                  {handoutIconRoles.map((role) => (
                    <RoleTabButton
                      key={role.value}
                      type="button"
                      $active={handoutOptions.activeIconRole === role.value}
                      aria-pressed={handoutOptions.activeIconRole === role.value}
                      onClick={() => {
                        updateHandoutOption("activeIconRole", role.value);
                        setHandoutIconFolder(getHandoutRoleFolder(role.value, handoutIconFolders));
                        setHandoutIconSearch("");
                      }}
                    >
                      {role.label}
                    </RoleTabButton>
                  ))}
                </RoleTabs>

                <SelectedIconSlots>
                  {handoutIconRoles.map((role) => {
                    const icon = handoutOptions.icons[role.value];
                    return (
                      <SelectedIconSlot key={role.value} $empty={!icon}>
                        <span>{role.label}</span>
                        {icon ? (
                          <>
                            <img src={icon.url} alt="" />
                            <button type="button" aria-label={`移除${role.label}圖示`} onClick={() => clearHandoutIcon(role.value)}>
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <small>未選</small>
                        )}
                      </SelectedIconSlot>
                    );
                  })}
                </SelectedIconSlots>

                <SearchBox>
                  <FaSearch />
                  <input
                    aria-label="搜尋圖示素材"
                    value={handoutIconSearch}
                    placeholder={`搜尋${selectedHandoutRole.label}圖示`}
                    onChange={(event) => setHandoutIconSearch(event.target.value)}
                  />
                </SearchBox>
                {isHandoutWordLayout(handoutOptions) && handoutOptions.activeIconRole === "type" &&
                  <small>題型編號在左側區塊的「題型編號」調整；此處選擇圖示樣式。</small>}

                <Field><span>素材分類</span>
                  <select aria-label="圖示素材分類" value={handoutIconFolder} onChange={(event) => setHandoutIconFolder(event.target.value)}>
                    <option value="">全部資料夾</option>
                    {handoutIconFolders.map((folder) => <option key={folder} value={folder} title={folder}>{folder.split("/").pop() || "未分類"}</option>)}
                  </select>
                </Field>
                <small aria-live="polite">找到 {filteredHandoutIconFiles.length} / {handoutIconFiles.length} 張素材</small>
                {handoutOptions.icons[handoutOptions.activeIconRole]?.url && <SettingGrid>
                  <Field><span>圖示寬（公分）</span><HandoutNumberInput aria-label="選取圖示寬度" min={0.2} max={18} step="0.1" emptyValue={null}
                    value={handoutOptions.icons[handoutOptions.activeIconRole].width_cm ?? handoutOptions.icons[handoutOptions.activeIconRole].widthCm ?? ""}
                    onChange={(value) => updateHandoutIconSize("width_cm", value)} /></Field>
                  <Field><span>圖示高（公分）</span><HandoutNumberInput aria-label="選取圖示高度" min={0.2} max={18} step="0.1" emptyValue={null}
                    value={handoutOptions.icons[handoutOptions.activeIconRole].height_cm ?? handoutOptions.icons[handoutOptions.activeIconRole].heightCm ?? ""}
                    onChange={(value) => updateHandoutIconSize("height_cm", value)} /></Field>
                </SettingGrid>}

                <IconGallery>
                  {handoutIconLoading ? (
                    <EmptyState role="status"><LoadingText>素材讀取中</LoadingText></EmptyState>
                  ) : filteredHandoutIconFiles.length ? (
                    filteredHandoutIconFiles.map((file) => {
                      const selected =
                        handoutOptions.icons[handoutOptions.activeIconRole]?.key === file.key;
                      return (
                        <IconTile
                          key={file.key}
                          type="button"
                          draggable={wordImageExtensions.has(getAssetExtension(file.key))}
                          onDragStart={(event) => {
                            setDraggedHandoutAsset(file);
                            event.dataTransfer.effectAllowed = "copy";
                            event.dataTransfer.setData("text/plain", file.key);
                          }}
                          onDragEnd={() => setDraggedHandoutAsset(null)}
                          $selected={selected}
                          aria-pressed={selected}
                          onClick={() => selectHandoutIcon(file)}
                          title={file.key}
                        >
                          <img src={file.url} alt="" loading="lazy" />
                          <span>{file.name}</span>
                        </IconTile>
                      );
                    })
                  ) : (
                    <EmptyState>沒有符合搜尋或分類的素材</EmptyState>
                  )}
                </IconGallery>
              </HandoutSettingsColumn>
            </HandoutSettingsBody>
            <DialogActions>
              <IconButton type="button" onClick={() => setShowHandoutSettingsDialog(false)}>
                <FaCheck />
                完成
              </IconButton>
            </DialogActions>
          </HandoutSettingsDialog>
        </ModalOverlay>
      )}
      {focusPythonTarget && <HandoutImagePythonDialog key={`${focusPythonTarget.sectionId}:${focusPythonTarget.image.id}`}
        autoPreviewOnOpen image={focusPythonTarget.image} bodyFontSize={handoutOptions.bodyFontSize} previewMatplotlibCode={previewMatplotlibCode}
        onClose={() => setFocusPythonTarget(null)} onApply={(updatedImage) => {
          if (focusPythonTarget.isNew) addHandoutFocusImage(focusPythonTarget.sectionId, updatedImage, { prebuilt: true });
          else replaceHandoutImage(focusPythonTarget.sectionId, focusPythonTarget.image.id, updatedImage);
          setFocusPythonTarget(null);
        }} />}
      {backgroundRemovalTarget && <ImageBackgroundRemovalDialog image={backgroundRemovalTarget.image} sourceOverride={backgroundRemovalTarget.source}
        onClose={() => setBackgroundRemovalTarget(null)} onApply={(updatedImage) => {
          replaceHandoutImage(backgroundRemovalTarget.sectionId, backgroundRemovalTarget.imageId, updatedImage);
          setBackgroundRemovalTarget(null);
        }} />}
      <ExpandedEditorModal
        editor={handoutVimEditor}
        onClose={() => setHandoutVimTarget(null)}
        onCopy={(value) => navigator.clipboard?.writeText?.(value)}
      />
    </PageShell>
  );
}

const PageShell = styled.div`
  width: min(1180px, 100%);
  box-sizing: border-box;
  min-width: 0;
  margin: 18px auto 56px;
  padding: 0 12px;
  color: var(--app-text, #202124);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-end;
  padding: 12px 0 14px;
  border-bottom: 1px solid var(--app-border, #dedede);
`;

const Eyebrow = styled.span`
  color: var(--app-muted-text, #5f6368);
  font-size: 13px;
  font-weight: 800;
`;

const PageTitle = styled.h2`
  margin: 2px 0 0;
  color: var(--app-text, #202124);
  font-size: 26px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const CountText = styled.span`
  color: var(--app-subtle-text, #5f6368);
  font-size: 14px;
  font-weight: 800;
`;

const Notice = styled.div`
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid
    ${({ $tone }) =>
    $tone === "error"
      ? "#efb5b5"
      : $tone === "success"
        ? "#a7d8b5"
        : "#d5d7db"};
  border-radius: 6px;
  background: ${({ $tone }) =>
    $tone === "error"
      ? "#fff4f4"
      : $tone === "success"
        ? "#f2fbf5"
        : "#f8f9fa"};
  color: ${({ $tone }) =>
    $tone === "error"
      ? "#9f1d1d"
      : $tone === "success"
        ? "#17642c"
        : "#3c4043"};
  font-weight: 800;
`;

const FeatureTabs = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 14px 0 2px;
  padding: 4px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 8px;
  background: var(--app-surface, #fff);
`;

const FeatureTabButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 36px;
  min-width: 118px;
  border: 1px solid
    ${({ $active }) => ($active ? "var(--app-nav-active-bg, #202124)" : "transparent")};
  border-radius: 6px;
  padding: 0 13px;
  background: ${({ $active }) =>
    $active ? "var(--app-nav-active-bg, #202124)" : "transparent"};
  color: ${({ $active }) =>
    $active ? "var(--app-nav-active-text, #fff)" : "var(--app-input-text, #202124)"};
  font-weight: 900;
  cursor: pointer;
`;

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 1.25fr) repeat(8, minmax(96px, 0.9fr)) 40px;
  gap: 8px;
  padding: 14px 0;

  select {
    min-width: 0;
    min-height: 38px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    padding: 0 10px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
  }

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

    > button:last-child {
      justify-self: end;
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);

    > button:last-child {
      justify-self: start;
    }
  }
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  padding: 0 10px;
  background: var(--app-input-bg, #fff);
  color: var(--app-muted-text, #5f6368);

  input {
    min-width: 0;
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--app-input-text, #202124);
    font: inherit;
  }
`;

const Workspace = styled.div`
  display: grid;
  grid-template-columns: ${({ $layout }) => $layout ? "320px minmax(0, 1fr)" : "minmax(0, 1fr) 330px"};
  grid-template-areas: ${({ $layout }) => $layout ? '"output preview"' : '"questions output"'};
  gap: 16px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-areas: ${({ $layout }) => $layout ? '"output" "preview"' : '"questions" "output"'};
  }
`;

const QuestionPanel = styled.section`
  grid-area: questions;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 8px;
  min-width: 0;
  min-height: 0;
  height: ${({ $matchHeight }) => ($matchHeight ? `${$matchHeight}px` : "auto")};
  --question-results-max-height: ${({ $matchHeight }) =>
    $matchHeight ? "none" : "min(72vh, calc(100vh - 260px))"};

  @media (max-width: 900px) {
    grid-template-rows: none;
    height: auto;
    --question-results-max-height: min(72vh, calc(100vh - 260px));
  }
`;

const QuestionResultsScroll = styled.div`
  display: grid;
  align-content: start;
  gap: 8px;
  min-height: 0;
  height: 100%;
  max-height: var(--question-results-max-height);
  border-radius: 8px;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  padding-right: 2px;
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 8px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);

  @media (max-width: 720px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const SelectionSummary = styled.div`
  min-width: 0;
  color: var(--app-subtle-text, #5f6368);
  font-size: 13px;
  font-weight: 900;
`;

const SelectionActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: 720px) {
    justify-content: flex-start;
  }
`;

const QuestionRow = styled.article`
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: 10px;
  min-width: 0;
  padding: 12px;
  border: 1px solid
    ${({ $selected }) => ($selected ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #dedede)")};
  border-radius: 8px;
  background: ${({ $selected }) =>
    $selected ? "var(--app-surface-soft, #f4f5f7)" : "var(--app-surface, #fff)"};
  color: var(--app-surface-text, #202124);
  cursor: zoom-in;
  ${({ $flash }) => $flash && css`
    outline: 3px solid #1a73e8;
    outline-offset: 2px;
    animation: ${insertFlashFrame} ${INSERT_FLASH_MS}ms ease-out forwards;
  `}

  &:focus-visible {
    outline: 2px solid var(--app-nav-active-bg, #202124);
    outline-offset: 3px;
  }
`;

const FamilyAction = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  gap: 12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--app-surface-text, #202124);
  text-align: left;
  font: inherit;
  cursor: pointer;

  span {
    min-width: 0;
    color: var(--app-muted-text, #777);
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    flex: 0 0 auto;
    padding: 6px 10px;
    border: 1px solid var(--app-border, #ddd);
    border-radius: 6px;
    background: var(--app-input-bg, #fff);
    font-size: 12px;
    font-weight: 700;
    line-height: 1.5;
  }

  &:hover strong {
    border-color: var(--app-border-strong, #9aa0a6);
    background: var(--app-surface-soft, #f4f5f7);
  }

  &:focus-visible {
    outline: 2px solid var(--app-border-strong, #9aa0a6);
    outline-offset: 3px;
    border-radius: 6px;
  }
`;

const FamilySetupAction = styled.button`
  flex: 0 0 auto;
  padding: 6px 4px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--app-muted-text, #777);
  font: inherit;
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: var(--app-surface-soft, #f4f5f7);
    color: var(--app-surface-text, #202124);
  }

  &:focus-visible {
    outline: 2px solid var(--app-border-strong, #9aa0a6);
    outline-offset: 2px;
  }
`;

const SelectButton = styled.button`
  width: 30px;
  height: 30px;
  border: 1px solid
    ${({ $selected }) => ($selected ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #cfd4dc)")};
  border-radius: 6px;
  background: ${({ $selected }) =>
    $selected ? "var(--app-nav-active-bg, #202124)" : "var(--app-input-bg, #fff)"};
  color: ${({ $selected }) => ($selected ? "var(--app-nav-active-text, #fff)" : "var(--app-input-text, #202124)")};
  cursor: pointer;
`;

const QuestionContent = styled.div`
  min-width: 0;
  display: grid;
  gap: 6px;
`;

const QuestionTitle = styled.div`
  max-height: 88px;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  color: var(--app-surface-text, #202124);
  font-weight: 800;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  .katex {
    font-size: 1em;
  }

  .katex-display {
    margin: 4px 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const QuestionSolution = styled.div`
  border: 1px solid var(--app-border, #e5e7eb);
  border-left: 3px solid var(--app-border-strong, #9aa0a6);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--app-surface-soft, #f7f8fa);
`;

const QuestionSolutionLabel = styled.div`
  margin-bottom: 4px;
  color: var(--app-muted-text, #5f6368);
  font-size: 12px;
  font-weight: 900;
`;

const QuestionSolutionText = styled.div`
  max-height: 180px;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  color: var(--app-subtle-text, #3d3d3d);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  .katex {
    font-size: 1em;
  }

  .katex-display {
    margin: 4px 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const QuestionIdLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "4px" : "6px")};
  color: var(--app-muted-text, #6b7280);
  font-family:
    source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: ${({ $compact }) => ($compact ? "11px" : "12px")};
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const SmallIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $compact }) => ($compact ? "22px" : "26px")};
  height: ${({ $compact }) => ($compact ? "22px" : "26px")};
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #3c4043);
  cursor: pointer;

  &:hover {
    background: var(--app-surface-soft, #f4f5f7);
  }

  svg {
    font-size: ${({ $compact }) => ($compact ? "10px" : "12px")};
  }
`;

const QuestionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  color: var(--app-muted-text, #5f6368);
  font-size: 12px;
  font-weight: 800;

  span:not([data-difficulty]) {
    border-radius: 999px;
    background: var(--app-surface-soft, #eef2f7);
    padding: 3px 7px;
  }
`;

const VisibilityPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid
    ${({ $visibility }) => ($visibility === "private" ? "#e6a7a7" : "#99c9a4")} !important;
  background: ${({ $visibility }) =>
    $visibility === "private" ? "#fff1f1" : "#eef9f0"} !important;
  color: ${({ $visibility }) =>
    $visibility === "private" ? "#9b1c1c" : "#1f6b34"};
  font-weight: 900;

  svg {
    font-size: 10px;
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid
    ${({ $status }) =>
    $status === "published"
      ? "#9dbbe8"
      : $status === "archived"
        ? "#c7b3e6"
        : "#cfd4dc"} !important;
  background: ${({ $status }) =>
    $status === "published"
      ? "#eff6ff"
      : $status === "archived"
        ? "#f5f0ff"
        : "#f8f9fa"} !important;
  color: ${({ $status }) =>
    $status === "published"
      ? "#1d4f91"
      : $status === "archived"
        ? "#5b2c83"
        : "#4b5563"};
  font-weight: 900;

  svg {
    font-size: 10px;
  }
`;

const GoodQuestionPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid #e1b84f;
  border-radius: 999px;
  background: #fff7d6;
  color: #8a5a00;
  padding: 3px 7px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 900;
  white-space: nowrap;

  svg {
    font-size: 10px;
  }
`;

const ThinkingLine = styled.div`
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
`;

const QuestionListFooter = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  color: var(--app-subtle-text, #5f6368);
  font-size: 13px;
  font-weight: 800;
`;

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid #d5d7db;
  border-top-color: var(--app-nav-active-bg, #202124);
  border-radius: 999px;
  animation: math-bank-picker-spin 0.8s linear infinite;

  @keyframes math-bank-picker-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ExportPanel = styled.aside`
  grid-area: output;
  position: sticky;
  top: 12px;
  display: grid;
  gap: 12px;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 8px;
  padding: 14px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);

  @media (max-width: 900px) {
    position: static;
  }
`;

const PanelTitle = styled.h3`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 17px;
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
  color: var(--app-subtle-text, #3c4043);
  font-size: 13px;
  font-weight: 800;

  input,
  select,
  textarea {
    min-height: 38px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    padding: 0 10px;
    font: inherit;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
  }

  input[readonly] {
    background: var(--app-surface-soft, #f4f5f7);
    color: var(--app-muted-text, #5f6368);
  }

  textarea {
    min-height: 96px;
    padding: 9px 10px;
    resize: vertical;
    line-height: 1.5;
  }
`;

const TextareaWithVim = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
  > textarea {
    grid-row: 2;
    display: block;
    width: 100%;
    box-sizing: border-box;
  }
`;

const VimFieldButton = styled.button`
  grid-row: 1;
  justify-self: end;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  padding: 2px 6px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  font-size: 11px;
  line-height: 1.2;
  cursor: pointer;
`;
// Opening the Vim modal re-renders the editor; show progress until it is up.
const PendingVimFieldButton = withPendingFeedback(VimFieldButton);

const SelectedList = styled.div`
  display: grid;
  gap: 12px;
  max-height: min(60vh, 560px);
  padding-right: 4px;
  border-radius: 8px;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
`;

const PickerA4PreviewBlock = styled.div`
  grid-area: preview;
  min-width: 0;
  position: sticky;
  top: 12px;
  display: grid;
  gap: 8px;
  min-width: 0;
`;

const SelectedItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--app-border, #eceff1);
  border-top-color: ${({ $dragOver }) =>
    $dragOver ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #eceff1)"};
  border-top-width: ${({ $dragOver }) => ($dragOver ? "3px" : "1px")};
  border-radius: 8px;
  background: ${({ $dragging }) =>
    $dragging ? "var(--app-surface-soft, #f4f5f7)" : "var(--app-surface, #fff)"};
  color: var(--app-subtle-text, #3c4043);
  font-size: 13px;
  opacity: ${({ $dragging }) => ($dragging ? 0.64 : 1)};
`;

const SelectedCardHeader = styled.div`
  display: grid;
  grid-template-columns: 16px auto minmax(0, 1fr) 32px;
  align-items: center;
  gap: 8px;
  min-width: 0;

  > strong {
    line-height: 32px;
  }
`;

const SelectedUuidButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  min-height: 32px;
  padding: 4px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--app-muted-text, #6b7280);
  text-align: left;
  font-family: source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 11px;
  cursor: pointer;

  svg {
    flex: 0 0 auto;
    font-size: 12px;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: var(--app-surface-soft, #f4f5f7);
    color: var(--app-surface-text, #202124);
  }
`;

const SelectedRemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #3c4043);
  cursor: pointer;

  &:hover {
    background: var(--app-surface-soft, #f4f5f7);
  }
`;
const PendingSelectedRemoveButton = withPendingFeedback(SelectedRemoveButton);

const DeletedQuestionBadge = styled.span`
  flex: none;
  border-radius: 999px;
  padding: 1px 8px;
  background: var(--app-danger-bg, #fdecea);
  color: var(--app-danger-text, #8c1d18);
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
`;

const SelectedCardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding-top: 8px;
  border-top: 1px solid var(--app-border, #eceff1);
`;

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  min-height: 26px;
  color: var(--app-muted-text, #6b7280);
  cursor: grab;

  svg {
    font-size: 13px;
  }
`;

const SelectedIndex = styled.strong`
  line-height: 26px;
`;

const SelectedPrompt = styled.div`
  min-width: 0;
  line-height: 1.65;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  .katex {
    font-size: 1em;
  }

  .katex-display {
    margin: 3px 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const HandoutWorkspace = styled.div`
  input, select, textarea { min-width: 0; max-width: 100%; box-sizing: border-box; }
  grid-column: 1 / -1;
  display: grid;
  gap: 14px;
  min-width: 0;
`;

const HandoutTopBar = styled.section`
  display: grid;
  gap: 18px;
  min-width: 0;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 10px;
  padding: 20px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
`;

const HandoutTopHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 20px;
`;

const HandoutSummaryLine = styled.div`
  margin-top: 0;
  color: var(--app-muted-text, #5f6368);
  font-size: 13px;
  font-weight: 600;
`;

const HandoutTemplatePicker = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  min-width: 0;
  color: var(--app-subtle-text, #3c4043);
  font-size: 13px;
  font-weight: 800;
`;

const HandoutTemplateButton = styled.button`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 12px;
  min-width: 0;
  min-height: 52px;
  box-sizing: border-box;
  border: 1px solid var(--app-border, #cfd4dc);
  border-radius: 8px;
  padding: 7px 12px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  text-align: left;
  cursor: pointer;
  box-shadow: none;

  > span:first-child { width: 32px; height: 32px; }

  > svg {
    justify-self: end;
    color: var(--app-muted-text, #6b7280);
    font-size: 12px;
  }
`;

const TemplateMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  line-height: 1;
  border-radius: 8px;
  background: ${({ $variant }) => ($variant === "exam" ? "var(--app-nav-active-bg, #111827)" : "#eef6f3")};
  color: ${({ $variant }) => ($variant === "exam" ? "#ffffff" : "#0f766e")};
  box-shadow: ${({ $variant }) =>
    $variant === "exam"
      ? "inset 0 0 0 1px #4b5563"
      : "inset 0 0 0 1px #b7d8ce"};

  svg { display: block; width: 16px; height: 16px; }
`;

const TemplateButtonText = styled.span`
  display: grid;
  gap: 4px;
  min-width: 0;

  strong,
  small {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: var(--app-input-text, #202124);
    font-size: 14px;
    font-weight: 900;
    line-height: 1.3;
  }

  small {
    color: var(--app-muted-text, #6b7280);
    font-size: 11px;
    font-weight: 800;
    line-height: 1.4;
  }
`;

const HandoutTemplateMenu = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  display: grid;
  gap: 6px;
  min-width: 250px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 8px;
  padding: 8px;
  background: var(--app-surface, #fff);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.22);
`;

const HandoutTemplateMenuItem = styled.button`
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) 18px;
  gap: 12px;
  align-items: center;
  min-width: 0;
  border: 1px solid
    ${({ $active }) => ($active ? "var(--app-nav-active-bg, #202124)" : "transparent")};
  border-radius: 8px;
  padding: 10px;
  background: ${({ $active }) =>
    $active ? "var(--app-surface-soft, #f4f5f7)" : "transparent"};
  color: var(--app-input-text, #202124);
  text-align: left;
  cursor: pointer;

  > svg {
    justify-self: end;
  }
`;

const HandoutControlField = styled(Field)`
  min-width: 0;

  input,
  select {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }
`;

const HandoutTopControls = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 210px), 1fr));
  gap: 16px;
  align-items: end;
  min-width: 0;

  input, select {
    height: 48px;
    min-height: 48px;
    font-size: 14px;
  }

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const HandoutActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 24px;
  min-width: 0;
  border-top: 1px solid var(--app-border, #dedede);
  padding-top: 16px;

  button {
    flex: 0 0 auto;
    height: 40px;
    min-height: 40px;
    padding: 0 13px;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
  }

  button svg { flex-shrink: 0; font-size: 14px; }
`;



const HandoutExportActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  margin-left: auto;

  @media (max-width: 540px) {
    width: 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
`;

const INSERT_FLASH_MS = 3000;
const insertFlashFrame = keyframes`
  0%, 70% { outline-color: #1a73e8; background-color: rgba(26, 115, 232, 0.12); }
  100% { outline-color: transparent; background-color: transparent; }
`;
const handoutPanelSpin = keyframes`to { transform: rotate(360deg); }`;

const HandoutMainGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, .7fr) minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  min-width: 0;

  /* The block list sets the row height; the content and picker panels match it
     and scroll internally instead of growing past it. */
  > section:nth-child(-n + 3) { align-content: start; }
  > section:nth-child(2) { position: relative; }
  > section:nth-child(2)[data-switching]::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 20;
    border-radius: inherit;
    background: color-mix(in srgb, var(--app-surface, #fff) 72%, transparent);
  }
  > section:nth-child(2)[data-switching]::after {
    content: "";
    position: absolute;
    top: min(50%, 220px);
    left: 50%;
    z-index: 21;
    width: 26px;
    height: 26px;
    margin: -15px 0 0 -15px;
    border: 3px solid var(--app-muted-text, #5f6368);
    border-right-color: transparent;
    border-radius: 999px;
    animation: ${handoutPanelSpin} .7s linear infinite;
    will-change: transform;
  }
  > section:nth-child(1) { min-height: 520px; }
  > section:nth-child(2), > section:nth-child(3) {
    align-self: stretch;
    contain: size;
    overflow: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
  }

  [data-handout-layout="medium"] &, [data-handout-layout="narrow"] & {
    grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);

    > section:nth-child(3) {
      grid-column: 1 / -1;
      align-self: start;
      contain: none;
      overflow: visible;
    }
  }

  [data-handout-layout="narrow"] & {
    grid-template-columns: minmax(0, 1fr);

    > section:nth-child(2), > section:nth-child(3) {
      grid-column: auto;
      align-self: start;
      contain: none;
      overflow: visible;
    }
  }
`;

const HandoutBusyIndicator = styled.div`
  position: fixed;
  top: 14px;
  right: 16px;
  z-index: 1200;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 7px;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 999px;
  background: var(--app-surface, #fff);
  color: var(--app-muted-text, #5f6368);
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .12);
  pointer-events: none;

  > span:first-child { width: 12px; height: 12px; }
`;

const HandoutFontCredit = styled.div`
  margin-top: 8px;
  text-align: center;
  font-size: 11px;
  color: var(--app-muted-text, #6b7280);
  a { color: inherit; }
`;

const HandoutOutlineHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  > h3 { margin-bottom: 0; }
`;

const HandoutOutlineVisibilityButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 6px;
  background: transparent;
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
  cursor: pointer;
  &:hover { background: var(--app-surface-soft, #f4f5f7); }
  &:focus-visible { outline: 2px solid var(--app-nav-active-bg, #202124); outline-offset: 2px; }
`;

const HandoutOutlineList = styled.div`
  padding: 6px 0;
  display: grid;
  align-content: start;
  gap: 8px;
  max-height: min(70vh, 680px);
  border-radius: 8px;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
`;

const HandoutOutlineActions = styled.div`
  display: grid;
  gap: 8px;
  > button {
    width: 100%;
    box-sizing: border-box;
  }
`;

const HandoutOutlineItem = styled.div`
  position: relative;
  cursor: ${({ draggable }) => draggable ? "grab" : "default"};
  opacity: ${({ $dragging }) => $dragging ? 0.5 : 1};
  transition: opacity 0.15s ease;
  &::before, &::after {
    content: "";
    position: absolute;
    left: 8px;
    right: 8px;
    height: 3px;
    border-radius: 999px;
    background: var(--app-nav-active-bg, #202124);
    pointer-events: none;
    transition: opacity 0.12s ease, transform 0.12s ease;
  }
  &::before {
    top: -6px;
    opacity: ${({ $insertBefore }) => $insertBefore ? 1 : 0};
    transform: ${({ $insertBefore }) => $insertBefore ? "scaleX(1)" : "scaleX(0.96)"};
  }
  &::after {
    bottom: -6px;
    opacity: ${({ $insertAfter }) => $insertAfter ? 1 : 0};
    transform: ${({ $insertAfter }) => $insertAfter ? "scaleX(1)" : "scaleX(0.96)"};
  }
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  min-height: 58px;
  border: 1px solid
    ${({ $active, $drop }) =>
    $active || $drop ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #e5e7eb)"};
  border-radius: 6px;
  padding: 6px;
  background: ${({ $active, $drop }) =>
    $active || $drop ? "var(--app-surface-soft, #f4f5f7)" : "var(--app-input-bg, #fff)"};
  color: var(--app-input-text, #202124);
  text-align: left;
  ${({ $break }) => $break && `
    min-height: 34px;
    padding: 2px 6px;
    border-style: dashed;
    border-radius: 4px;
    &[data-outline-kind="section_break"] { border-left: 3px double var(--app-muted-text, #7c6b99); }
    > button:not([data-outline-delete]) { display: flex; align-items: center; gap: 8px; min-height: 28px; padding: 2px 4px; }
    > button strong { font-size: 12px; font-weight: 700; }
    > button span { font-size: 11px; font-weight: 400; }
    > span { flex-basis: 18px; }
    > button[data-outline-delete] { width: 24px; height: 24px; flex-basis: 24px; }
  `}
`;

const HandoutOutlineGrip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  align-self: stretch;
  color: var(--app-muted-text, #6b7280);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const HandoutOutlineSelect = styled.button`
  display: grid;
  flex: 1 1 auto;
  gap: 4px;
  min-width: 0;
  min-height: 44px;
  padding: 4px;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;

  strong,
  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 14px;
    font-weight: 900;
  }

  span {
    color: var(--app-muted-text, #6b7280);
    font-size: 12px;
    font-weight: 800;
  }
`;

const HandoutOutlineDelete = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: var(--app-muted-text, #6b7280);
  cursor: pointer;

  &:hover {
    background: var(--app-border, #e5e7eb);
    color: var(--app-surface-text, #202124);
  }
`;
const PendingHandoutOutlineDelete = withPendingFeedback(HandoutOutlineDelete);

const HandoutQuestionBreakButton = styled.button`
  width: 100%;
  padding: 7px;
  border: 1px dashed ${({ $active }) => $active ? "#e19631" : "var(--app-border, #9ca3af)"};
  border-radius: 6px;
  background: ${({ $active }) => $active ? "#fff3db" : "transparent"};
  color: ${({ $active }) => $active ? "#8a4b00" : "var(--app-muted-text, #6b7280)"};
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
`;

const SectionStateCard = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
`;

const handoutEditorScroll = css`
  max-height: min(var(--handout-two-card-height, 620px), 70dvh);
  overflow-y: auto;
  scrollbar-gutter: stable;
  overscroll-behavior-y: contain;
`;

const HandoutFocusEditor = styled(SectionStateCard)`
  ${handoutEditorScroll}

  /* Wide and medium layouts scroll the whole equal-height content panel; a
     second inner scroller there clips the lower fields. */
  [data-handout-layout="wide"] &, [data-handout-layout="medium"] & {
    max-height: none;
    overflow: visible;
  }

  ${TextareaWithVim} > textarea {
    min-height: 240px;
    resize: vertical;
  }
`;

const TemplateWordPanel = styled.div`
  display: grid;
  gap: 8px;
  min-width: 0;
`;

const TemplateWordItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  min-width: 0;
  padding: 10px;
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 6px;
  background: var(--app-surface-soft, #f7f8fa);

  strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--app-surface-text, #202124);
    font-size: 13px;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const TemplateWordButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const TocPreviewList = styled.ol`
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    min-width: 0;
    padding: 9px 10px;
    border: 1px solid var(--app-border, #e5e7eb);
    border-radius: 6px;
    background: var(--app-surface-soft, #f7f8fa);
    color: var(--app-subtle-text, #3c4043);
    font-size: 13px;
    font-weight: 900;
  }

  strong,
  span {
    min-width: 0;
  }

  strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    flex: 0 0 auto;
    color: var(--app-muted-text, #6b7280);
  }
`;

const HandoutDropZone = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  min-height: 160px;
  border: 1px dashed
    ${({ $active }) => ($active ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #cfd4dc)")};
  border-radius: 8px;
  padding: 10px;
  background: ${({ $active }) =>
    $active ? "var(--app-surface-soft, #f4f5f7)" : "var(--app-input-bg, #fff)"};
  ${handoutEditorScroll}
`;

const HandoutDropHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${({ $compact }) => ($compact ? "34px" : "82px")};
  border: 1px dashed var(--app-border, #d5d7db);
  border-radius: 6px;
  color: var(--app-muted-text, #6b7280);
  font-size: 13px;
  font-weight: 900;
  text-align: center;
`;

const HandoutInsertMarker = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 18px;
  color: var(--app-nav-active-bg, #202124);
  font-size: 12px;
  font-weight: 900;

  &::before,
  &::after {
    content: "";
    flex: 1 1 auto;
    height: 2px;
    border-radius: 999px;
    background: currentColor;
  }

  span {
    flex: 0 0 auto;
    border: 1px solid currentColor;
    border-radius: 999px;
    padding: 2px 8px;
    background: var(--app-surface, #fff);
  }
`;

const HandoutSectionQuestionCard = styled(SelectedItem)`
  position: relative;
  cursor: grab;
  opacity: ${({ $dragging }) => ($dragging ? 0.5 : 1)};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    opacity 0.15s ease;

  &:hover {
    border-color: var(--app-border-strong, #9aa0a6);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  }

  &:active {
    cursor: grabbing;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 8px;
    right: 8px;
    height: 3px;
    border-radius: 999px;
    background: var(--app-nav-active-bg, #202124);
    opacity: 0;
    pointer-events: none;
    transform: scaleX(0.96);
    transition:
      opacity 0.12s ease,
      transform 0.12s ease;
  }

  &::before {
    top: -6px;
    opacity: ${({ $insertBefore }) => ($insertBefore ? 1 : 0)};
    transform: ${({ $insertBefore }) => ($insertBefore ? "scaleX(1)" : "scaleX(0.96)")};
  }

  &::after {
    bottom: -6px;
    opacity: ${({ $insertAfter }) => ($insertAfter ? 1 : 0)};
    transform: ${({ $insertAfter }) => ($insertAfter ? "scaleX(1)" : "scaleX(0.96)")};
  }

`;

const SectionActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const HandoutHint = styled.div`
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
`;

const HandoutFilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 112px), 1fr));
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;

  > * {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  ${SearchBox} {
    grid-column: 1 / -1;
    width: 100%;
  }

  select {
    width: 100%;
    min-width: 0;
    min-height: 38px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    padding: 0 10px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
    font: inherit;
  }

  > button {
    grid-column: 1 / -1;
    justify-self: end;
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const HandoutQuestionPool = styled.div`
  display: grid;
  gap: 9px;
  max-height: 640px;
  border-radius: 8px;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
`;

const HandoutPoolQuestion = styled.article`
  display: grid;
  gap: 10px;
  min-width: 0;
  padding: 11px;
  border: 1px solid
    ${({ $selected }) => ($selected ? "var(--app-border-strong, #9aa0a6)" : "var(--app-border, #e5e7eb)")};
  border-radius: 8px;
  background: ${({ $selected }) =>
    $selected ? "var(--app-surface-soft, #f4f5f7)" : "var(--app-surface, #fff)"};
  color: var(--app-surface-text, #202124);
  cursor: zoom-in;

  &:focus-visible {
    outline: 2px solid var(--app-nav-active-bg, #202124);
    outline-offset: 3px;
  }
`;

const HandoutBuilderGrid = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 360px) minmax(0, 320px) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  margin-top: 14px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const HandoutPanel = styled.section`
  display: grid;
  gap: 13px;
  min-width: 0;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 8px;
  padding: 14px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
`;

const HandoutPreviewPanel = styled(HandoutPanel)`
  grid-column: 1 / -1;
  /* Break out of the 1180px page column so two A4 pages stay readable;
     48px keeps clear of the viewport edges and a vertical scrollbar. */
  margin-inline: calc((100% - min(100vw - 48px, 1800px)) / 2);
`;

const PreviewPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const PreviewTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 420px;
  min-width: 0;
  flex-wrap: wrap;
`;

const PreviewTitleText = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
  > small { color: var(--app-muted-text, #5f6368); font-size: 12px; }
`;

const PreviewCountBadge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--app-surface-soft, #f1f3f4);
  color: var(--app-subtle-text, #5f6368);
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const PreviewSearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: ${({ $compact }) => ($compact ? "0 1 220px" : "1 1 260px")};
  max-width: ${({ $compact }) => ($compact ? "240px" : "380px")};
  min-width: 160px;
  height: 34px;
  box-sizing: border-box;
  padding: 0 6px 0 12px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 999px;
  background: var(--app-input-bg, #fff);
  color: var(--app-subtle-text, #64748b);
  font-size: 13px;
  transition: border-color .15s, box-shadow .15s;
  &:focus-within { border-color: #1a73e8; box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15); }
  > svg { flex-shrink: 0; font-size: 12px; }
  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    padding: 0;
    background: transparent;
    color: var(--app-input-text, #202124);
    font: inherit;
    &::placeholder { color: var(--app-muted-text, #9aa0a6); }
  }
  > span {
    flex-shrink: 0;
    padding-right: 2px;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    &[data-empty] { color: var(--app-danger-text, #b3261e); }
  }
`;

const PreviewSearchStep = styled.button`
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--app-subtle-text, #5f6368);
  font-size: 11px;
  cursor: pointer;
  &:hover { background: var(--app-surface-soft, #f1f3f4); }
  &:focus-visible { outline: 2px solid #1a73e8; outline-offset: 1px; }
`;

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// Joined buttons: undo/redo, page layout. Pressed state reads as selected.
const PreviewSegment = styled.div`
  display: inline-flex;
  > button {
    min-height: 34px;
    padding: 0 12px;
    font-size: 13px;
    border-radius: 0;
    margin-left: -1px;
  }
  > button:first-child { margin-left: 0; border-radius: 8px 0 0 8px; }
  > button:last-child { border-radius: 0 8px 8px 0; }
  > button:only-child { border-radius: 8px; }
  > button[aria-pressed="true"] {
    position: relative;
    z-index: 1;
    border-color: var(--app-nav-active-bg, #202124);
    background: var(--app-nav-active-bg, #202124);
    color: var(--app-nav-active-text, #fff);
  }
`;

const PreviewPageJump = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 34px;
  box-sizing: border-box;
  padding: 0 10px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 8px;
  background: var(--app-input-bg, #fff);
  color: var(--app-subtle-text, #64748b);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  cursor: text;
  input {
    box-sizing: content-box;
    height: 22px;
    padding: 0 4px;
    border: 1px solid transparent;
    border-radius: 5px;
    background: var(--app-surface-soft, #f1f3f4);
    color: var(--app-input-text, #202124);
    font: inherit;
    font-weight: 700;
    text-align: center;
    &:focus { outline: 0; border-color: #1a73e8; background: var(--app-input-bg, #fff); }
  }
`;

const HandoutAssetShelf = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;

  padding: 12px;
  border: 1px solid var(--app-border, #e3e5e8);
  border-radius: 12px;
  background: var(--app-surface-soft, #f8f9fa);

  > strong { display: inline-flex; align-items: center; gap: 6px; margin-right: 4px; font-size: 13px; }
  > strong svg { color: var(--app-subtle-text, #5f6368); }
  > input {
    min-width: 150px;
    max-width: 230px;
    padding: 7px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
  }
`;

const HandoutAssetShelfItems = styled.div`
  display: flex;
  gap: 8px;
  flex: 1 0 100%;
  min-width: 0;
  overflow-x: auto;
  padding-bottom: 4px;

  > button {
    display: grid;
    grid-template-columns: 48px minmax(0, 100px);
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    padding: 5px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
    cursor: grab;
  }

  img { width: 48px; height: 48px; object-fit: contain; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; }
`;

const SectionMiniTitle = styled.h4`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: var(--app-subtle-text, #3c4043);
  font-size: 13px;
  font-weight: 900;
`;

const SettingGrid = styled.div`
  display: grid;
  min-width: 0;
  > * { min-width: 0; }
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 155px), 1fr));
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const MarginGrid = styled(SettingGrid)`
  grid-template-columns: repeat(5, minmax(0, 1fr));

  @media (max-width: 1180px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const HeaderFooterGrid = styled(SettingGrid)`
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const ExamSettingsGrid = styled(SettingGrid)`
  grid-template-columns: repeat(3, minmax(0, 1fr));

  ${Field}:nth-last-child(-n + 4) {
    grid-column: 1 / -1;
  }

  textarea {
    min-height: 72px;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ExamPartSettingsPanel = styled.div`
  display: grid;
  grid-column: 1 / -1;
  gap: 8px;
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 6px;
  padding: 10px;
  background: var(--app-surface-soft, #f7f8fa);
`;

const ExamPartSettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  strong {
    color: var(--app-surface-text, #202124);
    font-size: 13px;
    font-weight: 900;
  }

  span {
    color: var(--app-muted-text, #6b7280);
    font-size: 12px;
    font-weight: 800;
  }
`;

const ExamPartSettingsList = styled.div`
  display: grid;
  gap: 8px;
`;

const ExamPartSettingsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(110px, 0.8fr) minmax(82px, 0.45fr) minmax(180px, 1.4fr);
  gap: 8px;
  align-items: end;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const RoleTabs = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  padding-bottom: 2px;
`;

const RoleTabButton = styled.button`
  flex: 0 0 auto;
  min-height: 32px;
  border: 1px solid
    ${({ $active }) => ($active ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #d5d7db)")};
  border-radius: 6px;
  padding: 0 10px;
  background: ${({ $active }) =>
    $active ? "var(--app-nav-active-bg, #202124)" : "var(--app-input-bg, #fff)"};
  color: ${({ $active }) =>
    $active ? "var(--app-nav-active-text, #fff)" : "var(--app-input-text, #202124)"};
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
`;

const SelectedIconSlots = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const SelectedIconSlot = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 70px 24px;
  gap: 6px;
  align-items: center;
  min-height: 46px;
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 6px;
  padding: 6px;
  background: ${({ $empty }) =>
    $empty ? "var(--app-surface-soft, #f7f8fa)" : "var(--app-surface, #fff)"};

  span,
  small {
    min-width: 0;
    color: var(--app-subtle-text, #3c4043);
    font-size: 12px;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  img {
    width: 70px;
    height: 36px;
    object-fit: contain;
    background: #fff;
    border-radius: 3px;
  }

  button {
    width: 24px;
    height: 24px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
    cursor: pointer;
  }
`;

const IconGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  max-height: 420px;
  overflow: auto;
`;

const IconTile = styled.button`
  display: grid;
  grid-template-rows: 74px auto;
  gap: 5px;
  min-width: 0;
  border: 1px solid
    ${({ $selected }) => ($selected ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #e5e7eb)")};
  border-radius: 6px;
  padding: 7px;
  background: ${({ $selected }) =>
    $selected ? "var(--app-surface-soft, #f4f5f7)" : "var(--app-input-bg, #fff)"};
  color: var(--app-input-text, #202124);
  cursor: pointer;

  img {
    width: 100%;
    height: 74px;
    object-fit: contain;
    background: #fff;
    border-radius: 3px;
  }

  span {
    min-width: 0;
    font-size: 11px;
    font-weight: 800;
    overflow-wrap: anywhere;
    white-space: normal;
  }
`;

const PageChrome = styled.div`
  display: grid;
  gap: 6px;
  min-width: 0;
  padding: ${({ $plain }) => $plain ? "12px 0" : "10px"};
  border: ${({ $plain }) => $plain ? "none" : "1px solid var(--app-border, #d5d7db)"};
  border-radius: 8px;
  background: ${({ $plain }) => $plain ? "transparent" : "var(--app-surface-soft, #eef1f5)"};
`;

const HandoutPreviewNavigation = styled.div`
  position: relative;
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  margin: 0 auto;
  padding: 0 52px;
  box-sizing: border-box;
  &[data-reader-edge-navigation="true"] > button { width: 44px; height: 44px; opacity: .6; background: rgba(145, 150, 160, .17); }
  &[data-reader-edge-navigation="true"] > button:hover { opacity: 1; }

`;

const PreviewPageButton = styled.button`
  position: absolute;
  top: 50%;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--app-surface-soft, #eef1f5);
  color: var(--app-subtle-text, #64748b);
  cursor: pointer;
  font-size: 14px;
  transform: translateY(-50%);
  transition: color 150ms ease, background 150ms ease;

  &:first-child { left: 0; }
  &:last-child { right: 0; }

  &:hover:not(:disabled) {
    background: var(--app-surface, #fff);
    color: var(--app-surface-text, #202124);
  }

  &:focus-visible {
    outline: 2px solid var(--app-subtle-text, #64748b);
    outline-offset: 3px;
  }

  &:disabled {
    opacity: 0.2;
    cursor: default;
  }
`;

const PreviewPageIndicator = styled.span`
  padding: 0 4px;
  color: var(--app-subtle-text, #64748b);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const A4PreviewViewport = styled.div`
  position: relative;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  margin: 0 auto;
`;

const A4PreviewTrack = styled.div`
  position: absolute;
  top: 0;
  height: ${handoutPageHeight}px;
  transform-origin: top left;
`;

const A4PageBackdrops = styled.div`
  display: flex;
  gap: ${handoutPageGap}px;
  height: ${handoutPageHeight}px;
`;

const A4Paper = styled.div`
  position: relative;
  box-sizing: border-box;
  flex: 0 0 ${handoutPageWidth}px;
  width: ${handoutPageWidth}px;
  height: ${handoutPageHeight}px;
  border: 1px solid #d3d8df;
  background: #fff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  color: #4b5563;

  > div {
    position: absolute;
    left: var(--preview-left, 54px);
    right: var(--preview-right, 54px);
  }

  > div:first-child { top: 26px; }
  > div:last-child { bottom: 25px; }
`;

const A4PreviewFlow = styled.div`
  position: absolute;
  top: 80px;
  left: 54px;
  width: ${handoutPageContentWidth}px;
  height: ${handoutPageHeight - 160}px;
  color: #161616;
  column-width: ${handoutPageContentWidth}px;
  column-gap: ${handoutColumnGap}px;
  column-fill: auto;
  overflow: visible;
  overflow-wrap: anywhere;

  > * { max-width: 100%; }
  h1, h2, h3, h4, h5, h6 { font-family: var(--handout-heading-font, inherit); }
  > div:first-child { column-span: none; }
  [data-preview-pagination-mark="true"] [data-handout-question-number]::before {
    content: ""; display: block; position: absolute; top: .55em; left: -.75em;
    width: .25em; height: .25em; background: #161616;
  }
  @media print {
    [data-preview-pagination-mark="true"] [data-handout-question-number]::before { display: none; }
  }
  [data-preview-editable="true"] {
    cursor: grab;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }
  [data-preview-editable="true"]:hover,
  [data-preview-editable="true"]:focus-visible {
    outline: 1px solid #9ca3af;
    outline-offset: 3px;
    background-color: rgba(156, 163, 175, 0.07);
  }
  [data-preview-dragging="true"] { opacity: .45; cursor: grabbing; }
  [data-preview-insert="before"] { box-shadow: inset 0 3px #64748b; }
  [data-preview-insert="after"] { box-shadow: inset 0 -3px #64748b; }

`;

const HandoutPreviewHeading = styled.h5`
  margin: 0.9em 0 0.6em;
  font-family: var(--handout-heading-font, inherit);
  font-size: var(--handout-heading-size, 1.15em);
  font-weight: 900;
  break-after: avoid;
`;

const HandoutPreviewImageBlock = styled.div`
  margin: 1em 0;
  outline: ${({ $selected }) => $selected ? "2px solid #4f8de8" : "none"};
  cursor: pointer;
`;

const HandoutImageLine = styled.div`
  overflow-wrap: anywhere;
`;

const HandoutImageText = styled.div`
  display: ${({ as }) => as === "span" ? "inline-block" : "block"};
  font-size: ${({ $fontSize }) => $fontSize ? `${$fontSize}pt` : "inherit"};
  position: relative;
  top: ${({ $offset }) => -($offset || 0)}pt;
  margin-top: 0.5em;
  text-align: left;
  overflow-wrap: anywhere;
`;

const HandoutPreviewQuestionImages = styled.div`
  display: grid;
  gap: 0.5em;
  margin-top: 0.5em;
  min-width: 0;
`;

const HandoutQuestionImageRow = styled.div`
  min-width: 0;
  text-align: right;
  break-inside: avoid;
`;

const HandoutResizableImage = styled.div`
  position: relative;
  isolation: isolate;
  img { position: relative; z-index: -1; }
  figure { margin: 0; border: 0; padding: 0; }
  display: inline-block;
  max-width: 100%;
  vertical-align: ${({ $inline }) => $inline ? "baseline" : "top"};
  break-inside: avoid;
  text-align: center;

  img { display: block; width: 100%; height: auto; }

  button {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 26px;
    height: 26px;
    border: 1px solid #64748b;
    border-radius: 5px;
    background: #fff;
    color: #1f2937;
    cursor: ew-resize;
    touch-action: none;
  }

  small {
    position: absolute;
    left: 0;
    bottom: 0;
    padding: 2px 4px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.9);
    font-size: 10px;
  }
`;

const PreviewHeaderLine = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  color: #161616;
  font-family: ${({ $wordLayout }) => $wordLayout ? '"微軟正黑體", "Microsoft JhengHei", "PingFang TC", sans-serif' : '"Times New Roman", "HandoutKai", serif'};
  font-size: ${({ $wordLayout }) => $wordLayout ? "12pt" : "10pt"};
  font-weight: 400;

  span:last-child {
    text-align: right;
  }
`;

const PreviewFooterLine = styled(PreviewHeaderLine)`
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;

  span:nth-child(2) {
    min-width: 28px;
    text-align: center;
    font-family: "Times New Roman", serif;
  }
`;

const PreviewIcon = styled.img`
  width: 1.35em;
  height: 1.35em;
  object-fit: contain;
  vertical-align: -0.24em;
  margin-right: 0.28em;
`;

const FocusImageEditor = styled.div`
  min-width: 0;
  input, select, textarea { min-width: 0; max-width: 100%; box-sizing: border-box; }
  display: grid;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 8px;
  background: var(--app-surface-soft, #f7f8fa);
  > strong { overflow-wrap: anywhere; }
`;

const FocusPreviewBlock = styled.section`
  margin: 0 0 10pt;
  padding: ${({ $framed }) => $framed ? "0" : "10pt 12pt"};
  background: ${({ $framed }) => $framed ? "#fff" : "#f0f0f0"};
  border: 0;
  border-left: ${({ $framed }) => $framed ? "0" : "4pt solid #9ca3af"};

  h5 { margin: 0 0 6pt; }
  p { margin: 0 0 4pt; }
  ol, ul { margin: 0 0 4pt; padding-left: 0.2in; list-style-position: inside; }
  li { margin-bottom: 4pt; }
  ${({ $wordLayout }) => $wordLayout && `
    [data-focus-content] h1, [data-focus-content] h2, [data-focus-content] h3,
    [data-focus-content] h4, [data-focus-content] h5, [data-focus-content] h6 {
      font-size: inherit; line-height: 1.25; font-family: inherit;
    }
  `}
`;

const HandoutBreakPreview = styled.div`
  position: relative;
  break-before: column;
  break-inside: avoid;
  height: 0;
  min-height: 0;
  margin: 0;
  padding: 0;
  border: 0;
  line-height: 0;
  > span {
    display: ${({ $showMarks = true }) => $showMarks ? "block" : "none"};
    position: absolute;
    bottom: 2pt;
    left: 0;
    font: 10px/1.2 sans-serif;
    color: #6b7280;
    border-bottom: 1px dashed #9ca3af;
    pointer-events: none;
  }
  @media print { > span { display: none; } }
`;

const ExamPreviewStack = styled.div`
  display: grid;
  gap: ${({ $compact }) => ($compact ? "8px" : "12px")};
  min-width: 0;
  max-height: ${({ $compact }) => ($compact ? "420px" : "100%")};
  overflow: auto;
`;

const ExamTemplatePage = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: ${({ $compact }) => ($compact ? "6px" : "10px")};
  width: 100%;
  aspect-ratio: 210 / 297;
  max-height: ${({ $compact }) => ($compact ? "420px" : "760px")};
  min-height: 0;
  padding: ${({ $compact }) => ($compact ? "12px 14px" : "24px 30px")};
  border: 1px solid var(--app-border, #dedede);
  border-radius: 6px;
  box-sizing: border-box;
  background: #fff;
  color: #161616;
  font-family: ${handoutKaiFontFamily};
  font-size: ${({ $compact }) => ($compact ? "10px" : "14px")};
  line-height: 1.45;
  box-shadow: inset 0 0 0 1px #f3f4f6;
  overflow: hidden;
`;

const ExamTemplateHeader = styled.div`
  color: #30343a;
  font-size: 0.9em;
  font-weight: 800;
`;

const ExamTemplateFooter = styled.div`
  min-height: 1.4em;
  color: #30343a;
  font-size: 0.86em;
  font-weight: 800;
  text-align: center;
`;

const ExamInstructionTemplateBody = styled.div`
  position: relative;
  display: grid;
  gap: 1.1em;
  min-height: 0;
  overflow: hidden;
`;

const ExamIdentityLine = styled.div`
  margin-top: 0.6em;
  text-align: center;
  color: #374151;
  font-size: 0.82em;
  font-weight: 800;
  letter-spacing: 0;
`;

const ExamCoverTitle = styled.div`
  text-align: center;
  font-size: 1.28em;
  font-weight: 900;
`;

const ExamBrandPlate = styled.div`
  justify-self: center;
  min-width: 11em;
  border: 0.18em solid #9ca3af;
  box-shadow: 0 0 0 0.08em #d1d5db;
  padding: 0.26em 1em;
  background: #111111;
  color: #ffffff;
  font-family: "Times New Roman", "PMingLiU", serif;
  font-size: 1.28em;
  font-weight: 900;
  text-align: center;
`;

const ExamNoticeBox = styled.div`
  display: grid;
  gap: 0.35em;
  border: 1px solid #111827;
  padding: 0.72em 1em;
  text-align: center;
  font-weight: 900;
`;

const ExamInfoBox = styled.div`
  position: relative;
  display: grid;
  gap: 0.52em;
  min-height: 300px;
  border: 1px solid #111827;
  padding: 1.8em 2.2em;
  overflow: hidden;

  strong,
  span {
    position: relative;
    z-index: 1;
  }

  strong {
    font-weight: 900;
  }
`;

const ExamWatermark = styled.div`
  position: absolute;
  inset: 8% 0 auto;
  z-index: 0;
  color: rgba(148, 163, 184, 0.22);
  font-family: "Times New Roman", serif;
  font-size: 18em;
  font-weight: 900;
  line-height: 0.75;
  text-align: center;
  pointer-events: none;
`;

const ExamSignatureBox = styled.div`
  justify-self: end;
  width: min(17em, 48%);
  min-height: 2.6em;
  border: 1px solid #111827;
`;

const ExamQuestionTemplateBody = styled.div`
  display: grid;
  grid-template-rows: auto repeat(${examPreviewQuestionsPerPage}, minmax(0, 1fr));
  gap: ${({ $compact }) => ($compact ? "5px" : "8px")};
  min-height: 0;
  overflow: hidden;
`;

const ExamQuestionPartTitle = styled.div`
  font-weight: 900;
`;

const ExamQuestionSlot = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: 4px;
  min-height: 0;
  padding-bottom: 4px;
  border-bottom: 1px solid #e5e7eb;
`;

const ExamQuestionLine = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 5px;
  min-height: 0;
  overflow: hidden;
`;

const ExamQuestionNumber = styled.span`
  font-weight: 900;
`;

const ExamQuestionBody = styled.div`
  min-width: 0;
  overflow: hidden;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  .katex {
    font-size: 1em;
  }

  .katex-display {
    margin: 2px 0;
  }
`;

const ExamQuestionPlaceholder = styled.span`
  color: #9ca3af;
  font-size: 1.25em;
  font-weight: 900;
`;

const ExamSlotAnswer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 1.25em;
  color: #111827;
  font-size: 0.92em;
  font-weight: 800;
  overflow: hidden;

  strong {
    color: #c00000;
    font-family: "Times New Roman", serif;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));
`;

const PreviewDialog = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: 14px;
  width: min(1080px, 100%);
  max-height: min(860px, calc(100vh - 36px));
  border: 1px solid var(--app-border, #dedede);
  border-radius: 8px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  padding: 16px;
`;

const HandoutSettingsDialog = styled(PreviewDialog)`
  width: min(1160px, 100%);
`;

const TemplatePreviewDialog = styled(PreviewDialog)`
  width: min(980px, 100%);
`;

const TemplatePreviewFrame = styled.iframe`
  width: 100%;
  min-height: min(720px, calc(100vh - 180px));
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  background: #fff;
`;

const HandoutSettingsBody = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(300px, 0.75fr);
  gap: 14px;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const HandoutSettingsColumn = styled.section`
  display: grid;
  align-content: start;
  gap: 12px;
  min-width: 0;
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const DialogTitle = styled.h3`
  margin: 0;
  color: var(--app-surface-text, #202124);
  font-size: 19px;
`;

const DialogMeta = styled.div`
  margin-top: 3px;
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
  font-weight: 800;
`;

const PreviewDialogBody = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 340px) minmax(0, 1fr);
  gap: 14px;
  min-height: 0;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewOrderPanel = styled.section`
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 8px;
  min-height: 0;
`;

const PreviewPanelTitle = styled.h4`
  margin: 0;
  font-size: 14px;
`;

const PreviewSettingsPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  min-width: 0;
  padding: 10px;
  border: 1px solid var(--app-border, #eceff1);
  border-radius: 6px;
  background: var(--app-surface-soft, #f7f8fa);

  ${PreviewPanelTitle} {
    grid-column: 1 / -1;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewSettingField = styled.label`
  display: grid;
  gap: 5px;
  grid-column: ${({ $wide }) => ($wide ? "1 / -1" : "auto")};
  min-width: 0;
  color: var(--app-subtle-text, #3c4043);
  font-size: 12px;
  font-weight: 900;

  input,
  select {
    min-width: 0;
    min-height: 34px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    padding: 0 8px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
    font: inherit;
  }
`;

const PreviewOrderList = styled.div`
  display: grid;
  align-content: start;
  gap: 8px;
  min-height: 0;
  max-height: 100%;
  border-radius: 8px;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
`;

const PreviewOrderItem = styled.div`
  display: grid;
  grid-template-columns: 18px auto minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  min-width: 0;
  padding: 8px;
  border: 1px solid var(--app-border, #eceff1);
  border-top-color: ${({ $dragOver }) =>
    $dragOver ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #eceff1)"};
  border-top-width: ${({ $dragOver }) => ($dragOver ? "3px" : "1px")};
  border-radius: 6px;
  background: ${({ $dragging }) =>
    $dragging ? "var(--app-surface-soft, #f4f5f7)" : "var(--app-surface, #fff)"};
  opacity: ${({ $dragging }) => ($dragging ? 0.64 : 1)};

  button {
    width: 26px;
    height: 26px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #3c4043);
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
  }
`;

const PreviewOrderText = styled.div`
  max-height: 74px;
  overflow: hidden;
  color: var(--app-subtle-text, #3c4043);
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const HandoutPagePreview = styled.div`
  min-height: 0;
  max-height: 100%;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  column-count: ${({ $layout }) => ($layout === "twoColumn" ? 2 : 1)};
  column-gap: 24px;
  padding: ${({ $layout }) => ($layout === "compact" ? "18px 18px" : "24px 22px")};
  border: 1px solid var(--app-border, #dedede);
  border-radius: 6px;
  background: #fff;
  color: #161616;
  font-family: ${({ $fontFamily }) => $fontFamily || "inherit"};
  font-size: ${({ $bodySize }) => $bodySize || 12}px;
  line-height: ${({ $lineHeight }) => $lineHeight || 1.55};
  box-shadow: inset 0 0 0 1px #f3f4f6;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const HandoutTitle = styled.div`
  margin-bottom: 24pt;
  text-align: center;
  font-family: var(--handout-heading-font, inherit);
  font-size: ${({ $titleSize }) => $titleSize || 17}pt;
  font-weight: 900;
  column-span: all;
`;

const HandoutSectionTitle = styled.h5`
  margin: 0;
  font-family: var(--handout-heading-font, inherit);
  font-size: var(--handout-heading-size, 1.15em);
  font-weight: 900;
  break-after: avoid;
`;

const HandoutQuestion = styled.div`
  display: grid;
  gap: 0.4em;
  padding-left: 0;
  margin-top: 10pt;
  line-height: inherit;
  break-inside: auto;
  &[data-inserted-flash="true"] {
    outline: 3px solid #1a73e8;
    outline-offset: 4px;
    animation: ${insertFlashFrame} ${INSERT_FLASH_MS}ms ease-out forwards;
    @media print { outline: none; animation: none; }
  }
  &[data-word-figure-layout="beside"] {
    grid-template-columns: minmax(0, 1fr) var(--word-figure-width); column-gap: 0.3cm; align-items: start;
    > [data-question-copy] { grid-column: 1; grid-row: 1; }
    grid-template-rows: min-content 1fr;
    > [data-question-figures] { grid-column: 2; grid-row: 1 / span 2; margin-top: 0; }
    > :nth-child(n+3) { grid-column: 1; grid-row: 2; align-self: start; }
  }
`;

const HandoutQuestionText = styled.div`
  display: grid;
  grid-template-columns: 0.25in minmax(0, 1fr);
  gap: 0;
  font-weight: 400;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  .katex {
    font-size: 1em;
  }

  .katex-display {
    margin: 3px 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const HandoutQuestionNumber = styled.span`
  position: relative;
  font-weight: 400;
`;

const HandoutQuestionBody = styled.div`
  min-width: 0;
`;

const MarkdownPreviewTable = styled.table`
  width: auto;
  max-width: 100%;
  margin: 0.55em 0;
  border-collapse: collapse;
  table-layout: auto;
  font-size: 1em;

  td {
    min-width: 3.6em;
    border: 1px solid #9ca3af;
    padding: 0.34em 0.58em;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
  }
`;

const HandoutAnswer = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  color: #111827;
  font-weight: 800;
`;

const AnswerSourceRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
  width: 100%;
  margin: 4pt 0;
  > div { min-width: 0; overflow-wrap: anywhere; }
`;

const AnswerSourceText = styled.div`
  ${({ $compact }) => $compact && "position: absolute; top: 0; right: 0; width: 50%;"}
  color: #111827;
  text-align: right;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-weight: normal;
  font-size: 12pt;
  line-height: 1;
`;

const HandoutSolution = styled.div`
  color: #374151;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const HandoutResponsePreview = styled.div`
  color: #ff0000;
  padding-left: ${({ $mixed }) => $mixed ? "0" : "0.25in"};
  > div { color: inherit; }
  > div:not(:empty) { margin: ${({ $mixed, $compact }) => $mixed && !$compact ? "4pt 0 12pt" : "4pt 0"}; }
  p { margin: 0 0 4pt; }
`;

const HandoutEmpty = styled.div`
  padding: 28px 0;
  color: #6b7280;
  text-align: center;
  font-weight: 800;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 12px;
  border: 1px solid
    ${({ $primary }) => ($primary ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #d5d7db)")};
  border-radius: 6px;
  background: ${({ $primary }) =>
    $primary ? "var(--app-nav-active-bg, #202124)" : "var(--app-input-bg, #fff)"};
  color: ${({ $primary }) =>
    $primary ? "var(--app-nav-active-text, #fff)" : "var(--app-input-text, #202124)"};
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// Handout editor actions show a spinner until their re-render is committed.
const HandoutActionButton = withPendingFeedback(IconButton);

const IconOnlyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  cursor: pointer;
`;

const EmptyState = styled.div`
  padding: 18px;
  color: currentColor;
  opacity: 0.72;
  font-weight: 800;
  text-align: center;
`;


// Shared by the editable builder and saved-record reader.
function HandoutBuilderA4Preview({
  handoutPreviewMode,
  handoutPreviewPage,
  handoutPreviewPageCount,
  handoutPreviewScale,
  setHandoutPreviewPage,
  draggedHandoutAsset,
  dropHandoutAssetOnPreview,
  handoutContentLeft,
  handoutContentRight,
  handoutContentTop,
  handoutContentBottom,
  handoutPageContentWidth,
  handoutColumnGap,
  handoutOptions,
  handoutLineHeight,
  handoutFlowRef,
  handoutTitle,
  handoutSections,
  activeHandoutSectionId,
  setActiveHandoutSectionId,
  renderHandoutResizableImage,
  updateHandoutImageWidth,
  updateHandoutFocusImage,
  handoutQuestionIndexById,
  updateQuestionImageWidth,
  handoutOutputMode,
  canExportHandout,
  handoutPickerTitle,
  handoutQuestionTargetLabel,
  readOnly = false,
  deferCompactImages = true,
  showFormattingMarks = false,
  immersive = false,
  edgeNavigation = false,
  hidePageNavigation = false,
  onlyVisiblePages = false,
  hidePageLabels = false,
  handoutFlowLabel = "A4 講義內容",
  getHandoutPreviewInteractionProps,
  flashQuestionIds = [],
}) {
    const localFlowRef = useRef(null);
    const attachFlow = useCallback((node) => {
      localFlowRef.current = node;
      if (typeof handoutFlowRef === "function") handoutFlowRef(node);
      else if (handoutFlowRef) handoutFlowRef.current = node;
    }, [handoutFlowRef]);
    const interactiveProps = (section, question, index) => readOnly ? { tabIndex: 0 } : getHandoutPreviewInteractionProps?.(section, question, index) || {};
    const shownPages = handoutPreviewMode === "two" ? 2 : 1;
    const previewStride = handoutPageWidth + handoutPageGap;
    const chapterHeaders = useHandoutChapterHeaders(localFlowRef, handoutSections, handoutOptions, previewStride);
    const backdropStart = onlyVisiblePages ? handoutPreviewPage : 0;
    const backdropCount = onlyVisiblePages ? Math.min(shownPages, handoutPreviewPageCount - backdropStart) : handoutPreviewPageCount;
    return (
      <PageChrome $plain style={immersive ? { padding: 0, gap: 0 } : undefined}>
        <HandoutPreviewFonts />
        <HandoutPreviewNavigation aria-label={hidePageNavigation ? undefined : "講義預覽翻頁"} data-reader-edge-navigation={edgeNavigation || undefined} style={edgeNavigation ? { padding: 0 } : undefined}>
          {!hidePageNavigation && <PreviewPageButton type="button" aria-label="上一頁" title="上一頁（Shift＋Enter）"
            disabled={handoutPreviewPage === 0}
            onClick={() => setHandoutPreviewPage((page) => Math.max(0, page - shownPages))}>
            <FaChevronLeft aria-hidden="true" />
          </PreviewPageButton>}
          <A4PreviewViewport
            data-reader-page-viewport={readOnly || undefined}
            style={{
              width: (shownPages * handoutPageWidth + (shownPages - 1) * handoutPageGap) * handoutPreviewScale,
              height: handoutPageHeight * handoutPreviewScale,
            }}
            onDragOver={(event) => {
              if (draggedHandoutAsset) {
                event.preventDefault();
                event.dataTransfer.dropEffect = "copy";
              }
            }}
            onDrop={dropHandoutAssetOnPreview}
          >
            <A4PreviewTrack style={{
              width: handoutPreviewPageCount * previewStride - handoutPageGap,
              left: -handoutPreviewPage * previewStride * handoutPreviewScale,
              transform: `scale(${handoutPreviewScale})`,
            }}>
              <A4PageBackdrops style={backdropStart ? { paddingLeft: backdropStart * previewStride } : undefined}>
                {Array.from({ length: backdropCount }, (_, offset) => backdropStart + offset).map((pageIndex) => (
                  <A4Paper key={pageIndex} aria-label={hidePageLabels ? undefined : `第 ${pageIndex + 1} 頁`}
                    style={{ "--preview-left": `${handoutContentLeft}px`, "--preview-right": `${handoutContentRight}px` }}>
                    <PreviewHeaderLine data-handout-page-header={pageIndex + 1} $wordLayout={isHandoutWordLayout(handoutOptions)}>
                      {handoutHeaderForPage(handoutOptions, pageIndex, chapterHeaders).map((text, index) => <span key={index}>{text}</span>)}
                    </PreviewHeaderLine>
                    <PreviewFooterLine>
                      {['Left', 'Center', 'Right'].map((position) => {
                        const config = handoutOptions.headerFooter;
                        const even = config.differentOddEvenPages && pageIndex % 2 === 1;
                        const side = config.pageNumberPosition === "center" ? "Center"
                          : config.pageNumberPosition === "none" ? ""
                          : (config.pageNumberPosition === "outer") !== Boolean(even) ? "Right" : "Left";
                        const text = position === "Center" ? "" : config[`${even ? "even" : "odd"}Footer${position}`];
                        return <span key={position}>{text}{position === side ? `${text ? "  " : ""}${pageIndex + 1}` : ""}</span>;
                      })}
                    </PreviewFooterLine>
                  </A4Paper>
                ))}
              </A4PageBackdrops>
              <A4PreviewFlow
                ref={attachFlow}
                data-handout-flow
                aria-label={handoutFlowLabel || undefined}
                data-reader-flow={readOnly || undefined}
                style={{
                  top: handoutContentTop,
                  left: handoutContentLeft,
                  width: handoutPageContentWidth,
                  height: handoutPageHeight - handoutContentTop - handoutContentBottom,
                  columnWidth: handoutPageContentWidth,
                  columnGap: handoutColumnGap,
                  fontFamily: isHandoutWordLayout(handoutOptions)
                    ? `"Times New Roman", ${handoutKaiFontFamily}`
                    : handoutPreviewFontFamilies[handoutOptions.bodyFontName] || handoutPreviewFontFamilies["標楷體"],
                  fontSize: `${Number(handoutOptions.bodyFontSize) || 12}pt`,
                  "--handout-math-font": '"HandoutMathKai", "Cambria Math", "STIX Two Math", "Times New Roman", serif',
                  "--handout-heading-font": handoutPreviewFontFamilies[handoutOptions.headingFontName] || handoutPreviewFontFamilies["微軟正黑體"],
                  "--handout-heading-size": `${Number(handoutOptions.headingFontSize) || 14}pt`,
                  lineHeight: handoutLineHeight,
                }}
              >
                {!isHandoutWordLayout(handoutOptions) && <HandoutTitle $titleSize={Number(handoutOptions.titleFontSize) || 20}>
                  {handoutOptions.icons.chapter && <PreviewIcon src={handoutOptions.icons.chapter.url} alt="" style={handoutIconPreviewStyle(handoutOptions.icons.chapter)} />}
                  <MathText source={handoutTitle || defaultHandoutTitle} fallback="" />
                </HandoutTitle>}
                {!isHandoutWordLayout(handoutOptions) && handoutOptions.focus.unitTitle && handoutOptions.focus.unitTitle !== handoutTitle && (
                  <HandoutPreviewHeading><MathText source={handoutOptions.focus.unitTitle} fallback="" /></HandoutPreviewHeading>
                )}
                {handoutSections.map((section, sectionIndex) =>
                  section.type === "page_break" || section.type === "section_break" ? (
                    needsStandalonePageBreak(section, sectionIndex, handoutSections, handoutOptions) && <HandoutBreakPreview key={section.id} $showMarks={showFormattingMarks} data-handout-section-id={section.id} {...interactiveProps(section)}>
                      <span>{section.title} · 下一頁{section.type === "section_break" ? "開始新節" : "繼續"}</span>
                    </HandoutBreakPreview>
                  ) : section.type === "image" ? (
                    <HandoutPreviewImageBlock key={section.id} data-handout-section-id={section.id}
                      $selected={activeHandoutSectionId === section.id}
                      onClick={readOnly ? undefined : () => setActiveHandoutSectionId(section.id)}
                      title={readOnly ? undefined : "點擊編輯圖片"}
                      style={readOnly ? { cursor: "default" } : undefined}
                      {...interactiveProps(section)}>
                      <HandoutImageLine style={{ textAlign: section.alignment || "center" }}>
                        {renderHandoutResizableImage({
                          src: section.asset?.url,
                          asset: section.asset,
                          alt: section.title,
                          widthCm: section.widthCm || 8,
                          onResize: (width) => updateHandoutImageWidth(section.id, width),
                          label: section.title,
                          inline: section.textLayout === "inline",
                        })}
                        {getHandoutImageTextParts(section).inline && (
                          <HandoutImageText as="span" $fontSize={section.textFontSize} $offset={section.textOffsetPt}> <MathText source={getHandoutImageTextParts(section).inline} fallback="" /></HandoutImageText>
                        )}
                      </HandoutImageLine>
                      {getHandoutImageTextParts(section).below && (
                        <HandoutImageText $fontSize={section.textFontSize} $offset={section.textOffsetPt}><MathText source={getHandoutImageTextParts(section).below} fallback="" /></HandoutImageText>
                      )}
                    </HandoutPreviewImageBlock>
                  ) : section.type === "focus" ? (
                    <FocusPreviewBlock key={section.id} data-handout-section-id={section.id}
                      style={needsChapterPageBreak(section, sectionIndex, handoutSections, handoutOptions) ? { breakBefore: "column" } : undefined}
                      $wordLayout={isHandoutWordLayout(handoutOptions)}
                      $framed={isHandoutWordLayout(handoutOptions) || Boolean(getHandoutFocusFrameUrl(handoutOptions.focus.frame))} {...interactiveProps(section)}>
                      {isHandoutWordLayout(handoutOptions) && section.chapter && <div data-handout-chapter-start={section.id}>
                        <HandoutWordHeading settings={handoutOptions} role="chapter" title={section.chapter.title} number={section.chapter.number} />
                      </div>}
                      {isHandoutWordLayout(handoutOptions) && !section.continuePreviousFocus && <HandoutWordHeading settings={handoutOptions}
                        role="focus" title={getHandoutFocusCaption(section.title)} />}
                      <HandoutFocusFrame frame={handoutOptions.focus.frame} grayAsset={{ url: handoutOptions.focus.grayAssetURL || handoutOptions.focus.gray_asset?.url }} backgroundStyle={isHandoutWordLayout(handoutOptions) ? getHandoutFocusBackgroundStyle(section, handoutOptions) : "frame"}>
                        {!isHandoutWordLayout(handoutOptions) && !section.continuePreviousFocus && <HandoutSectionTitle>
                          {handoutOptions.icons.focus && <PreviewIcon src={handoutOptions.icons.focus.url} alt="" style={handoutIconPreviewStyle(handoutOptions.icons.focus)} />}
                          <MathText source={section.title || "重點整理"} fallback="" />
                        </HandoutSectionTitle>}
                        <HandoutFocusContent section={section} source={getHandoutFocusLines(section, handoutOptions)} wordLayout={isHandoutWordLayout(handoutOptions)}
                          bodyFontSize={handoutOptions.bodyFontSize}
                          maxFloatWidthCm={isHandoutWordLayout(handoutOptions) ? (handoutPageContentWidth / (96 / 2.54) - 28 * 2.54 / 72) * .35 : undefined}
                          renderImage={(image) => {
                            const url = getHandoutQuestionImageUrl(image.asset);
                            return (url || image.asset?.source_code) ? renderHandoutResizableImage({ src: url, asset: image.asset,
                              alt: image.asset.alt_text || image.asset.name || "觀念附圖", widthCm: image.widthCm,
                              label: image.asset.name || "觀念附圖", onResize: (width) => updateHandoutFocusImage?.(section.id, image.id, "widthCm", width),
                            }) : <QuestionAssetPreviewStrip transparentBackground assets={[image.asset]} widthCm={image.widthCm} bodyFontSize={handoutOptions.bodyFontSize} showCaption={false} compact lazyRender={deferCompactImages} />;
                          }} />
                      </HandoutFocusFrame>
                    </FocusPreviewBlock>
                  ) : section.type === "questions" && section.questions.length ? (
                    <React.Fragment key={section.id}>
                      {section.questions.map((question, index) => (
                        <React.Fragment key={getHandoutQuestionOccurrenceKey(section.questions, index)}>
                          {needsQuestionPageBreak(question, index, handoutSections[sectionIndex - 1], handoutOptions) && (
                            <HandoutBreakPreview $showMarks={showFormattingMarks} data-handout-section-id={section.id}>
                              <span>分頁符號 · 第 {index + 1} 題從下一頁開始</span>
                            </HandoutBreakPreview>
                          )}
                          {index === 0 && hasHandoutSectionHeading(section, handoutOptions) && (
                            <HandoutPreviewHeading as={isHandoutWordLayout(handoutOptions) ? "div" : undefined}
                              style={isHandoutWordLayout(handoutOptions) ? { margin: 0 } : undefined}
                              data-handout-section-id={section.id} {...interactiveProps(section)}>
                              {isHandoutWordLayout(handoutOptions) ? <HandoutWordHeading settings={handoutOptions}
                                role={section.heading?.role || "plain"} number={section.heading?.number}
                                title={withoutHandoutScores(section.title || handoutOptions.focus.typeTitle || "")} /> : <>
                                {handoutOptions.icons.type && <PreviewIcon src={handoutOptions.icons.type.url} alt="" style={handoutIconPreviewStyle(handoutOptions.icons.type)} />}
                                <MathText source={withoutHandoutScores(section.title || handoutOptions.focus.typeTitle || "")} fallback="" />
                              </>}
                            </HandoutPreviewHeading>
                          )}
                          <HandoutQuestion data-handout-question-uuid={question.math_bank?.id || question.question_uuid || question.id} data-preview-question-id={question.id} data-inserted-flash={flashQuestionIds.includes(question.id) || undefined} data-handout-section-id={section.id} data-word-figure-layout={isHandoutWordLayout(handoutOptions) && getHandoutBesideFigureWidth(question, handoutOptions) ? "beside" : undefined} data-preview-pagination-mark={(showFormattingMarks && Boolean(question.pageBreakBefore || question.pagination?.keepWithNext || question.pagination?.keepTogether)) || undefined} style={{ ...getQuestionPaginationStyle(question), ...(isHandoutWordLayout(handoutOptions) ? { marginTop: `${handoutOptions.questionSpacingPt ?? 0}pt`, "--word-figure-width": `${getHandoutBesideFigureWidth(question, handoutOptions) || 0}cm` } : {}) }} {...interactiveProps(section, question, index)}>
                            <HandoutQuestionText data-question-copy>
                              <HandoutQuestionNumber data-handout-question-number>{getHandoutQuestionNumber(handoutQuestionIndexById, section.id, index, question.id)}.</HandoutQuestionNumber>
                              <HandoutQuestionBody>
                                {isHandoutWordLayout(handoutOptions) ? <HandoutWordQuestionText source={withoutHandoutScores(getHandoutPreviewPrompt(question))} settings={handoutOptions} widthCm={handoutPageContentWidth / (96 / 2.54) - .7 - (getHandoutBesideFigureWidth(question, handoutOptions) || 0)} /> : <MarkdownAwareMathText source={withoutHandoutScores(getHandoutPreviewPrompt(question))} fallback="" />}
                              </HandoutQuestionBody>
                            </HandoutQuestionText>
                            <HandoutPreviewQuestionImages data-question-figures>
                              {getPreviewableQuestionAssets(getPromptQuestionAssets(question.assets || [])).map((asset, assetIndex) => {
                                const url = getHandoutQuestionImageUrl(asset);
                                if (!url && !asset.source_code) return <AssetPreviewStrip key={asset.id || assetIndex} assets={[asset]} imageAlignments={handoutOptions.questionImageAlignments}
                                  widthCm={isHandoutWordLayout(handoutOptions) ? handoutOptions.questionImageWidths?.[String(asset.id || "")] || 8 : undefined}
                                  compact lazyRender={deferCompactImages} />;
                                const assetId = String(asset.id || "");
                                const widthCm = handoutOptions.questionImageWidths?.[assetId] || 8;
                                const alignment = getQuestionImageAlignment(asset, handoutOptions.questionImageAlignments);
                                return <HandoutQuestionImageRow key={assetId || url} data-question-image-id={assetId} data-question-image-alignment={alignment}
                                  style={{ textAlign: alignment === "inline" ? "left" : alignment }}>
                                  {renderHandoutResizableImage({
                                    src: url, asset,
                                    alt: asset.alt_text || `第 ${getHandoutQuestionNumber(handoutQuestionIndexById, section.id, index, question.id)} 題圖片`,
                                    widthCm,
                                    onResize: (width) => assetId && updateQuestionImageWidth(assetId, width),
                                    label: `第 ${getHandoutQuestionNumber(handoutQuestionIndexById, section.id, index, question.id)} 題圖片`,
                                  })}
                                </HandoutQuestionImageRow>;
                              })}
                            </HandoutPreviewQuestionImages>
                            {handoutOutputMode !== "student" && <HandoutResponsePreview $mixed $compact={!isHandoutWordLayout(handoutOptions)}>
                              <AnswerSourcePreview question={question} widthPx={handoutPageContentWidth} mode={handoutOutputMode} compact={!isHandoutWordLayout(handoutOptions)}>
                                {handoutOutputMode !== "student" && <>
                                  {getHandoutPreviewAnswer(question) && (isHandoutWordLayout(handoutOptions) ? <HandoutWordAnswer settings={handoutOptions} source={getHandoutPreviewAnswer(question)} /> : <HandoutAnswer><strong>【答案】</strong><MarkdownAwareMathText source={getHandoutPreviewAnswer(question)} fallback="" /></HandoutAnswer>)}
                                  <QuestionAssetPreviewStrip assets={question.assets || []} role="answer" showCaption={false} widthCm={8} imageWidths={handoutOptions.questionImageWidths} bodyFontSize={handoutOptions.bodyFontSize} defaultAlignment="right" imageAlignments={handoutOptions.questionImageAlignments} />
                                </>}
                              </AnswerSourcePreview>
                              {["teaching", "teaching_detailed"].includes(handoutOutputMode) && <>
                                {question.solution_md && <HandoutSolution><MarkdownMathText source={question.solution_md} /></HandoutSolution>}
                                <QuestionAssetPreviewStrip assets={question.assets || []} role="solution" showCaption={false} widthCm={8} imageWidths={handoutOptions.questionImageWidths} bodyFontSize={handoutOptions.bodyFontSize} defaultAlignment="right" imageAlignments={handoutOptions.questionImageAlignments} />
                              </>}
                            </HandoutResponsePreview>}
                          </HandoutQuestion>
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ) : null)}
                {!canExportHandout && (
                  <HandoutEmpty>從右側{handoutPickerTitle}拖到左側{handoutQuestionTargetLabel}，預覽會在這裡更新。</HandoutEmpty>
                )}
              </A4PreviewFlow>
            </A4PreviewTrack>
          </A4PreviewViewport>
          {!hidePageNavigation && <PreviewPageButton type="button" aria-label="下一頁" title="下一頁（Enter）"
            disabled={handoutPreviewPage + shownPages >= handoutPreviewPageCount}
            onClick={() => setHandoutPreviewPage((page) => Math.min(handoutPreviewPageCount - 1, page + shownPages))}>
            <FaChevronRight aria-hidden="true" />
          </PreviewPageButton>}
        </HandoutPreviewNavigation>
        {!immersive && (handoutOptions.bodyFontName === "標楷體" || handoutOptions.headingFontName === "標楷體") && (
          <HandoutFontCredit>
            優先使用本機標楷體；無法載入時使用<a href="https://language.moe.gov.tw/material/info?m=9fe3fe82-8bbf-44c0-961d-873ea079e284" target="_blank" rel="noreferrer">中華民國教育部</a>標準楷書（字形與標楷體不同）。
          </HandoutFontCredit>
        )}
      </PageChrome>
    );
}

const SavedHandoutA4Preview = React.memo(HandoutBuilderA4Preview);
const savedHandoutNoop = () => {};
const readerRowGap = 24;
const readerStackPadding = 12;

// Saved records open as two-page spreads; initialLayout picks another reading mode.
export function SavedHandoutPreview({ record, keyboardEnabled = true, readerControls, initialLayout = "two" }) {
  const panelRef = useRef(null);
  const handoutFlowRef = useRef(null);
  const toolbarRef = useRef(null);
  const nativeCanvasRef = useRef(null);
  const legacyCanvasRef = useRef(null);
  const measureHostRef = useRef(null);
  const chromeHidden = Boolean(readerControls?.chromeHidden);
  const [readerWidth, setReaderWidth] = useState(900);
  const [readerCanvasHeight, setReaderCanvasHeight] = useState(600);
  const [readerLayout, setReaderLayout] = useState(initialLayout);
  const handoutPreviewMode = readerLayout.startsWith("two") ? "two" : "single";
  const nativeMode = readerLayout.endsWith("continuous") && !readerControls?.handwriting;
  const previousNativeMode = useRef(nativeMode);
  const [legacyPage, setLegacyPage] = useState(0);
  const [handoutPreviewPageCount, setCount] = useState(1);
  const [pendingImageSizes, setPendingImageSizes] = useState(0);
  const [handoutPreviewScale, setScale] = useState(0.9);
  const [readerZoom, setReaderZoom] = useState("page");
  const [showFormattingMarks, setShowFormattingMarks] = useState(false);
  const effectiveMode = readerWidth < 720 ? "single" : handoutPreviewMode;
  const effectiveLayout = readerWidth < 720 ? readerLayout.replace(/^two/, "single") : readerLayout;
  const spread = effectiveMode === "two" ? 2 : 1;
  const navigationInset = chromeHidden || readerWidth < 900 ? 0 : 104;
  const rowHeight = handoutPageHeight * handoutPreviewScale;
  const rowStride = rowHeight + readerRowGap;
  const continuous = useContinuousReaderPaging({ panelRef, canvasRef: nativeCanvasRef,
    enabled: nativeMode && keyboardEnabled, pageCount: handoutPreviewPageCount, spread,
    rowStride, topPadding: readerStackPadding, recordId: record.id, loading: record.items_has_more });
  const legacyBadge = useReaderPaging({ panelRef, canvasRef: legacyCanvasRef, enabled: !nativeMode && keyboardEnabled,
    page: legacyPage, pageCount: handoutPreviewPageCount, spread,
    setPage: setLegacyPage, recordId: record.id, loading: record.items_has_more, immediateWheel: true });
  const continuousPage = continuous.page;
  const goToContinuousPage = continuous.goToPage;
  const handoutPreviewPage = nativeMode ? continuousPage : legacyPage;
  const setHandoutPreviewPage = nativeMode ? goToContinuousPage : setLegacyPage;
  const payload = record.payload || {};
  const handoutOptions = useMemo(() => {
    const saved = record.payload?.options || {};
    return {
      ...defaultHandoutOptions, ...saved,
      margins: { ...getDefaultHandoutMargins(record.template_id || record.payload?.templateId || "junior_math_handout"), ...saved.margins },
      focus: { ...defaultHandoutOptions.focus, ...saved.focus },
      icons: { ...defaultHandoutOptions.icons, ...saved.icons },
      questionImageWidths: saved.questionImageWidths || {},
      questionImageAlignments: saved.questionImageAlignments || record.payload?.handout_options?.question_image_alignments || {},
      headerFooter: normalizeHandoutHeaderFooter(saved, defaultHandoutOptions.headerFooter),
    };
    // Later item batches omit payload; keep options stable so loaded pages are not rebuilt.
  }, [record.payload, record.template_id]);
  const handoutSections = useMemo(() => {
    const items = record.items || [];
    const used = new Set();
    let awaitingItems = false;
    const sections = groupHandoutConceptSections(record.payload?.sections || []).map((section) => {
      if (awaitingItems) return null;
      if (section.type !== "questions") return section;
      const references = section.questions || section.question_ids || [];
      return { ...section, questions: references.map((reference, position) => {
        const id = String(typeof reference === "string" ? reference : reference?.id || reference?.question_uuid || "");
        const matches = (item) => String(item.question_uuid || item.snapshot?.id || "") === id;
        let index = items.findIndex((item, i) => !used.has(i) && matches(item));
        if (index < 0 && !record.items_has_more) index = items.findIndex(matches);
        if (index < 0 && record.items_has_more) { awaitingItems = true; return null; }
        if (awaitingItems) return null;
        if (index < 0) return { id: `missing-${section.id}-${position}`, prompt_md: `此題的紀錄快照尚未找到。${id}` };
        used.add(index);
        return { ...items[index].snapshot, question_uuid: items[index].question_uuid || items[index].snapshot?.question_uuid || items[index].snapshot?.id,
          id: `${id}:snapshot:${index}:${position}`, pageBreakBefore: reference?.pageBreakBefore ?? items[index].snapshot?.pageBreakBefore, pagination: reference?.pagination ?? items[index].snapshot?.pagination };
      }).filter(Boolean) };
    }).filter(Boolean);
    const remaining = items.filter((_, i) => !used.has(i));
    if (remaining.length && !record.payload?.snapshot_selection) sections.push({ id: "remaining", type: "questions", title: sections.length ? "其他題目" : "", questions: remaining.map((item, i) => ({ ...item.snapshot, question_uuid: item.question_uuid || item.snapshot?.question_uuid || item.snapshot?.id, id: `remaining-${i}` })) });
    return sections;
  }, [record]);
  const handoutQuestionIndexById = useMemo(() => {
    const numbers = new Map();
    let number = 1;
    handoutSections.forEach((section) => {
      if (section.type !== "questions") return;
      number = getHandoutStartNumber(section.startNumber) ?? number;
      section.questions.forEach((question, index) => {
        numbers.set(handoutQuestionSlotKey(section.id, index), number);
        numbers.set(question.id, number++);
      });
    });
    return numbers;
  }, [handoutSections]);
  const pixelsPerCm = 96 / 2.54;
  const handoutContentLeft = (Number(handoutOptions.margins.left) + Number(handoutOptions.margins.gutter || 0)) * pixelsPerCm;
  const handoutContentRight = Number(handoutOptions.margins.right) * pixelsPerCm;
  const handoutContentTop = Number(handoutOptions.margins.top) * pixelsPerCm;
  const handoutContentBottom = Number(handoutOptions.margins.bottom) * pixelsPerCm;
  const handoutPageContentWidth = handoutPageWidth - handoutContentLeft - handoutContentRight;
  const handoutColumnGap = handoutPageWidth + handoutPageGap - handoutPageContentWidth;
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const flow = handoutFlowRef.current;
    const forcedPages = getForcedHandoutPageCount(handoutSections, handoutOptions);
    const measure = () => {
      const width = (nativeMode ? nativeCanvasRef.current?.clientWidth : 0) || panel?.clientWidth || 900;
      setReaderWidth(width);
      const shown = width >= 720 && handoutPreviewMode === "two" ? 2 : 1;
      const sideSpace = nativeMode ? readerStackPadding * 2 : chromeHidden || width < 900 ? 0 : 104;
      const widthScale = Math.max(0.1, (width - sideSpace) / (shown * handoutPageWidth + (shown - 1) * handoutPageGap));
      const scrollArea = panel?.closest("[data-record-scroll]");
      const availableHeight = scrollArea?.clientHeight || window.innerHeight - 100;
      const scrollStyle = scrollArea ? window.getComputedStyle(scrollArea) : null;
      const scrollPadding = (parseFloat(scrollStyle?.paddingTop) || 0) + (parseFloat(scrollStyle?.paddingBottom) || 0);
      let pageHeight;
      if (nativeMode) {
        const toolbarStyle = toolbarRef.current ? window.getComputedStyle(toolbarRef.current) : null;
        const toolbarHeight = chromeHidden ? 0 : (toolbarRef.current?.offsetHeight || 0) +
          (parseFloat(toolbarStyle?.marginTop) || 0) + (parseFloat(toolbarStyle?.marginBottom) || 0);
        const viewportHeight = Math.max(1, availableHeight - scrollPadding - toolbarHeight - 4);
        setReaderCanvasHeight(viewportHeight);
        pageHeight = viewportHeight - readerStackPadding * 2;
      } else {
        // Handwriting/export retains the original single paged document surface.
        const paper = panel?.querySelector("[data-reader-page-viewport]");
        const chromeHeight = Math.max(0, (panel?.offsetHeight || 0) - (paper?.offsetHeight || 0));
        pageHeight = availableHeight - scrollPadding - chromeHeight - 4;
      }
      const pageScale = Math.max(0.1, pageHeight / handoutPageHeight);
      setScale(readerZoom === "page" ? Math.min(widthScale, pageScale, 2)
        : readerZoom === "width" ? Math.min(widthScale, 2) : Number(readerZoom));
      const pages = Math.max(1, forcedPages, Math.ceil(((flow?.scrollWidth || 0) + handoutColumnGap - 1) / (handoutPageWidth + handoutPageGap)));
      setCount(pages);
      setPendingImageSizes(flow?.querySelectorAll('[data-handout-image-size="pending"]').length || 0);
      setLegacyPage((page) => Math.floor(Math.min(page, pages - 1) / shown) * shown);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    const scrollArea = panel?.closest("[data-record-scroll]");
    [panel, flow, scrollArea, toolbarRef.current, nativeCanvasRef.current].filter(Boolean).forEach((element) => observer?.observe(element));
    const disconnectFlow = observeHandoutFlow(flow, measure);
    const loadHost = nativeMode ? measureHostRef.current : panel;
    loadHost?.addEventListener("load", measure, true);
    window.addEventListener("resize", measure);
    let cancelled = false;
    const fontsReady = document.fonts?.load?.('14px "HandoutKai"', "講義數學");
    fontsReady?.then(() => { if (!cancelled) measure(); }).catch(() => {});
    return () => { cancelled = true; observer?.disconnect(); disconnectFlow(); loadHost?.removeEventListener("load", measure, true); window.removeEventListener("resize", measure); };
  }, [handoutSections, handoutOptions, handoutColumnGap, handoutPreviewMode, readerZoom, chromeHidden, nativeMode]);
  useEffect(() => { setLegacyPage(0); }, [record.id]);
  useEffect(() => { if (!nativeMode && effectiveMode === "two") setLegacyPage((page) => Math.floor(page / 2) * 2); }, [effectiveMode, nativeMode]);
  useLayoutEffect(() => {
    if (previousNativeMode.current && !nativeMode) {
      setLegacyPage(continuousPage);
    } else if (!previousNativeMode.current && nativeMode) {
      goToContinuousPage(legacyPage);
    }
    previousNativeMode.current = nativeMode;
  }, [nativeMode, continuousPage, goToContinuousPage, legacyPage]);
  useEffect(() => {
    if (!keyboardEnabled) return undefined;
    const frame = requestAnimationFrame(() => (nativeMode ? nativeCanvasRef.current : panelRef.current)?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [record.id, keyboardEnabled, nativeMode]);
  useEffect(() => {
    if (!keyboardEnabled) return undefined;
    const handleKey = (event) => {
      if (event.key !== "Enter" || event.defaultPrevented || event.isComposing || event.keyCode === 229 || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.target?.closest?.('input, textarea, select, button, a, [contenteditable="true"]')) return;
      const dialog = panelRef.current?.closest('[role="dialog"]');
      if (dialog && event.target?.closest?.('[role="dialog"]') !== dialog) return;
      event.preventDefault();
      setHandoutPreviewPage((page) => event.shiftKey ? Math.max(0, page - spread) : page + spread >= handoutPreviewPageCount ? page : page + spread);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [keyboardEnabled, spread, handoutPreviewPageCount, setHandoutPreviewPage]);
  const renderSavedImage = useCallback(({ src, alt, widthCm, inline, asset }) => (
    <HandoutResizableImage $inline={inline} style={{ width: `${widthCm}cm` }}>
      {asset?.source_language === "python" && asset.source_code
        ? <QuestionAssetPreviewStrip assets={[asset]} widthCm={widthCm} bodyFontSize={handoutOptions.bodyFontSize} transparentBackground showCaption={false} />
        : src ? <img src={src} alt={alt || "講義圖片"} {...handoutImageLoadProps(asset)} /> : null}
    </HandoutResizableImage>
  ), [handoutOptions.bodyFontSize]);
  const context = useMemo(() => ({
    handoutPreviewMode: effectiveMode, handoutPreviewPageCount, handoutPreviewScale,
    handoutOptions, handoutSections, handoutContentLeft, handoutContentRight, handoutContentTop,
    handoutContentBottom, handoutPageContentWidth, handoutColumnGap, handoutQuestionIndexById,
    handoutTitle: record.title, handoutLineHeight: Number(handoutOptions.lineSpacing) || 1.25,
    handoutOutputMode: payload.outputMode || record.output_mode || "teaching",
    readOnly: true, deferCompactImages: !readerControls?.handwriting, showFormattingMarks, canExportHandout: true, draggedHandoutAsset: null,
    dropHandoutAssetOnPreview: savedHandoutNoop, activeHandoutSectionId: null, setActiveHandoutSectionId: savedHandoutNoop,
    updateHandoutImageWidth: savedHandoutNoop, updateQuestionImageWidth: savedHandoutNoop,
    renderHandoutResizableImage: renderSavedImage,
  }), [effectiveMode, handoutPreviewPageCount, handoutPreviewScale, handoutOptions, handoutSections,
    handoutContentLeft, handoutContentRight, handoutContentTop, handoutContentBottom, handoutPageContentWidth,
    handoutColumnGap, handoutQuestionIndexById, record.title, payload.outputMode, record.output_mode, showFormattingMarks, renderSavedImage, readerControls?.handwriting]);
  const rowCount = Math.ceil(handoutPreviewPageCount / spread);
  const contentWidth = (spread * handoutPageWidth + (spread - 1) * handoutPageGap) * handoutPreviewScale;
  const currentRow = Math.floor(handoutPreviewPage / spread);
  const rows = Array.from({ length: continuous.lastRow - continuous.firstRow + 1 }, (_, index) => index + continuous.firstRow);
  return <PreviewUuidTools resetKey={record.id} enabled={!readerControls?.handwriting}><div ref={panelRef} tabIndex={0} role="region" aria-label="已儲存講義預覽" data-record-reader-content data-reader-layout={effectiveLayout}>
    {nativeMode ? continuous.pageBadge : legacyBadge}
    <SavedReaderToolbar ref={toolbarRef} hidden={chromeHidden} data-reader-toolbar data-record-export-exclude="true">
      <small title="實際字型、分頁與頁數以下載的 Word 為準。">閱讀預覽（分頁估算）{pendingImageSizes > 0 && ` · 圖片尺寸待載入 ${pendingImageSizes} 張`}</small>
      {nativeMode && <IconButton type="button" aria-label="上一頁" disabled={handoutPreviewPage === 0} onClick={() => setHandoutPreviewPage((page) => Math.max(0, page - spread))}><FaChevronLeft aria-hidden="true" /></IconButton>}
      <PreviewPageIndicator aria-live="polite">第 {handoutPreviewPage + 1}{effectiveMode === "two" && handoutPreviewPage + 1 < handoutPreviewPageCount ? `–${handoutPreviewPage + 2}` : ""} / {handoutPreviewPageCount} 頁</PreviewPageIndicator>
      {nativeMode && <IconButton type="button" aria-label="下一頁" disabled={handoutPreviewPage + spread >= handoutPreviewPageCount} onClick={() => setHandoutPreviewPage((page) => Math.min(handoutPreviewPageCount - 1, page + spread))}><FaChevronRight aria-hidden="true" /></IconButton>}
      <label>閱讀模式 <select aria-label="講義閱讀模式" value={effectiveLayout} onChange={(event) => setReaderLayout(event.target.value)}>
        <option value="single">單頁</option>
        <option value="single-continuous">單頁連續</option>
        <option value="two" disabled={readerWidth < 720}>雙頁</option>
        <option value="two-continuous" disabled={readerWidth < 720}>雙頁連續</option>
      </select></label>
      <IconButton type="button" aria-label="顯示格式標記" title="顯示或隱藏分頁控制標記（不列印）" aria-pressed={showFormattingMarks} onClick={() => setShowFormattingMarks((value) => !value)}>¶</IconButton>
      <label>縮放 <select aria-label="講義預覽縮放" value={readerZoom} onChange={(event) => setReaderZoom(event.target.value)}>
        <option value="page">整頁</option><option value="width">符合寬度</option>
        {[0.5, 0.75, 1, 1.25, 1.5, 2].map((zoom) => <option key={zoom} value={zoom}>{zoom * 100}%</option>)}
      </select></label>
      <small>{nativeMode ? "滾輪／滑動連續閱讀" : "滾輪／滑動翻頁"} · Enter 下一頁 · Shift + Enter 上一頁
        {nativeMode && <span> · 標楷體優先，楷書備援：<a href="https://language.moe.gov.tw/material/info?m=9fe3fe82-8bbf-44c0-961d-873ea079e284" target="_blank" rel="noreferrer">教育部標準楷書</a></span>}
      </small>
    </SavedReaderToolbar>
    {nativeMode ? <>
      <SavedReaderMeasurement ref={measureHostRef} aria-hidden="true" inert="" data-reader-measure>
        <SavedHandoutA4Preview {...context} handoutPreviewPage={0} handoutPreviewScale={1} handoutFlowRef={handoutFlowRef}
          setHandoutPreviewPage={savedHandoutNoop} immersive edgeNavigation hidePageNavigation hidePageLabels handoutFlowLabel="講義分頁計算" />
      </SavedReaderMeasurement>
      <SavedReaderCanvas ref={nativeCanvasRef} $continuous data-reader-canvas data-reader-native-scroll tabIndex={0} role="region" aria-label="講義連續閱讀區" style={{ height: readerCanvasHeight }}>
        <SavedReaderStack data-reader-stack style={{ width: contentWidth + readerStackPadding * 2, height: rowCount * rowStride - readerRowGap + readerStackPadding * 2 }}>
          {rows.map((row) => <SavedReaderSpread key={row} data-reader-spread={row} data-reader-current-row={row === currentRow || undefined}
            aria-label={row === currentRow ? "A4 講義內容" : undefined} aria-hidden={row !== currentRow || undefined} inert={row !== currentRow ? "" : undefined}
            style={{ top: readerStackPadding + row * rowStride, width: contentWidth, height: rowHeight }}>
            <SavedHandoutA4Preview {...context} handoutPreviewPage={row * spread} handoutFlowRef={null}
              setHandoutPreviewPage={savedHandoutNoop} immersive edgeNavigation hidePageNavigation onlyVisiblePages handoutFlowLabel={false} />
          </SavedReaderSpread>)}
        </SavedReaderStack>
      </SavedReaderCanvas>
    </> : <SavedReaderCanvas ref={legacyCanvasRef} $immersive={chromeHidden} data-reader-canvas>
      <div style={{ width: contentWidth + navigationInset, minWidth: "100%" }}>
        <HandoutBuilderA4Preview {...context} handoutPreviewPage={legacyPage} handoutFlowRef={handoutFlowRef} setHandoutPreviewPage={setLegacyPage} immersive={chromeHidden} edgeNavigation={navigationInset === 0} />
      </div>
    </SavedReaderCanvas>}
  </div></PreviewUuidTools>;
}

const QuestionPaginationSettings = styled.details`
  font-size: 12px; color: var(--app-muted-text, #596273); margin: 5px 0;
  summary { cursor: pointer; width: fit-content; padding: 4px 0; }
  &[open] { padding-bottom: 6px; }
  label { display: inline-flex; align-items: center; gap: 5px; margin: 4px 12px 0 0; cursor: pointer; }
  input { margin: 0; }
  select { padding: 4px 6px; border: 1px solid var(--app-border, #d7dee4); border-radius: 5px; background: var(--app-input-bg, #fff); color: var(--app-input-text, #27313c); font: inherit; }
`;

const SavedReaderToolbar = styled(PreviewPanelHeader)`
  &[hidden] { display: none; }
  position: sticky; top: 0; z-index: 5; justify-content: center; gap: 8px;
  margin-bottom: 12px; padding: 10px; box-sizing: border-box;
  border: 1px solid var(--app-border, #d7dee4); border-radius: 10px;
  background: var(--app-surface, #fff); color: var(--app-text, #27313c);
  label { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; }
  select { padding: 6px; border-radius: 5px; background: var(--app-surface, #fff); color: inherit; border: 1px solid var(--app-border, #d7dee4); }
  small { color: var(--app-muted-text, #687380); }
  @media (max-width: 640px) { gap: 6px; padding: 6px; small { flex-basis: 100%; text-align: center; font-size: 11px; } }
`;
const SavedReaderMeasurement = styled.div`position: absolute; width: ${handoutPageWidth}px; height: 0; overflow: hidden; pointer-events: none;`;
const SavedReaderStack = styled.div`position: relative; min-width: 100%;`;
const SavedReaderSpread = styled.div`position: absolute; left: 50%; transform: translateX(-50%);`;
const SavedReaderCanvas = styled.div`width: 100%; min-width: 0; overflow: auto; overscroll-behavior-x: contain; padding: ${({ $immersive }) => $immersive ? "0" : "8px 0 12px"}; background: var(--app-bg, #edf0f4); border-radius: ${({ $immersive }) => $immersive ? "0" : "8px"}; ${({ $continuous }) => $continuous && "padding: 0; min-height: 0; scrollbar-gutter: stable; overscroll-behavior: contain;"}`;

const PickerSteps = styled.nav`
  display: flex; gap: 12px; margin: 4px 0 16px;
  button { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 14px 18px; border: 1px solid var(--app-border, #d5d7db); border-radius: 12px; background: var(--app-surface, white); color: var(--app-surface-text, #202124); cursor: pointer; text-align: left; font: inherit; }
  button[aria-current="step"] { border-color: #526fac; background: var(--app-surface-soft, #edf2ff); box-shadow: 0 0 0 1px #526fac; }
  button:disabled { opacity: .5; cursor: default; }
  b { display: grid; place-items: center; width: 28px; height: 28px; border-radius: 50%; background: var(--app-surface-hover, #e4e8f0); color: var(--app-surface-text, #24395f); }
  button[aria-current="step"] b { background: var(--app-nav-active-bg, #24395f); color: var(--app-nav-active-text, #fff); }
  small { font-size: 12px; color: var(--app-muted-text, #596273); }
  @media (max-width: 600px) { gap: 8px; button { flex: 1; padding: 10px; } small { flex-basis: 100%; } }
`;
